import { getNoticeEventBounds } from "../../utils/noticeCalendarEvents";
import { explicitQuestion } from "./explicitQuestion";
import type { NoticeWithType } from "../../components/ViewNotices";
import { getSidebarItem, type SidebarItemKey } from "../../config/sidebarConfig";
import { canOpenSidebarItem } from "../navigation/sidebarAccess";
import {
  aspectFromScores,
  eventLine,
  followUpAspect,
  FUZZY_ASPECT_PHRASES,
  isShortFollowUp,
  mentionsWhen,
  namesAnEvent,
  noTimeLine,
  type FollowUpAspect,
} from "./aspectFollowUp";
import type {
  AnswerContext,
  AnswerEntry,
  AnswerLink,
  CatalogueEntry,
  FilledSlots,
  HandoffEntry,
  HandoffTarget,
  HowToAction,
  HowToEntry,
  SlotName,
  SlotQuestion,
  SlotValues,
  SummaryLine,
} from "./catalogue";
import { CATALOGUE, isSlotQuestion } from "./catalogue";
import { aRole, capitalize } from "./catalogue/wording";
import { noticeKindStart } from "./catalogue/create";
import { askFor } from "./catalogue/findByName";
import {
  asksForNextEvent,
  findNoticeForWhen,
  loadFeed,
  noticeFollowUpQuery,
  noticeLink,
  wantsTheEventsPage,
  wantsUpcomingEvents,
  whichMentioned,
  whenWordings,
} from "./catalogue/notices";
import { cancelKindOf, DELETE_THE_DRAFT } from "./cancelPhrasing";
import { findDateRange, resolveDateRange } from "./dateRange";
import { isStuckPhrase, pageStuckReply, stepsOf, STILL_STUCK_ACTIONS, STILL_STUCK_REPLY, WHICH_STEP_REPLY } from "./dontSeeIt";
import {
  followUpRejects,
  namedSlotOf,
  readBareFollowUp,
  readDirectFollowUp,
  readFollowUp,
  slotValue,
  withoutEndPunctuation,
} from "./followUpRules";
import { introductionFor, isIntroductionRequest } from "./introduction";
import { answerFromLesson } from "./lessonAnswers";
import { speakLesson } from "./lessonSpeech";
import { createWordOverlapMatcher } from "./wordOverlapMatcher";
import { offTopicReply } from "./offTopicReplies";
import { pageToOpen } from "./catalogue/openPage";
import { roleIn } from "../team/teamRoles";
import { looksLikeQuestion } from "./sideQuestion";
import { loadCircles, resolveCircle } from "./catalogue/pickCircle";
import { phrasingKey, type MatchThresholds, type QuestionMatcher, type RankedEntry } from "./questionMatcher";
import { isSafeguardingConcern, SAFEGUARDING_REPLY, SAFEGUARDING_SHARING_REPLY, asksToShareSafeguardingConcern } from "./safeguarding";
import { significantWords } from "./significantWords";
import { findRecipients } from "./tasks/recipients";
import {
  ABOUT_ASK_REPLY,
  FRUSTRATED_REPLY,
  GOODBYE_REPLY,
  GREETING_REPLY,
  HOW_ARE_YOU_REPLY,
  NERVOUS_REPLY,
  REACTION_REPLY,
  smallTalkReply,
  THANKS_REPLY,
} from "./smallTalk";
import { isApproval } from "./tasks/approval";
import { sendsLater, startsOver, switchedKind } from "./tasks/midDraft";
import { vagueRequestReply } from "./vagueRequests";
import { spokenCore, withVoice } from "./voice";
import { tidyMessage } from "./tidyMessage";
import { describedEventRequest, asksToAddDescribedEvent, HELP_ME_MAKE_ONE, ONE_PERSON, asksToMakeItForThem, namesAPicture, readWorkRequest, withoutTextSpeak } from "./workRequest";
import {
  asksIfSent,
  asksTheDate,
  asksWhoEveryoneReaches,
  dateReply,
  EVERYONE_REACH_REPLY,
  meansTomorrow,
  privateDataReply,
  sentStatusReply,
} from "./directAnswers";
import { asksForADraft, draftAskedFor, draftName } from "./tasks/parkedTasks";
import { audienceOf, placeOf, recurrenceLine, upcomingAudience, upcomingPlace } from "./upcomingFollowUp";
import { readTaskDetails } from "./taskDetails";
import { loadTaskContext } from "./tasks/loadTaskContext";
import { noticeTask } from "./tasks/noticeTask";
import {
  CANCELLED_REPLY,
  carryOnPrompt,
  continueTask,
  contextualTaskTurn,
  isContextualTaskRequest,
  takesAsADetail,
  resumeTask,
  startTask,
  type TaskContext,
  type TaskRecord,
  type TaskTurn,
} from "./tasks/taskConversation";
import { isNovaUpdate, NOVA_OFFER_REPLY } from "./novaUpdate";
import { toNova } from "../nova/novaHandover";
import {
  isTranslationRequest,
  isWritingRequest,
  toNewNotice,
  toNewImageNotice,
  TRANSLATION_REQUEST_REPLY,
  TRANSLATION_REQUEST_REPLY_NO_WIZARD,
  WRITING_REQUEST_REPLY,
  WRITING_REQUEST_REPLY_NO_WIZARD,
  isTeachingWork,
  TEACHING_WORK_REPLY,
} from "./writingRequest";

/**
 * A catalogue question and the details it was asked with, kept so follow-ups
 * can reuse it. `awaiting` is the detail a follow-up question just asked for.
 * `startedByText` is the message that started a handoff task, kept so "that's
 * not what I meant" can offer what else it might have been.
 */
/** Who or what an answer was about, in its own words ("Sports Day", "Easter 2025"): what "it" means in a later message. */
export type AskedQuestion = {
  id: string;
  slots: SlotValues;
  awaiting?: SlotName;
  startedByText?: string;
  /** An Event just described, waiting for an explicit request to prepare it. */
  eventDescription?: string;
  /** Last reviewed draft, retained only to reopen the form after a delivery-status question. */
  handedOverDraft?: TaskRecord;
  subject?: string;
  /** Titles the last answer named, so the next message can ask about one of them. */
  mentions?: string[];
  /** How many times in a row "I don't see it" (or the like) has followed this same answer */
  recoveryAttempts?: number;
  /** Questions just offered ("Did you mean…", closest questions), so "the second one" can pick one. */
  offered?: string[];
  /** The names the follow-up question offered for `awaiting` ("Which Template?"): one of them, as is, is the answer */
  awaitingChoices?: string[];
};

export type AssistantReply = (
  /** `fromFallback`: matched by shared words because the matching model wasn't ready yet, so it's worth asking again once it is */
  | { kind: "answer"; text: string; link: AnswerLink; asked: AskedQuestion; fromFallback?: boolean; openNow?: boolean }
  /** `asked` marks the next message as following a safeguarding concern */
  | { kind: "safeguarding"; text: string; asked?: AskedQuestion }
  | { kind: "offTopic"; text: string }
  /** "Hi" or "thanks", answered straight away */
  | { kind: "smallTalk"; text: string }
  /** "What can you do?" or "help": what it can do for this person, with example questions to press */
  | { kind: "introduction"; text: string; questions: string[]; asked?: AskedQuestion }
  /** An instant search result opened from what was typed, so the chat still shows what happened */
  | { kind: "opened"; text: string }
  /** Asked to write or reword text; `to` is a new Notice in the wizard, if they can open Create */
  | { kind: "writingRequest"; text: string; to?: HandoffTarget; asked?: AskedQuestion }
  /** Told what's happening rather than asked: Nova can turn it into Drafts, so `to` opens Nova with it filled in */
  | { kind: "novaOffer"; text: string; to: HandoffTarget }
  | { kind: "didYouMean"; text: string; questions: string[]; asked?: AskedQuestion; fromFallback?: boolean }
  /** A "how do I…?" question answered in steps, with a button that does the real thing */
  | { kind: "howTo"; text: string; actions: HowToAction[]; asked: AskedQuestion; fromFallback?: boolean }
  /** `notFound`: the sentence saying a Notice it was asked about by name doesn't exist, which a draft's side question can say on its own */
  | { kind: "unanswered"; text: string; questions: string[]; asked?: AskedQuestion; fromFallback?: boolean; notFound?: string }
  | { kind: "loadFailed"; text: string; asked?: AskedQuestion }
  | { kind: "searchOnly"; text: string }
  | { kind: "handoff"; text: string; summary: SummaryLine[]; to: HandoffTarget; asked: AskedQuestion }
  | {
      kind: "askSlot";
      text: string;
      choices: string[];
      reason: "missing" | "ambiguous" | "notFound";
      asked: AskedQuestion;
    }
  | { kind: "cannot"; text: string; asked?: AskedQuestion }
  /** "I don't see it", "where is that?": stuck on the last answer. `link`, `choices` and `actions` are never more than one at once. */
  | {
      kind: "recovery";
      text: string;
      link?: AnswerLink;
      choices?: string[];
      actions?: HowToAction[];
      asked: AskedQuestion;
    }
  /** A guided task's turn: a question with choices, or its summary to confirm. `task` is the record that follows; null once it's over. */
  | {
      kind: "task";
      text: string;
      choices: string[];
      summary?: SummaryLine[];
      to?: HandoffTarget;
      task: TaskRecord | null;
      /** The catalogue entry whose request started the task, on its first turn */
      startedBy?: string;
      /** A side question answered while the task stays open */
      asked?: AskedQuestion;
    }
  /** "Cancel" or "that's not what I meant" at a step of a handoff task: ends it, offering what else it might have been */
  | { kind: "cancelled"; text: string; choices: string[] }
) & {
  dropTask?: true;
  /** Best catalogue match score when the matcher ran. Absent for safeguarding and when matching never ran. */
  topScore?: number;
};

export const UNANSWERED_REPLY =
  "I can't answer that yet. These are the closest questions I can answer:";

export const LOAD_FAILED_REPLY =
  "I couldn't load that right now. Please try again in a moment.";

export const DID_YOU_MEAN_REPLY = "Did you mean one of these?";

/** The kind they chose, said back before the first step of it. */
const KIND_SAID: Record<"notice" | "poll" | "event", string> = {
  notice: "A Notice. Nothing is sent until you press Publish.",
  poll: "A Poll. Nothing is sent until you press Publish.",
  event: "An Event. Nothing is sent until you press Publish.",
};

/** "Actually a poll", "no, an event instead": the open task is the wrong kind, so the other kind starts. */
const SWITCH_KIND = /^(?:(?:actually|no|rather|wait)[,\s]+)+(?:make it\s+|do\s+)?(?:a|an)\s+(poll|event|notice)(?:\s+instead)?[?.!]*$/i;

/**
 * "can it be an event instead?", "I want to change it to an Event not a Information notice",
 * "make it a poll": the draft open is the wrong kind.
 */
const SWITCH_TO =
  /\b(?:can|could)\s+(?:it|this|we)\s+(?:be|make\s+it)\s+(?:a|an)\s+(poll|event|notice)\b|\b(?:change|switch|turn|make)\s+(?:it|this)\s+(?:in)?to\s+(?:a|an)\s+(poll|event|notice)\b|\b(?:a|an)\s+(poll|event|notice)\s+instead\b|\bi\s+want\s+(?:it\s+to\s+be|to\s+change\s+it\s+to)\s+(?:a|an)\s+(poll|event|notice)\b|^(?:(?:sorry|oops|no|oh|wait|actually)[,.!\s]+)*(?:i\s+meant|i\s+mean|it\s+should\s+(?:be|have\s+been)|it'?s\s+(?:meant\s+to\s+be|supposed\s+to\s+be)|make\s+it)\s+(?:a|an)\s+(poll|event|notice)\b/i;

function kindSwitchedTo(text: string): "poll" | "event" | "notice" | undefined {
  const conversational = text.match(/\b(?:a\s+)?(poll|event|notice)\s+(?:would be better|is better)\b/i) ?? text.match(/\bi just want a (?:normal )?(notice|event|poll)\b/i);
  if (conversational) return conversational[1].toLowerCase() as "poll" | "event" | "notice";
  const correction = text.match(/^(?:(?:no|actually|sorry)[,\s]+)*(?:i wanted|i want|i meant)\s+(?:a|an)\s+(poll|event|notice)\s*,?\s+not\s+(?:a|an)\s+(?:poll|event|notice)/i);
  if (correction) return correction[1].toLowerCase() as "poll" | "event" | "notice";
  if (/^(?:change|switch|make) (?:this|it) (?:to |into )?(?:a )?(?:normal )?announcement\b/i.test(text)) return "notice";
  const found = text.match(SWITCH_TO);
  const kind = found?.slice(1).find(Boolean)?.toLowerCase();
  return kind === "poll" || kind === "event" || kind === "notice" ? kind : undefined;
}

/** "can it be an event instead? it's on Friday 30 October at 11:00": what's said after the switch, as details for the new kind. */
function saidAfterSwitch(text: string): string {
  const found = text.match(SWITCH_TO);
  if (found?.index === undefined) return "";
  return text
    .slice(found.index + found[0].length)
    .replace(/^\s*instead\b/i, "")
    .replace(/^[\s?!.,;:]+/, "")
    .replace(/^(?:it'?s|it\s+is|its)\s+/i, "")
    .trim();
}

/** What a Notice and an Event share, kept when one becomes the other: the title, message and who it's for. */
function carriedOver(record: TaskRecord, to: "poll" | "event" | "notice"): Record<string, unknown> {
  const { title, message, to: audience } = record.details as { title?: unknown; message?: unknown; to?: { kind?: string } };
  // An Event is never for one User, and a Notice never for the Internal team only
  const keptAudience = audience && !(to === "event" && audience.kind === "user") && audience.kind !== "team" ? audience : undefined;
  if (to === "poll") return keptAudience && audience?.kind !== "user" ? { to: keptAudience } : {};
  return Object.fromEntries(Object.entries({ title, message, to: keptAudience }).filter(([, value]) => value !== undefined));
}

const IMAGE_NOTICE_HOW_TO = "create.imageNoticeHowTo";

/** An image notice, asked for in any words: the picture is uploaded on Create, so that's where it's made. */
function imageNoticeReply(request = ""): AssistantReply & { kind: "howTo" } {
  const title = request.match(/\btitled\s+["“](.+?)["”]/i)?.[1]
    ?? request.match(/\btitled\s+(.+?)(?=\s+(?:for|to)\s+|[.!?]|$)/i)?.[1] ?? "";
  return {
    kind: "howTo",
    text: "I can't make image notices in this chat, because the picture has to be uploaded. On Create, press Create a notice on the Image card, add a title and the picture, then Continue and Publish. Or I can start an Information notice now.",
    actions: [
      { kind: "open", label: "Open Create", to: toNewImageNotice(title) },
      { kind: "start", label: "Start an Information notice", ask: "create an information notice" },
    ],
    asked: { id: IMAGE_NOTICE_HOW_TO, slots: title ? { topic: title } : {} },
  };
}

/** Claim lists aren't made in the chat: what to do instead. */
const CLAIM_LIST_HOW_TO = "create.claimListHowTo";
export const CLAIM_LIST_REPLY =
  "I can't make a claim list in this chat, but it's quick on Create: press Create a claim list on the Claims card, add the items, choose who it goes to, then Publish. Each family claims one item, and it disappears once it's taken.";
function claimListReply(): AssistantReply & { kind: "howTo" } {
  return {
    kind: "howTo",
    text: CLAIM_LIST_REPLY,
    actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    asked: { id: CLAIM_LIST_HOW_TO, slots: {} },
  };
}
/** "can you schedule it for 7am tomorrow?", "schedule this for Monday": the draft that's open, and when */
const SCHEDULE_IT = /\bschedule\s+(?:it|this|that|the\s+(?:notice|poll|event)|my\s+(?:notice|poll|event))\b(?:\s+(?:for|at|on)\s+(.+))?$/i;
/** "will parents be told?", "do they get notified?", "will families know?" */
const PARENTS_TOLD =
  /\b(?:will|do|does|are|would)\s+(?:the\s+)?(?:parents|families|they|everyone)\s+(?:be\s+|get\s+)?(?:told|notified|informed|know|find out|hear)\b/i;
/** "what do parents see?", "what will they see?": how it looks to parents. */
const PARENTS_SEE = /^(?:(?:and|so|ok(?:ay)?)[,\s]+)*what\s+(?:do|does|will|would|can)\s+(?:the\s+)?(?:parents|families|they|them)\s+see\b[^.]*[?.!]*$/i;
/** "sign up", "claim list", "volunteers": a list families take one item from. */
const NAMES_A_CLAIM_LIST = /\b(?:claims?\s*lists?|sign[\s-]?up\s*(?:sheets?|lists?|forms?)|sign[\s-]?ups?|volunteer(?:s|ing)?|rosters?)\b/i;
/** Asking for one to be made: "I need", "create", "can you make", "how do I let them". */
const ASKS_TO_MAKE_ONE = /\b(?:create|make|start|set\s*up|need|want|can\s+you|could\s+you|how\s+do\s+i\s+(?:let|get|make|create))\b/i;
/** About claim lists already out, not making one: "who claimed", "unclaim", "the difference". */
const ABOUT_CLAIMS_OUT = /\b(?:who\s+claimed|unclaim|difference|same\s+item|edit|after\s+(?:i\s+)?sen[dt])\b/i;
/** "a bring-and-share list", "a list where parents choose what to bring", "parents need to pick which snack to bring": a claim list, unnamed. */
const DESCRIBES_A_CLAIM_LIST =
  /\bbring[\s-]*(?:and|&|n)[\s-]*share\b|\blist\b[^.?!]{0,30}\b(?:choose|pick|claim|sign\s+up\s+for|say)\b[^.?!]{0,20}\b(?:bring|take|do|help\s+with)\b|\b(?:pick|choose|claim)\s+(?:which|what)\s+(?:\w+\s+){0,2}(?:to|they(?:'ll|\s+will)?)\s+bring\b|\bwho\s+(?:brings|is\s+bringing|will\s+bring)\s+what\b/i;
const asksForAClaimList = (text: string) =>
  ((NAMES_A_CLAIM_LIST.test(text) && ASKS_TO_MAKE_ONE.test(text)) || DESCRIBES_A_CLAIM_LIST.test(text)) && !ABOUT_CLAIMS_OUT.test(text);
/** "can't you make the list in here?" after claim lists were explained: asking for one to be made here. */
const MAKE_THE_LIST_HERE = /\b(?:make|create|do|set\s+up|start)\s+(?:the|a|this|that|my)\s+(?:list|one)\b[^.?!]*\b(?:here|in\s+(?:here|this\s+chat|the\s+chat)|for\s+me)\b|\bcan'?t\s+you\s+(?:make|do|create)\s+(?:it|the\s+list|one)\b/i;

/** Said while a draft is open: a picture is attached when the wizard opens, and an Image notice is its own kind. */
const PICTURE_IN_THE_WIZARD =
  "I can't add pictures in this chat. When the Notice opens, attach the picture on its writing step. For a Notice built around a picture, choose Image on Create.";
const PICTURE_WORD = "(?:pic|pics|picture|pictures|image|images|photo|photos|img|foto)";
/** "put a picture on", "add an image": a picture for the draft. */
const PICTURE_ON_IT = new RegExp(`\\b(?:put|add|attach)\\s+(?:(?:a|an|the|some|my)\\s+)?${PICTURE_WORD}\\b`, "i");
/** "… the library. add a pic pls": a picture asked for in its own clause at the end of a message. */
const PICTURE_ASKED_AT_END = new RegExp(
  `(?:^|[.,!;]+\\s*|\\s+(?:and|also|then)\\s+)(?:(?:please|pls|plz)\\s+)?(?:(?:can|could)\\s+(?:you|u)\\s+)?(?:add|attach|put)\\s+(?:(?:a|an|the|some|my)\\s+)?${PICTURE_WORD}\\b(?:\\s+(?:of|on|to|with)\\b[^.!?]*)?[\\s.!]*(?:(?:please|pls|plz|thanks|asseblief)[\\s.!]*)?$`,
  "i"
);
/**
 * "where do I go?", "where do I do that?", "how?", "ok how do I do it myself then?": the steps just given,
 * asked for again, never a new question.
 */
const STEPS_AGAIN =
  /^(?:(?:ok(?:ay)?|so|and|then)[,\s]+)*(?:(?:(?:can|could|would)\s+you\s+)?(?:please\s+)?(?:show\s+me\s+)?where\s+(?:do\s+i\s+|can\s+i\s+|to\s+)?(?:do|see|find|go)\b(?:\s+(?:that|it|this|then))*|how|how\s+(?:do|can)\s+i\s+(?:do|make)\s+(?:it|that|this|one)(?:\s+myself)?(?:\s+then)?)[\s?.!]*$/i;

/** "I want to send a photo to parents", "can I share some pictures with the class": a picture, sent. */
const SEND_A_PICTURE =
  /^(?:(?:hi|ok(?:ay)?|so|sorry)[,.!\s]+)*(?:i\s+(?:want|need|would\s+like|'d\s+like)\s+to|(?:can|could|how\s+do|how\s+can)\s+i)\s+(?:send|share|post|put\s+up)\s+(?:a|an|some|the|my|these|this)?\s*(?:pic|pics|picture|pictures|image|images|photo|photos|img|foto)\b/i;

/** "how?", "how do I get there?", "where is that page?": the page the last answer is on. */
const HOW_DO_I_GET_THERE =
  /^(?:(?:ok(?:ay)?|so|and|but|then|um+)[,\s]+)*(?:how\s+do\s+i\s+(?:get|go)\s+(?:there|to\s+(?:it|that|that\s+page))|how\s+do\s+i\s+find\s+(?:it|that|that\s+page)|where\s+is\s+that\s+page|take\s+me\s+there|how)[\s?.!]*$/i;

/** "how do I make", "how can we send": asking how to make one. */
const HOW_TO_MAKE = /\bhow\s+(?:do|can|would|should)\s+(?:i|we|you)\s*(?:make|create|send|do|post|put)\b/i;
/** Pictures that aren't a Notice: Drive, a profile, a logo. */
const OTHER_PICTURES = /\b(?:drive|folder|profile|logo|banner|avatar|cover|format|size)s?\b/i;

/** Asked to reword text while a draft is open: Rewrite with Nova, once it opens, and the draft carries on. */
const WRITING_REQUEST_MID_TASK =
  "I don't write Notice text, but when it opens, Rewrite with Nova on the writing step can help with the wording.";

/** "will this go to every parent??", "who gets it?": about who it reaches, which the step asking who it's for answers. */
const ABOUT_WHO_IT_REACHES = /\b(?:go(?:es)?\s+to|reach(?:es)?|gets?|receives?|every\s*(?:one|parent|body)|all\s+(?:the\s+)?parents|spam|see\s+it)\b/i;

/** "who has seen Uniform Shop Sale?", "which parents have read the trip notice?" */
/** "how many people read the halloween notice?", "how many parents opened Sports Day": that Notice's views. */
const HOW_MANY_SAW =
  /^how\s+many\s+(?:people|parents|families|users|of\s+them)?\s*(?:have\s+|has\s+)?(?:seen|saw|viewed|read|opened)\s+(?:the\s+|my\s+|our\s+)?(.+?)(?:\s+(?:notice|post|announcement))?(?:\s+(?:so\s+far|yet))?[?.!]*$/i;
const WHO_HAS_SEEN =
  /^(?:who(?:'s|\s+has|\s+have)?|which\s+(?:parents|people|families|users)\s+have)\s+(?:seen|viewed|read|opened)\s+(.+?)[?.!]*$/i;

/** "just send it now", "publish it immediately, skip the review": asking Ask to send it itself. */
const SEND_IT_FOR_ME =
  /\b(?:just\s+)?(?:send|publish|post)\s+(?:it\s+)?(?:now|for\s+me|yourself|immediately|right\s+now)\b|\bdon'?t\s+want\s+to\s+press\s+publish\b|\bskip\s+the\s+review\b|\bno\s+need\s+to\s+confirm\b/i;
const CANT_SEND_FOR_YOU =
  "I can't send or publish it for you. Press Looks good to open it, check it, then press Publish yourself.";

/**
 * What a task started from a request says first: what's being prepared and that nothing goes out yet, in
 * the words each kind has always used. A detail it couldn't use ("I couldn't find “Grade 9”") comes first
 * instead, and a reply that already names the kind says it already.
 */
function startedSaying(kind: "notice" | "event" | "poll", urgent: boolean, text: string, extras = ""): string {
  if (!/^Sure\./.test(text) || /\b(?:notice|poll|event|publish)\b/i.test(text)) return `${extras}${text}`;
  const step = text.replace(/^Sure\.\s*/, "");
  if (kind === "poll") return `I'll start a Poll. Nothing is sent until you press Publish. ${extras}${step}`;
  if (urgent) return `I'll prepare an Urgent notice. You review it and press Publish. ${extras}${step}`;
  if (kind === "event") return `${KIND_SAID.event} ${extras}${step}`;
  return `${KIND_SAID.notice} ${extras}${step}`;
}

/** "whats the date today", kept so "more = tomorrow" can follow. */
const TODAY_ASKED: AskedQuestion = { id: "general.today", slots: {} };

/** "the weather one": the one offered choice with every word they used, when only one has them all. */
function choiceCalledTheOne(text: string, choices: string[]): string | undefined {
  const named = text.trim().match(/^(?:the\s+)?(.+?)\s+one[?.!]*$/i)?.[1];
  if (!named) return undefined;
  const words = (s: string) => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const wanted = words(named);
  const found = choices.filter((choice) => wanted.every((word) => words(choice).includes(word)));
  return found.length === 1 ? found[0] : undefined;
}

/** A capitalised first name, and maybe a surname: "Zola", "Zola Smith". Lower-case words ("the school") aren't a person. */
const PERSON = "[A-Z][\\p{L}'’-]+(?:\\s+[A-Z][\\p{L}'’-]+)?";

/** "Does Zola have any certificates?", "What awards has Zola won?", "Zola's certificates". */
const ASKS_FOR_A_PERSONS_CERTIFICATES = new RegExp(
  [
    `^(?:[Dd]oes|[Hh]as|[Dd]id)\\s+${PERSON}\\s+(?:have|got|get|receive|received|won|earn|earned)\\b.*\\b(?:certificates?|awards?)\\b`,
    `^(?:[Ww]hat|[Ww]hich)\\s+(?:certificates?|awards?)\\s+(?:has|does|did)\\s+${PERSON}\\s+(?:got|have|get|won|earned|received)\\b`,
    `^${PERSON}['’]s\\s+(?:certificates?|awards?)\\b`,
  ].join("|"),
  "u"
);

/** "when is the year end gala", "where is the ed tech conference": a named thing, by when or where it is. */
const ASKS_ABOUT_A_NAMED_THING =
  /^(?:when|where|what time)\s+(?:is|was|are|does)\s+(?:the|our)\s+(.+?)(?:\s+(?:happening|held|on|starting))?[?.!]*$/i;

/** "who has seen Uniform Shop Sale?", "who read the trip notice": a named Notice, by who has seen it. */
const ASKS_WHO_SAW_A_NAMED_THING =
  /^who\s+(?:has|have|'s)\s+(?:seen|read|viewed|opened)\s+(?:the\s+|our\s+)?(.+?)(?:\s+notice)?[?.!]*$/i;
/** Words that ask about Notices in general, never one called that: "my notice", "the next event", "it". */
const NOT_A_NAME = /^(?:it|this|that|them|my|mine|the|our|any|a|an|next|last|latest|upcoming|first|newest|calendar|notices?|events?|polls?)\b/i;
const notFoundCalled = (name: string) => `I couldn't find a Notice called “${name}”.`;
/** The answers that look up one Notice by name. */
const ABOUT_A_NAMED_NOTICE = new Set(["notices.when", "notices.viewers", "notices.views", "notices.unseen"]);

/** "which notices did Thandi send?", "What notices has Thandi Mokoena sent", "notices sent by Thandi". */
const ASKS_FOR_A_PERSONS_NOTICES = new RegExp(
  [
    `^(?:[Ww]hich|[Ww]hat)\\s+notices\\s+(?:did|has|have)\\s+${PERSON}\\s+(?:send|sent|create|created|write|written|wrote|post|posted)\\b`,
    `^(?:[Ss]how me\\s+)?(?:the\\s+)?[Nn]otices\\s+(?:sent|created|written|posted)\\s+by\\s+${PERSON}[?.!]*$`,
  ].join("|"),
  "u"
);

const RESUME_WORDS =
  /^(?:(?:ok(?:ay)?|right|so|sorry)[,.]?\s+)?(?:let['’]?s\s+)?(?:carry\s+on|keep\s+going|go\s+on|continue|resume|(?:go\s+|get\s+)?back\s+to)(?:\s+(?:with\s+)?(?:my|the|our|this|that)?\s*(?:notice|poll|event|it|that|one))?/i.source;

/** "carry on with my notice", "back to the notice", "ok continue": the open task, where it left off. */
const RESUME_TASK = new RegExp(`${RESUME_WORDS}[?.!]*$`, "i");

/** "back to the notice. Visitors must park…": the resume said first, then the step's answer. */
const RESUME_THEN = new RegExp(`${RESUME_WORDS}\\s*[.:!,–-]\\s*(\\S.*)$`, "is");

/**
 * "I mean Grade 2", "Not Grade 2. Grade R.", "the circle was Grade R": who it goes to, said again.
 * "same" when they mean last time's audience, which isn't kept.
 */
function correctedAudience(text: string): string | "same" | null {
  const said = text.trim().replace(/[\s.!?]+$/, "");
  if (/^(?:the\s+)?same\s+(?:parents|people|families|circle|class|group)\s+as\s+(?:last\s+time|before)$/i.test(said)) return "same";
  const named =
    said.match(/^not\s+[^.,]+[.,]\s*(?:it'?s\s+|i mean\s+)?(.+)$/i)?.[1] ??
    said.match(/^i\s+mean(?:t)?\s+(.+)$/i)?.[1] ??
    said.match(/^(?:the\s+)?circle\s+(?:is|was)\s+(.+)$/i)?.[1] ??
    said.match(/^(.+?)\s+was\s+the\s+circle\b/i)?.[1];
  const name = named?.trim().replace(/^the\s+/i, "");
  return name && /^grade\s+[a-z0-9]{1,2}$/i.test(name) ? name.replace(/^grade/i, "Grade").replace(/\b(r)$/, "R") : null;
}

/** Urgent, an attachment and a schedule asked for at once: each is its own step on the same Notice. */
const NOTICE_STEPS: Array<[RegExp, string]> = [
  [/\burgent\b/i, "turn on Mark as urgent notice on the first step"],
  [/\battach/i, "add the form under Attachments"],
  [/\bschedul/i, "choose Schedule for later on Scheduling"],
];

/** "Who do you mean?", "what name?": a question about the step that's waiting, not a new one. */
const ABOUT_THIS_STEP = /^(?:who|what|which)\s+(?:do you mean|name|one)\b|^(?:what do you need|whose name)\b/i;

/** What still holds for someone who can't open the page a question is about. */
const GATED_FACT: Record<string, string> = {
  "users.invite": "An invite can use an email or a phone number.",
  "home.joinRequests": "Join requests are listed on Users.",
  "users.joinRequests": "Join requests are listed on Users.",
};

/** This chat sees the dashboard, never a parent's own phone. */
const CANT_SEE_THEIR_PHONE =
  "I can't see a parent's phone. Ask them to open the Noticeboard app, check they've joined, and look for the Notice there.";

/** While the matcher loads, or if it failed to: page search still works, questions don't. */
export const SEARCH_ONLY_REPLY =
  "Question matching is unavailable. You can still search pages or choose an example question.";

/** What the chat says after opening an instant search result, e.g. "Here's the help article: What is Noticeboard?" */
export const openedReply = (kind: string, label: string) =>
  // On-screen names keep their capital: Notice, User, Circle
  `Here's the ${["Notice", "User", "Circle"].includes(kind) ? kind : kind.toLowerCase()}: ${label}`;

/** Words that make a message sound like it's about Noticeboard (singular, as significantWords leaves them). */
const NOTICEBOARD_WORDS = new Set([
  "notice",
  "draft",
  "event",
  "calendar",
  "user",
  "parent",
  "circle",
  "grade",
  "class",
  "team",
  "member",
  "admin",
  "owner",
  "creator",
  "role",
  "library",
  "worksheet",
  "resource",
  "certificate",
  "award",
  "signature",
  "template",
  "drive",
  "file",
  "folder",
  "attachment",
  "help",
  "tutorial",
  "ad",
  "advert",
  "advertisement",
  "account",
  "profile",
  "plan",
  "organization",
  "school",
  "church",
  "invite",
  "invitation",
  "family",
  "guardian",
  "join",
  "joined",
  "request",
  "rsvp",
  "poll",
  "reminder",
  "urgent",
  "publish",
  "published",
  "schedule",
  "scheduled",
  "send",
  "sent",
]);

/** Words that are never about Noticeboard, however close the rest of the message is to a question. */
const OFF_TOPIC_WORDS = new Set([
  "weather", "forecast", "joke", "recipe", "horoscope", "lottery", "dentist", "doctor", "rugby", "soccer", "football",
  "cricket", "netball", "flight", "taxi", "uber", "restaurant",
]);

/** Starts like a question ("How do I…", "What…", "Tell me about…", "Explain…"): it asks about something, so it never starts a task. */
const QUESTION_START =
  /^(how|where|why|what|whats|which|who|whos|whose|when|is|are|do|does|did|have|has|was|were|should|can|could|would)\b|^(?:tell me(?: more)? about|explain)\b/;

/** "Can you send a reminder?": a polite request to do the task, never a question about it. */
const POLITE_REQUEST = /^(?:can|could|would|will)\s+you\b/;

/**
 * "Can we send notices?": the school asking to do it, not whether it's possible.
 * Checked on the phrasing key, where "let's" has already lost its apostrophe.
 */
const COLLECTIVE_REQUEST =
  /^(?:(?:can|could|would|shall)\s+we\s+(?:please\s+)?|lets\s+)(?:send|create|write|post|publish|make|draft)\b/;

/**
 * Asked the assistant to send, create or publish a Notice, Poll or Event.
 * A how-to question, a repeat of something already sent, and anything it cannot
 * do (Zoom, email, a register) stay explanations or refusals.
 */
export function wantsHelpDoing(text: string): boolean {
  const q = text.trim();
  if (/^(how|where|why|what|whats|which|who|when|is|are|do|does|did)\b/i.test(q)) return false;
  if (/\b(again|same|reminder|duplicate|copy|confirm|already|zoom|register|delete|email|whatsapp|fee)\b/i.test(q)) return false;
  if (/\b(show me|tell me|explain|what does|where is)\b/i.test(q)) return false;
  const asksToDo =
    /^(?:(?:can|could|would|will)\s+you\b|please\b|help me\b|(?:can|could|would|shall)\s+we\b|let'?s\b|i\s+(?:need|want|have)\s+to\s+(?:send|create|write|post|publish|make|draft)\b)/i.test(q);
  const imperative = /^(?:send|create|write|post|publish|make|draft)\b/i.test(q);
  const aboutWork = /\b(?:notice|poll|event|announcement|message)\b/i.test(q);
  return (asksToDo || imperative) && (aboutWork || /^(?:post|send)\s+(?:this|it)\b/i.test(q));
}

/**
 * "I need to send an important notice titled Hello", "Let’s create a notice": the work is named outright,
 * so it starts even when the match is unsure. A reminder stays a question.
 */
export function obviousWorkRequest(text: string): { task: "notice" | "poll" | "event"; startedBy: string } | null {
  // "Write the notice for me about the gala" asks for a Notice to be prepared, not for wording help.
  if (/^(?:please\s+)?(?:write|draft)\s+(?:a|the|my)\s+notice\s+for\s+me\b/i.test(text.trim())) {
    return { task: "notice", startedBy: "create.notice" };
  }
  // "Please send a notice about the gala": a Notice, with what it's about already named.
  if (
    /^(?:(?:please|ok(?:ay)?,?)\s+)?(?:(?:can|could|would|will)\s+you\s+|i\s+(?:want|need)\s+(?:you\s+)?to\s+|let'?s\s+)?send\s+(?:a|an)\s+notice\s+about\s+/i.test(text.trim()) &&
    !/\b(?:one parent|a parent|one person|reminder|urgent)\b/i.test(text)
  ) {
    return { task: "notice", startedBy: "create.notice" };
  }
  // "Let's send a fresh one" after a missing notice: a new Notice.
  if (/^(?:ok(?:ay)?,?\s+)?let'?s\s+send\s+a\s+(?:fresh|new)\s+one\b/i.test(text.trim())) {
    return { task: "notice", startedBy: "create.notice" };
  }
  const match = text.trim().match(
    /^(?:i\s+(?:need|want|have)\s+to\s+|let['’]?s\s+)(?:send|create|write|post|publish|make|draft)\s+(?:(?:an?|the|some|this)\s+)*(?:important\s+|new\s+|urgent\s+)*(notices?|announcements?|messages?|polls?|events?)\b/i
  );
  if (!match || /\breminder\b/i.test(text)) return null;
  if (/\bpoll\b/i.test(text) && /\burgent\b/i.test(text)) return null;
  const noun = match[1].toLowerCase();
  if (noun.startsWith("poll")) return { task: "poll", startedBy: "create.notice" };
  if (noun.startsWith("event")) return { task: "event", startedBy: "calendar.createEvent" };
  return { task: "notice", startedBy: "create.notice" };
}

/** How many words a negation ("isn't") and the verb it's negating ("showing") can have between them. */
const PROBLEM_PHRASE_GAP = 20;

/**
 * Says something has gone wrong or isn't working, so it's asking for help rather than
 * starting a task: "isn't showing", "aren't appearing", "can't find", "I accidentally…".
 */
const PROBLEM_PHRASE = new RegExp(
  `\\b(?:isn't|aren't|wasn't|weren't|doesn't|don't|didn't|can't|cannot|couldn't|won't|wouldn't)\\b.{0,${PROBLEM_PHRASE_GAP}}?\\b(?:show(?:ing)?|appear(?:ing)?|work(?:ing)?|load(?:ing)?|send(?:ing)?|arriv(?:e|ing)|find|found|see|seen|open(?:ing)?|receiv\\w*|updat\\w*|get(?:ting)?|got)\\b|\\baccidentally\\b`,
  "i"
);

const SUGGESTION_COUNT = 3;

/** The question a catalogue entry is shown as. */
export const exampleQuestion = (entry: CatalogueEntry) => entry.examples[0];

/** Entries whose example question is written with a Circle name that may not exist in this Organization. */
const NAMES_A_CIRCLE = new Set(["circles.members", "calendar.forCircle", "notices.toCircle"]);

/** The "Grade 2" style placeholder those entries' examples are written with. */
const CIRCLE_PLACEHOLDER = /\bGrade \d+\b/;

/** The Circle to name in a suggestion: the one with the most members, the most recently created breaking a tie. */
export function biggestCircleName(
  circles: Array<{ name: string; memberCount?: number; createdAt?: string }>
): string | null {
  if (circles.length === 0) return null;
  return [...circles].sort(
    (a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0) || (b.createdAt ?? "").localeCompare(a.createdAt ?? "")
  )[0].name;
}

/**
 * `exampleQuestion`, with a Circle-naming placeholder swapped for a real Circle
 * this role can open, or worded generically when the Organization has none, so
 * tapping it never gives "I couldn't find a Circle called…".
 */
export function exampleQuestionNamed(entry: CatalogueEntry, circleName: string | null): string {
  const text = exampleQuestion(entry);
  return NAMES_A_CIRCLE.has(entry.id) ? text.replace(CIRCLE_PLACEHOLDER, circleName ?? "a Circle") : text;
}

/** The real Circle name to show in a list of suggestions, loaded only when one of them needs it. */
async function circleNameFor(entries: CatalogueEntry[], ctx: AnswerContext): Promise<string | null> {
  if (!entries.some((e) => NAMES_A_CIRCLE.has(e.id))) return null;
  try {
    return biggestCircleName(await loadCircles(ctx));
  } catch (error) {
    console.error("[Ask Noticeboard] Couldn't load Circles for a suggestion", error);
    return null;
  }
}

/** `entries`' example questions, with a Circle-naming placeholder filled in for the whole list at once. */
async function namedQuestions(entries: CatalogueEntry[], ctx: AnswerContext): Promise<string[]> {
  const circleName = await circleNameFor(entries, ctx);
  const questions = entries.map((e) => exampleQuestionNamed(e, circleName));
  return questions.filter((question, index) => questions.indexOf(question) === index);
}

/** The entries behind those example questions, for showing each with its page. Tasks aren't questions, so they're left out. */
export function pickExampleEntries(
  entries: CatalogueEntry[],
  count = 4
): CatalogueEntry[] {
  const available = entries.filter((e) => e.answer);
  if (available.length <= count) return available;
  const step = available.length / count;
  return Array.from({ length: count }, (_, i) => available[Math.floor(i * step)]);
}

function soundsLikeNoticeboard(text: string): boolean {
  return Array.from(significantWords(text)).some((w) => NOTICEBOARD_WORDS.has(w));
}

const soundsOffTopic = (text: string) =>
  !soundsLikeNoticeboard(text) && Array.from(significantWords(text)).some((w) => OFF_TOPIC_WORDS.has(w));

/** Asked for dates the dashboard doesn't keep, like a school term. */
export const WHICH_DATES_REPLY =
  "There are no school terms on the dashboard, so I can't tell which dates you mean. Which dates would you like?";
const DATE_CHOICES = ["This month", "Last month", "This year"];

/** The details an entry takes, from the message where it says them, otherwise the entry's defaults. */
function slotsFrom(text: string, entry: AnswerEntry): SlotValues {
  const slots: SlotValues = {};
  if (entry.slots?.dateRange) slots.dateRange = findDateRange(text) ?? entry.slots.dateRange;
  const named = namedSlotOf(entry);
  return named ? { ...slots, ...slotValue(named, text) } : slots;
}

const fill = (
  { dateRange, notice, circle, sender, child, user, member, role, worksheet, award, file }: SlotValues,
  entry: AnswerEntry,
  now: Date
): FilledSlots => ({
  dateRange: (dateRange && resolveDateRange(dateRange, now, entry.dates)) ?? undefined,
  notice,
  circle,
  sender,
  child,
  user,
  member,
  role,
  worksheet,
  award,
  file,
});

/** A follow-up question with choices; the detail it asks for is what the next message fills in. */
function askSlot(id: string, said: SlotValues, question: SlotQuestion, startedByText?: string): AssistantReply {
  const { [question.slot]: _asked, ...rest } = said;
  return {
    kind: "askSlot",
    text: question.text,
    choices: question.choices,
    reason: question.reason,
    asked: {
      id,
      slots: rest,
      awaiting: question.slot,
      startedByText,
      ...(question.choices.length ? { awaitingChoices: question.choices } : {}),
    },
  };
}

/** Loads the values and words the answer; a failed load is never shown as a zero. */
async function answer(
  entry: AnswerEntry,
  slots: SlotValues,
  ctx: AnswerContext,
  rotation: number,
  fromFallback = false,
  askedText = ""
): Promise<AssistantReply> {
  const filled = fill(slots, entry, new Date());
  if (slots.dateRange && !filled.dateRange) {
    return askSlot(entry.id, slots, askFor("dateRange", "missing", WHICH_DATES_REPLY, DATE_CHOICES));
  }
  const { load, templates, link, subjectOf } = entry.answer;
  try {
    const values = await load(filled, ctx);
    if (isSlotQuestion(values)) return askSlot(entry.id, slots, values);
    const declaredSubject = subjectOf?.(values);
    const mentions = Array.from(
      new Set((entry.answer.mentionsOf?.(values) ?? []).map((title) => title.trim()).filter(Boolean))
    );
    const subject = declaredSubject ?? (mentions.length === 1 ? mentions[0] : undefined);
    return {
      kind: "answer",
      text: templates[rotation % templates.length](values, filled, askedText),
      link: link(filled, values),
      asked: {
        id: entry.id,
        slots,
        ...(subject ? { subject } : {}),
        ...(mentions.length ? { mentions } : {}),
      },
      ...(fromFallback ? { fromFallback: true } : {}),
    };
  } catch (error) {
    console.error(`[Ask Noticeboard] Couldn't load the answer to ${entry.id}`, error);
    return { kind: "loadFailed", text: LOAD_FAILED_REPLY, asked: { id: entry.id, slots, ...(slots.notice ? { subject: slots.notice, mentions: [slots.notice] } : {}) } };
  }
}

const hasHandoff = (entry: CatalogueEntry): entry is HandoffEntry & Required<Pick<HandoffEntry, "handoff">> =>
  Boolean(entry.handoff);

const hasHowTo = (entry: CatalogueEntry): entry is HowToEntry => Boolean(entry.howTo);

/** A "how do I…?" answer: steps to read, and the button(s) that do the real thing. */
async function answerHowTo(
  entry: HowToEntry,
  ctx: AnswerContext,
  asked: string,
  fromFallback = false
): Promise<AssistantReply> {
  const { text, actions } = await entry.howTo.load(ctx, asked);
  return {
    kind: "howTo",
    text,
    actions,
    // Kept so a later "which step are you stuck on?" can reload the same steps
    asked: { id: entry.id, slots: {}, startedByText: asked },
    ...(fromFallback ? { fromFallback: true } : {}),
  };
}

/** How many times "I don't see it" (or the like) can follow the same answer before Help Center is offered instead of repeating it. */
const STUCK_AGAIN_AFTER = 1;

/** Carries the attempt count forward, so a second time in a row escalates instead of repeating the same help. */
const afterAnotherStuckMessage = (previous: AskedQuestion): AskedQuestion => ({
  ...previous,
  recoveryAttempts: (previous.recoveryAttempts ?? 0) + 1,
});

/** Help Center and the Fundamentals course: the fallback once pointing back at the same answer hasn't helped. */
const stillStuckReply = (asked: AskedQuestion): AssistantReply => ({
  kind: "recovery",
  text: STILL_STUCK_REPLY,
  actions: STILL_STUCK_ACTIONS,
  asked,
});

/**
 * The page recovery should describe: a Help Center "how do I…?" answer (see
 * `helpCenter.ts`'s `howToEntry`) is filed under Help Center for grouping, but
 * what it's actually about — and the page "I don't see it" means — is the one
 * page it names in `alsoNeeds`. Everything else is about its own sidebar item.
 */
const pageFor = (entry: CatalogueEntry): SidebarItemKey =>
  entry.sidebarItem === "helpCenter" && entry.alsoNeeds?.length === 1 ? entry.alsoNeeds[0] : entry.sidebarItem;

/**
 * "I don't see it", "where is that?": stuck on the last answer rather than asking
 * something new, so it's never matched against the catalogue or called off-topic.
 * The first time, it offers a `HowToEntry`'s own steps as chips (most "how do
 * I…?" questions are plain page answers in prose, not this type, and go
 * through the page branch below instead, same as any other answer); otherwise
 * it says where a page sits, or who can open it if this role can't. A second
 * time in a row, it points to Help Center instead of repeating the same help.
 */
async function recallFacts(entry: CatalogueEntry, previous: AskedQuestion, ctx: AnswerContext): Promise<string> {
  try {
    if (hasHowTo(entry)) {
      const loaded = await entry.howTo.load(ctx, previous.startedByText ?? "");
      return loaded.text;
    }
    if (entry.answer) {
      const reply = await answer(entry, previous.slots, ctx, 0, false, previous.startedByText || "");
      return textOf(reply);
    }
  } catch (error) {
    console.error(`[Ask Noticeboard] Couldn't reload ${entry.id} while recovering`, error);
  }
  return "";
}

async function recoveryReply(previous: AskedQuestion, entry: CatalogueEntry, ctx: AnswerContext): Promise<AssistantReply> {
  const asked = afterAnotherStuckMessage(previous);
  if (entry.id === "concepts.adCenter") {
    return {
      kind: "recovery",
      text: "If you mean Ad Center, a Creator can't open it. Going back to Home won't unlock it. An Admin has to set up the adverts.",
      asked,
    };
  }
  if (entry.id === "concepts.doesNotSend") {
    const create = getSidebarItem("create");
    return {
      kind: "recovery",
      text: "Nothing has been sent. You review the Notice on Create and press Publish yourself. I don't send it.",
      link: { label: `Open ${create.label}`, href: create.path },
      asked,
    };
  }
  if (entry.id === "concepts.whoSeesCircle" || entry.id === "concepts.addToCircle" || entry.id === "concepts.createCircle") {
    return {
      kind: "recovery",
      text: "Which part didn't work — opening the Circle, or checking who receives its Notices? As a Creator you can't open the Circle member list. An Owner or Admin can check it.",
      asked,
    };
  }
  if ((previous.recoveryAttempts ?? 0) >= STUCK_AGAIN_AFTER) {
    const recalled = await recallFacts(entry, previous, ctx);
    return {
      kind: "recovery",
      text: recalled ? `${STILL_STUCK_REPLY} ${recalled}` : STILL_STUCK_REPLY,
      actions: STILL_STUCK_ACTIONS,
      asked,
    };
  }

  if (hasHowTo(entry)) {
    try {
      const { text: howToText } = await entry.howTo.load(ctx, previous.startedByText ?? "");
      const steps = stepsOf(howToText);
      if (steps.length > 1) {
        return { kind: "recovery", text: `${WHICH_STEP_REPLY} ${howToText}`, choices: steps, asked };
      }
      return {
        kind: "recovery",
        text: `${howToText} ${STILL_STUCK_REPLY}`,
        actions: STILL_STUCK_ACTIONS,
        asked,
      };
    } catch (error) {
      console.error(`[Ask Noticeboard] Couldn't reload ${entry.id}'s steps`, error);
    }
    return stillStuckReply(asked);
  }

  const page = pageStuckReply(pageFor(entry), ctx.user);
  const recalled = await recallFacts(entry, previous, ctx);
  const reminder = handoffReminder(entry.id);
  const text = [page.text, recalled, reminder && recalled.includes(reminder) ? "" : reminder].filter(Boolean).join(" ");
  return { kind: "recovery", text, ...(page.kind === "pointAgain" ? { link: page.link } : {}), asked };
}

/** Starts a task: a summary card to open, or one short follow-up question with choices. */
/** "What role does Sam have?", "What's Sam's role?", "What is Thandi Nkosi's role?" */
const ROLE_OF_PERSON =
  /^(?:what(?:'s|\s+is)?\s+role\s+does\s+(.+?)\s+have|what(?:'s|\s+is)\s+(.+?)['’]s\s+role)\s*[?.!]*$/i;
/** "Add Lebo Dlamini as an Admin", "add Sipho to the team as a Creator" */
const ADD_PERSON_AS_ROLE = /^add\s+(.+?)\s+(?:to\s+(?:the\s+)?team\s+)?as\s+(?:an?\s+)?(creator|owner|admin)\b[\s.!]*$/i;
/** Words for a group, never one person's name: "Add Grade 2 parents as…" isn't a team member */
const SOUNDS_LIKE_A_GROUP =
  /\b(?:grade|class|parents|families|circle|everyone|all|them|him|her|someone|somebody|members?|teachers?|person|people|users?|staff)\b/i;

/** Handoffs that go on to start a Notice for a Circle in this chat. One parent's Notice isn't held up by having no Circles. */
const OFFERS_A_NOTICE = new Set(["create.offerToSend", "create.scheduleOffer"]);

async function startHandoff(
  entry: HandoffEntry & Required<Pick<HandoffEntry, "handoff">>,
  said: SlotValues,
  ctx: AnswerContext,
  startedByText: string
): Promise<AssistantReply> {
  try {
    // "Can you send a notice for me?" with nowhere to send it: said now, not after choosing a kind
    // When the Circles can't be loaded, the offer goes ahead and the Notice's own start says why later
    if (OFFERS_A_NOTICE.has(entry.id)) {
      const org = await loadTaskContext(ctx).catch(() => null);
      const cannot = org ? noticeTask.cannotStart(org) : undefined;
      if (cannot) return { kind: "task", text: cannot, choices: [], task: null };
    }
    const started = await entry.handoff(said, ctx);
    switch (started.kind) {
      case "ready":
        return {
          kind: "handoff",
          text: started.text,
          summary: started.summary,
          to: started.to,
          asked: { id: entry.id, slots: said, ...(said.notice ? { subject: said.notice, mentions: [said.notice] } : {}) },
        };
      case "ask":
        return askSlot(entry.id, said, started, startedByText);
      case "cannot":
        return { ...started, asked: { id: entry.id, slots: said, ...(said.notice ? { subject: said.notice, mentions: [said.notice] } : {}) } };
    }
  } catch (error) {
    console.error(`[Ask Noticeboard] Couldn't start ${entry.id}`, error);
    return { kind: "loadFailed", text: LOAD_FAILED_REPLY, asked: { id: entry.id, slots: said, ...(said.notice ? { subject: said.notice, mentions: [said.notice] } : {}) } };
  }
}

/** How many other matches "that's not what I meant" offers. */
const ALTERNATIVE_COUNT = 2;

/**
 * The top other matches for the message that started a task or handoff, as example
 * questions: ranked again, excluding the entry it actually started (which needn't be
 * the top scorer: a task can be chosen over a higher-scoring question, see `confidentMatch`).
 */
async function otherMatches(
  text: string,
  excludingId: string,
  byId: Map<string, CatalogueEntry>,
  matcher: QuestionMatcher,
  ctx: AnswerContext
): Promise<string[]> {
  const ranked = (await matcher.rank(text, Array.from(byId.keys()))).sort((a, b) => b.score - a.score);
  const entries = ranked
    .filter((r) => r.id !== excludingId)
    .slice(0, ALTERNATIVE_COUNT)
    .map((r) => byId.get(r.id))
    .filter((e): e is CatalogueEntry => Boolean(e));
  return namedQuestions(entries, ctx);
}

/**
 * A free-text answer shaped like a question, checked against the catalogue: the
 * text of a confident match's own answer, or undefined when nothing matches it
 * clearly enough to be sure it's a side question rather than the value.
 */
async function sideQuestionAnswer(
  text: string,
  byId: Map<string, CatalogueEntry>,
  matcher: QuestionMatcher,
  ctx: AnswerContext,
  rotation: number
): Promise<{ id: string; text: string } | undefined> {
  const questionIds = Array.from(byId.values())
    .filter((e) => e.answer || hasHowTo(e))
    .map((e) => e.id);
  if (questionIds.length === 0) return undefined;
  let ranked: RankedEntry[];
  try {
    ranked = bestScoreEach(await matcher.rank(text, questionIds)).sort((a, b) => b.score - a.score);
  } catch (error) {
    console.error("[Ask Noticeboard] Couldn't match a side question", error);
    return undefined;
  }
  const [top, runnerUp] = ranked;
  if (!top || !leads(top, runnerUp, matcher.thresholds)) return undefined;
  const entry = byId.get(top.id);
  if (entry && hasHowTo(entry)) {
    const reply = await answerHowTo(entry, ctx, text, false);
    return reply.kind === "howTo" ? { id: entry.id, text: reply.text } : undefined;
  }
  if (!entry?.answer) return undefined;
  const reply = await answer(entry, slotsFrom(text, entry), ctx, rotation, false, text);
  return reply.kind === "answer" ? { id: entry.id, text: reply.text } : undefined;
}

/** "oh wait before I forget — when is the science fair? did we…": the first question, without the words leading up to it. */
function firstQuestionIn(text: string): string {
  const unled = text.trim().replace(/^[^?—–]*?\s[—–-]\s+/, "");
  return (unled.match(/^[^?]*\?/)?.[0] ?? unled).trim();
}

/**
 * A question asked while a draft is open, answered as it would be with no draft: a confident
 * match first, then the whole reply path (which also finds Circles and Notices by name), or
 * that the Notice it names doesn't exist. Undefined when it would only guess, or start something.
 */
async function answerOnTheSide(text: string, { ctx, available, matcher, rotation }: ReplySession): Promise<{ id?: string; text: string } | undefined> {
  if (!matcher) return undefined;
  const question = firstQuestionIn(text);
  const byId = new Map(available.map((e) => [e.id, e]));
  const pollPrivacy = /\bwho voted\b/i.test(question) ? byId.get("create.pollPrivacy") : undefined;
  if (pollPrivacy && hasHowTo(pollPrivacy)) {
    const reply = await answerHowTo(pollPrivacy, ctx, question, false);
    if (reply.kind === "howTo") return { id: pollPrivacy.id, text: reply.text };
  }
  const quick = await sideQuestionAnswer(question, byId, matcher, ctx, rotation.answers);
  if (quick) return quick;
  const reply = await replyTo(question, { available, matcher, ctx, rotation, previous: null, task: null });
  if (reply.kind === "unanswered" && reply.notFound) return { text: reply.notFound };
  if ((reply.kind === "answer" || reply.kind === "howTo" || reply.kind === "cannot") && reply.text) {
    return { id: reply.asked?.id, text: reply.text };
  }
  return undefined;
}

/** A task's detail as "Is that … or a question for me?" names it. */
const STEP_NAMES: Record<string, string> = {
  title: "the title",
  message: "the message",
  question: "the question",
  choices: "the choices",
  day: "the day",
  time: "the time",
  place: "the place",
  to: "who it's for",
};

/** What a guided task's turn needs beyond the message itself, travelling together through every call in a reply. */
type ReplySession = {
  ctx: AnswerContext;
  available: CatalogueEntry[];
  matcher: QuestionMatcher | null;
  rotation: { answers: number; offTopic: number };
};

/** A guided task's turn, with what it needs to know about the Organization loaded first; null when the message isn't part of the task. */
async function taskTurn(
  turn: (org: TaskContext) => TaskTurn | null,
  { ctx, available, matcher, rotation }: ReplySession
): Promise<AssistantReply | null> {
  let org: TaskContext;
  try {
    org = await loadTaskContext(ctx);
  } catch (error) {
    console.error("[Ask Noticeboard] Couldn't load the Circles for a task", error);
    return { kind: "loadFailed", text: LOAD_FAILED_REPLY };
  }
  const next = turn(org);
  if (!next) return null;
  const { text, choices = [], summary, to, offerAlternativesTo, checkSideQuestion } = next.reply;
  if (checkSideQuestion && next.record) {
    const session = { ctx, available, matcher, rotation };
    // "can I send a reminder before it?" while making an Event: "it" is that Event
    const kindWord = next.record.task === "event" ? "the event" : next.record.task === "poll" ? "the poll" : "the notice";
    const named = checkSideQuestion.text.replace(/\bit\b/i, kindWord);
    const answered =
      (await answerOnTheSide(checkSideQuestion.text, session)) ??
      (named !== checkSideQuestion.text ? await answerOnTheSide(named, session) : undefined);
    if (answered) {
      const resumed = checkSideQuestion.fieldLabel === "to" ? resumeTask(next.record, org).reply : undefined;
      return {
        kind: "task",
        text: `${answered.text} ${resumed?.text ?? carryOnPrompt(next.record.task)}`,
        choices: resumed?.choices ?? [],
        task: next.record,
        ...(answered.id ? { asked: { id: answered.id, slots: {} } } : {}),
      };
    }
    if (checkSideQuestion.fieldLabel === "to") {
      const resumed = resumeTask(next.record, org);
      return { kind: "task", task: resumed.record, choices: resumed.reply.choices ?? [], text: `I couldn't answer that question. ${resumed.reply.text}` };
    }
    const detail = checkSideQuestion.fieldLabel;
    const label = STEP_NAMES[detail] ?? detail;
    return {
      kind: "task",
      text: `Is that ${label}, or a question for me?`,
      choices: [`Use as ${STEP_NAMES[detail] ? label.replace(/^the\s+/, "") : detail}`, "It's a question"],
      task: { ...next.record, pendingFreeText: { fieldLabel: detail, text: checkSideQuestion.text } },
    };
  }
  const alternatives =
    offerAlternativesTo && matcher
      ? await otherMatches(
          offerAlternativesTo.text,
          offerAlternativesTo.excludingId,
          new Map(available.map((e) => [e.id, e])),
          matcher,
          ctx
        )
      : [];
  if (!next.record && /nothing was created|nothing was sent/i.test(text)) {
    return { kind: "cancelled", text, choices: alternatives, dropTask: true };
  }
  return {
    kind: "task",
    text,
    choices: [...choices, ...alternatives],
    ...(summary && to ? { summary, to } : {}),
    task: next.record,
  };
}

/** "testing. then do the bus notice next": whether the first sentence is the open task's next answer. */
async function takesFirstSentence(record: TaskRecord, text: string, { ctx }: ReplySession): Promise<boolean> {
  const first = text.trim().split(/[.!?]+\s+/)[0];
  try {
    return takesAsADetail(record, first, await loadTaskContext(ctx));
  } catch {
    return false;
  }
}

/** "testing?" when asked who it's for: the step's answer, however it's punctuated. */
async function takesWhole(record: TaskRecord, text: string, { ctx }: ReplySession): Promise<boolean> {
  try {
    return takesAsADetail(record, text, await loadTaskContext(ctx));
  } catch {
    return false;
  }
}

/** "Carry on": the next question or summary for a task reopened in progress. */
export async function resumeTaskReply(
  record: TaskRecord,
  session: ReplySession
): Promise<AssistantReply | null> {
  return taskTurn((org) => resumeTask(record, org), session);
}

/** One ranking worth asking for in a batch; left out of it (and ranked as empty) when `worthIt` is false or there's nothing to rank against. */
type RankSlot = { text: string; allowedIds: string[]; worthIt?: boolean };

/**
 * `matcher.rankMany`, skipping slots not worth sending, so one call carries every
 * ranking this message needs: a matcher that embeds text (see `createEmbeddingMatcher`)
 * can then embed each distinct text once, however many slots share it.
 */
async function rankSlots(matcher: QuestionMatcher, slots: RankSlot[]): Promise<RankedEntry[][]> {
  const sendable = (slot: RankSlot) => (slot.worthIt ?? true) && slot.allowedIds.length > 0;
  const results = await matcher.rankMany(slots.filter(sendable).map(({ text, allowedIds }) => ({ text, allowedIds })));
  let next = 0;
  return slots.map((slot) => (sendable(slot) ? results[next++] : []));
}

/** Each entry once, with its best score, and exact if either phrasing was. */
function bestScoreEach(ranked: RankedEntry[]): RankedEntry[] {
  const best = new Map<string, RankedEntry>();
  ranked.forEach(({ id, score, exact }) => {
    const seen = best.get(id);
    best.set(id, {
      id,
      score: Math.max(score, seen?.score ?? 0),
      ...(exact || seen?.exact ? { exact: true as const } : {}),
    });
  });
  return Array.from(best.values());
}

/** Whether `first` is far enough ahead of `second` to answer without asking. */
const leads = (first: RankedEntry, second: RankedEntry | undefined, { answer, clearLead }: MatchThresholds) =>
  first.score >= answer && (!second || first.score - second.score >= clearLead);

/**
 * The entry to answer without asking, if there is one: an example phrasing asked
 * word for word, else one clearly ahead of the rest. A request ("Create an event")
 * starts its task when no question fits it clearly better.
 */
function confidentMatch(
  ranked: RankedEntry[],
  tasks: RankedEntry[],
  thresholds: MatchThresholds,
  preferTask = false
): RankedEntry | undefined {
  // The same entry can be ranked twice (as a question and as a task). Count it once,
  // so one word-for-word example still wins.
  const exact = bestScoreEach(ranked.filter((r) => r.exact));
  const [task, nextTask] = tasks;
  const taskReady = Boolean(task && leads(task, nextTask, thresholds));
  const exactRefusal = exact.length === 1 && /^(unsupported|troubleshooting)\./.test(exact[0].id);
  // "Can you send a notice?" starts the flow when that task is a real match.
  // A refusal ("Start a Zoom call") stays a refusal.
  // A request starts its task, unless the words they typed are already one specific
  // answer ("send it to one class", "send an sms"). "We don't send it for you"
  // stays behind the task: that line is the refusal for a different question.
  const exactBeatsTask =
    exact.length === 1 && exact[0].id !== task?.id && exact[0].id !== "concepts.doesNotSend";
  if (preferTask && taskReady && !exactRefusal && !exactBeatsTask) return task;
  // One phrasing can be an example of two entries ("How many users do we have?").
  // Answering one of them beats offering that same phrasing back.
  if (exact.length > 0) return [...exact].sort((a, b) => b.score - a.score)[0];
  const [top, ...rest] = ranked;
  // A runner-up that gives the same answer isn't a reason to ask which one
  const runnerUp = top ? rest.find((r) => !sameAnswer(top.id, r.id)) : undefined;
  if (top && leads(top, runnerUp, thresholds)) return top;
  if (!task || !leads(task, nextTask, thresholds)) return undefined;
  const question = ranked.find((r) => !tasks.includes(r));
  return !question || task.score >= question.score - thresholds.clearLead ? task : undefined;
}

/** Entries that answer the same question in the same way: one ranked close behind the other isn't a different reading. */
const SAME_ANSWER: string[][] = [["create.dmUserHowTo", "create.sendToOneParent"]];
const sameAnswer = (a: string, b: string) => a === b || SAME_ANSWER.some((group) => group.includes(a) && group.includes(b));

/** A follow-up that names something we couldn't find may not be a follow-up at all. */
const missedTheTarget = (reply: AssistantReply) =>
  reply.kind === "askSlot" && reply.reason === "notFound";

/** A fragment that keeps talking about the last answer: "when", "k when", "what event?", "where tho". */
export const isBareFollowUp = (text: string): boolean => followUpAspect(text) !== null;

/** A page this person can open, for a one-line follow-up. A hidden page stays on Home. */
function linkFor(entry: CatalogueEntry, ctx: AnswerContext): AnswerLink {
  const page = getSidebarItem(entry.sidebarItem);
  const home = getSidebarItem("home");
  const target = canOpenSidebarItem(page, ctx.user) ? page : home;
  return { label: `Open ${target.label}`, href: target.path };
}

function pointed(entry: CatalogueEntry, text: string, previous: AskedQuestion, ctx: AnswerContext): AssistantReply {
  return {
    kind: "answer",
    text,
    link: linkFor(entry, ctx),
    asked: {
      id: entry.id,
      slots: previous.slots,
      ...(previous.subject ? { subject: previous.subject } : {}),
      ...(previous.mentions?.length ? { mentions: previous.mentions } : {}),
      ...(previous.startedByText ? { startedByText: previous.startedByText } : {}),
    },
  };
}

function textOf(reply: AssistantReply): string {
  return "text" in reply ? reply.text : "";
}

/**
 * A handoff has no how-to to repeat. The follow-up still has to name the topic,
 * or "what time is that?" answers as if nothing was just said.
 */
const HANDOFF_REMINDER: Record<string, string> = {
  "drive.upload": "Nothing is uploaded until you press Upload & Share.",
  "create.fromTemplate": "That was about a template on Create. You still review it before Publish.",
  "certificates.newAward": "That was about a certificate or an Award. Nothing is saved until you press Save and add Awardees.",
  "create.continueDraft": "A draft is a Notice you have not published.",
};

const handoffReminder = (id: string): string | null => HANDOFF_REMINDER[id] ?? null;

/** The last answer again, when the follow-up is "huh" or "more" rather than a new fact. */
async function reanswer(
  entry: CatalogueEntry,
  ctx: AnswerContext,
  rotation: { answers: number; offTopic: number },
  usingFallback: boolean,
  previous: AskedQuestion
): Promise<AssistantReply | null> {
  if (hasHowTo(entry)) return answerHowTo(entry, ctx, previous.startedByText || "");
  if (entry.answer) return answer(entry, previous.slots, ctx, rotation.answers, usingFallback, previous.startedByText || "");
  const reminder = handoffReminder(entry.id);
  if (reminder) return pointed(entry, reminder, previous, ctx);
  return null;
}

/**
 * Answers "when", "which event", "where" or "who" about the last topic.
 * The line answers that word. The last answer stays with it, so the fact is not lost.
 * While a task is collecting a title, the short line stands alone.
 */
async function followUpReply(
  aspect: FollowUpAspect,
  entry: CatalogueEntry,
  ctx: AnswerContext,
  rotation: { answers: number; offTopic: number },
  usingFallback: boolean,
  previous: AskedQuestion,
  keepFacts: boolean
): Promise<AssistantReply> {
  const withFacts = (line: string, base: AssistantReply | null): AssistantReply => {
    const body = base ? textOf(base) : "";
    if (keepFacts && base && body) return { ...base, text: `${line} ${body}` };
    return pointed(entry, line, previous, ctx);
  };
  // "Where is it?" after one named Notice: that Notice's own place or audience, not its time again.
  if ((aspect === "where" || aspect === "who") && entry.id.startsWith("notices.") && previous.subject) {
    const pick = await findNoticeForWhen(previous.subject, ctx);
    if (pick && !isSlotQuestion(pick)) {
      const { notice } = pick;
      const circles = aspect === "where" ? [] : await loadCircles(ctx).catch(() => []);
      const line = aspect === "where" ? placeOf(notice.title, notice) : audienceOf(notice.title, notice, "the Notice", circles);
      return { kind: "answer", text: line, link: noticeLink(notice), asked: previous };
    }
  }
  // "Are they recurring?" after named Notices: each one's own repeat rule
  if (aspect === "recurs") {
    const titles = Array.from(new Set([...(previous.mentions ?? []), ...(previous.subject ? [previous.subject] : [])]));
    const picks = await Promise.all(titles.map((title) => findNoticeForWhen(title, ctx)));
    const notices = picks.flatMap((pick) => (pick && !isSlotQuestion(pick) ? [pick.notice] : []));
    if (notices.length > 0) {
      return { kind: "answer", text: notices.map(recurrenceLine).join(" "), link: noticeLink(notices[notices.length - 1]), asked: previous };
    }
    return pointed(entry, "Which Event do you mean? Tell me its title and I'll say whether it repeats.", previous, ctx);
  }
  const named = entry.id === "home.upcomingEvents" || entry.id === "calendar.whatsOn";
  if (aspect === "where" && named) {
    return pointed(entry, await upcomingPlace(ctx), previous, ctx);
  }
  if (aspect === "who" && named) {
    return pointed(entry, await upcomingAudience(ctx), previous, ctx);
  }
  if (aspect === "repeat" || aspect === "where" || aspect === "who") {
    const base = await reanswer(entry, ctx, rotation, usingFallback, previous);
    if (!base) return pointed(entry, noTimeLine(entry.id), previous, ctx);
    const body = textOf(base);
    const namesEvent = namesAnEvent(body);
    const hasWhen = mentionsWhen(body);
    if (aspect === "where" && namesEvent && hasWhen && !/\b(place|location|venue|hall|pool|room|at )\b|aren'?t the place|no location/i.test(body)) {
      return { ...base, text: `${body} The title and the time aren't the place.` };
    }
    if (aspect === "who" && namesEvent && hasWhen && !/\b(audience|for the|who .+ is for|internal team|everyone|parents)\b|aren'?t the audience|isn'?t on the home card/i.test(body)) {
      return { ...base, text: `${body} The title and the time aren't the audience.` };
    }
    return base;
  }
  if (aspect === "event") {
    const base = keepFacts ? await reanswer(entry, ctx, rotation, usingFallback, previous) : null;
    if (base && namesAnEvent(textOf(base))) return base;
    return withFacts(eventLine(entry.id), base);
  }
  const base = keepFacts ? await reanswer(entry, ctx, rotation, usingFallback, previous) : null;
  const body = base ? textOf(base) : "";
  if (base && mentionsWhen(body)) return base;
  const mentioned = previous.mentions ?? [];
  if (mentioned.length > 1) return pointed(entry, whichMentioned(mentioned), previous, ctx);
  if (mentioned.length === 1) {
    const pick = await findNoticeForWhen(mentioned[0], ctx);
    if (pick && !isSlotQuestion(pick)) {
      return {
        kind: "answer",
        text: whenWordings(pick.notice)[0],
        link: noticeLink(pick.notice),
        asked: {
          id: "notices.when",
          slots: { notice: mentioned[0] },
          subject: pick.notice.title,
          mentions: [pick.notice.title],
        },
      };
    }
  }
  return withFacts(noTimeLine(entry.id), base);
}

/** "it", standing in for the subject an earlier answer named: "Who has seen it?", "Add Jane to it". */
const PRONOUN = /\bit\b/i;

/**
 * Says enough besides "it" to be worth substituting the subject into and matching afresh: "Add
 * Jane to it" does (add, jane), "forget it" or "leave it" don't (just forget, leave once the stop
 * word "it" is dropped) and would otherwise risk being reinterpreted as a question about the
 * subject rather than left as a cancel-shaped phrase.
 */
const namesSomethingBesidesIt = (text: string) => significantWords(text).size > 1;

/** Replies that mean "I don't know what you're asking": worth re-trying with the original message rather than kept. */
const UNRESOLVED_KINDS = new Set<AssistantReply["kind"]>(["unanswered", "didYouMean", "offTopic", "searchOnly"]);

/** "that one", "the second one", "2": which offered question they mean. Null when this isn't a pick. */
export function pickOffered(text: string, offered: string[]): string | null {
  if (offered.length === 0) return null;
  const said = text
    .trim()
    .toLowerCase()
    .replace(/[?.!]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const exact = offered.find(
    (question) => question.toLowerCase().replace(/[?.!]+$/g, "").replace(/\s+/g, " ").trim() === said
  );
  if (exact) return exact;
  if (/^(?:that|this) one$/.test(said)) return offered[0];
  const ordinal = said.match(/^(?:the\s+)?(first|1st|second|2nd|third|3rd|last|[1-3])(?:\s+one)?$/);
  if (!ordinal) return null;
  if (ordinal[1] === "last") return offered[offered.length - 1];
  const index: Record<string, number> = { first: 0, "1st": 0, "1": 0, second: 1, "2nd": 1, "2": 1, third: 2, "3rd": 2, "3": 2 };
  const at = index[ordinal[1]];
  return at !== undefined && at < offered.length ? offered[at] : null;
}

/** The last topic, plus the questions just offered, so the next message can point at either. */
function withOffered(previous: AskedQuestion | null, questions: string[]): AskedQuestion {
  return {
    id: previous?.id ?? "suggestions",
    slots: previous?.slots ?? {},
    ...(previous?.awaiting ? { awaiting: previous.awaiting } : {}),
    ...(previous?.startedByText ? { startedByText: previous.startedByText } : {}),
    ...(previous?.subject ? { subject: previous.subject } : {}),
    ...(previous?.mentions?.length ? { mentions: previous.mentions } : {}),
    offered: questions,
  };
}

/**
 * Works out the reply to one message. Order matters: safeguarding is checked
 * before anything else, then small talk and "what can you do?", then requests to write or reword text, and only `available` entries can ever be matched.
 * `rotation` counts the answers and off-topic replies given so far, so each
 * goes through its wordings in turn. `matcher` is null until it is ready.
 * `previous` is the last question answered, including the titles it named.
 * A follow-up that only changes the date ("what about last month?") asks it
 * again, and "When is Dinner at Artistry?" can be about one of those titles.
 * After a task, the next message can answer its follow-up question ("Grade 2B")
 * or start it again with one detail changed ("Same for Grade 3"). `task` is a
 * guided task under way: once safeguarding is checked, it hears the message first.
 */

/** "Yes" to "Want to see this week?" on an all-time notice count. */
const AGREES_TO_THIS_WEEK = /^(?:yes|yep|yeah|yup|sure|ok(?:ay)?|please)(?:\s+please)?$/i;

/** "What files do we have in Drive?" and "what's in Drive" list the files. "Where are our files?" does not. */
const ASKS_FOR_THE_FILE_LIST =
  /\bwhat files do we have\b|\btell me what files we have\b|\bwhat other files\b|\bwhat(?:'s|s| is)?\s+in\s+(?:my\s+|our\s+|the\s+)?drive\b/i;

/** "What's in it?" after a file was just named. The chat cannot read the pages. */
const ASKS_WHAT_IS_INSIDE = /^(?:what(?:'s| is|s)?|whats)\s+(?:in|inside)\s+(?:it|that|this|the file|the pdf)\b/i;

/**
 * One of this person's Circles named in the message, as written there, longest name first so
 * "Grade 4A" wins over "Grade 4". Undefined when none is named or the Circles can't load.
 */
async function circleNamedIn(text: string, ctx: AnswerContext): Promise<string | undefined> {
  const circles = await loadCircles(ctx).catch(() => []);
  return longestNameIn(text, circles.map((circle) => circle.name));
}

/** The title of a sent or scheduled Notice named in the message, as written there. */
async function noticeNamedIn(text: string, ctx: AnswerContext): Promise<string | undefined> {
  const feed = await loadFeed(ctx).catch(() => null);
  if (!feed) return undefined;
  // "Welcome to Fugazi Capital." is named without its full stop
  const titles = [...feed.sent, ...feed.scheduled].map((notice) => notice.title?.replace(/[.!?\s]+$/, "") ?? "");
  return longestNameIn(text, titles.filter((title) => title.length >= 4));
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The longest of these names found whole in the text, as written there. */
function longestNameIn(text: string, names: string[]): string | undefined {
  for (const name of names.filter((n) => n && n.length >= 3).sort((a, b) => b.length - a.length)) {
    const found = text.match(new RegExp(`\\b${escapeRegExp(name)}\\b`, "i"));
    if (found) return found[0];
  }
  return undefined;
}

/** What a safeguarding reply leaves for the next message: not a catalogue question */
const SAFEGUARDING_ASKED: AskedQuestion = { id: "safeguarding", slots: {} };

/** "How do I create one?" stays on the last topic. On its own it is still the Circle example. */
const ASKS_TO_CREATE_ONE = /^(?:how do i |can i )?(?:create|make|add) one\b/i;

/** The same words said another way: "I've" for "we've", "my" for "our", "just" for "only". */
const sameWordsAs = (text: string) =>
  text
    .replace(/\b(?:i|we)'ve\b/gi, "we have")
    .replace(/\b(?:i|we)\b/gi, "we")
    .replace(/\b(?:my|our)\b/gi, "our")
    .replace(/\bjust\b/gi, "only");

/**
 * Whether a suggestion asks what was asked, in nearly the same words: at least seven in ten of each one's
 * words are in the other. Then it's answered, rather than offered back as "Did you mean".
 */
export function nearlyTheSame(said: string, example: string): boolean {
  const a = significantWords(sameWordsAs(said));
  const b = significantWords(sameWordsAs(example));
  if (a.size < 3 || b.size < 3) return false;
  const shared = Array.from(a).filter((word) => b.has(word)).length;
  return shared / a.size >= 0.7 && shared / b.size >= 0.7;
}

/** "can you make one…", "start one for me": one of what was just explained. */
const MAKE_ONE = /\b(?:make|create|start|set\s+up|do|add|put\s+in)\s+one\b/i;

/** Whether this is, word for word, one of the catalogue's examples. */
const looksLikeACatalogueExample = (text: string, available: CatalogueEntry[]) =>
  available.some((entry) => entry.examples.some((example) => phrasingKey(example) === phrasingKey(text)));

/** "and for grade 4?", "anything for TEST Grade 4 2027?": the Circle a short follow-up names, after a list of Events. */
const FOLLOW_UP_FOR = /^(?:and\s+|what\s+about\s+|anything\s+|any\s+events?\s+)?(?:for\s+)?/i;

/**
 * The Circle a message is only the name of ("TEST Grade 4 2027", "grade 4"), or, just after the Events coming up,
 * the one a short follow-up asks about ("anything for grade 4?").
 */
async function circleNamedAlone(text: string, previous: AskedQuestion | null, ctx: AnswerContext) {
  const afterEvents = Boolean(previous && (previous.id === "home.upcomingEvents" || previous.id.startsWith("calendar.")));
  const said = text.trim().replace(/[?.!]+$/, "");
  const bare = afterEvents ? said.replace(FOLLOW_UP_FOR, "") : said;
  // Any other follow-up ("and blackbrick umhlanga" after who's in a Circle) is read as a follow-up, further on
  if (previous && !afterEvents) return null;
  if (!bare || bare.split(/\s+/).length > 5 || (!afterEvents && bare !== said)) return null;
  const circles = await loadCircles(ctx).catch(() => []);
  const recipients = circles.map((circle) => ({ kind: "circle" as const, id: circle.circleId, name: circle.name }));
  const found = findRecipients(recipients, bare);
  // A bare name must be the whole of it, or nearly: "grade 4" for "TEST Grade 4 2027", never "Grade 4 Market Day"
  return found.length === 1 ? { name: found[0].name, afterEvents } : null;
}

/** The page a Fundamentals answer is about: the one it names ("from Notices"), or its topic's page, else Help Center (the lesson). */
function lessonPage(hit: { id: string; text: string }): SidebarItemKey {
  if (/\b(?:from|on|open)\s+Notices\b/.test(hit.text)) return "notices";
  const byTopic: Array<[RegExp, SidebarItemKey]> = [
    // Only pages every Team member has: Users, Team, Circles and Library depend on the role or the Organization
    [/^calendar\./, "calendar"],
    [/^drive\./, "drive"],
    [/^create\./, "create"],
  ];
  return byTopic.find(([pattern]) => pattern.test(hit.id))?.[1] ?? "helpCenter";
}

/** A Fundamentals follow-up, as an answer the chat can show. A slot is a title or a list, not a new question. */
function lessonAsReply(text: string, previousId?: string | null, spin = 0): AssistantReply | null {
  const hit = answerFromLesson(text, previousId);
  if (!hit) return null;
  if (hit.slot) {
    return {
      kind: "askSlot",
      text: hit.text,
      choices: [],
      reason: "missing",
      asked: { id: hit.id, slots: {} },
    };
  }
  const page = getSidebarItem(lessonPage(hit));
  const spoken = hit.id.startsWith("lesson.") ? speakLesson(text, hit.text, spin) : hit.text;
  return {
    kind: "answer",
    text: spoken,
    link: { label: `Open ${page.label}`, href: page.path },
    asked: { id: hit.id, slots: {} },
  };
}

/** How the words given for a task's question are named back: only short details, never the message or who it's for. */
const ECHO_LABELS: Record<string, string> = {
  title: "Title",
  question: "Question",
  place: "Place",
  choices: "Options",
};

/**
 * "staff meeting" while a task is collecting: keep the words they just gave in the reply,
 * named by the question they answered (`answered`, the detail just asked for).
 */
function echoSlot(reply: AssistantReply, text: string, answered: string | null | undefined): AssistantReply {
  if (reply.kind !== "task" || !reply.text) return reply;
  const label = answered ? ECHO_LABELS[answered] : undefined;
  if (!label) return reply;
  const said = text.trim().replace(/[?.!]+$/, "");
  if (said.length < 3 || said.length > 80) return reply;
  if (cancelKindOf(said)) return reply;
  if (/^(use as |it'?s a question|its a question)\b/i.test(said)) return reply;
  if (/^(how|what|where|when|who|why|can|do|does|is|are|should|could)\b/i.test(said)) return reply;
  if (reply.text.toLowerCase().includes(said.toLowerCase())) return reply;
  // "choices yes no maybe" was read as the choices, and the reply already says so
  if (/^Choices:/.test(reply.text)) return reply;
  // The same step asked again means the answer wasn't taken as it: "Each choice needs to be
  // different…", or "17:30" at "Where is it?", which went to the start time instead
  if (reply.task?.asked === answered) return reply;
  // What was kept, not what was typed: "Ooh lovely! Koeksisters, cupcakes" keeps the choices only
  const kept = answered ? reply.task?.details[answered] : undefined;
  const shown = Array.isArray(kept) ? kept.join(", ") : typeof kept === "string" ? kept : said;
  // "Question: Which day suits you?" keeps its question mark; anything else ends with a full stop
  const echoed = /\?$/.test(shown.trim()) ? shown.trim() : `${shown.replace(/[?.!]+$/, "")}.`;
  return { ...reply, text: `${label}: ${echoed} ${reply.text}` };
}

const aboutCertificates = (id: string) => id === "concepts.certificates" || id.startsWith("certificates.");

/** "who's in Fugazi General", including the spelling people actually type. */
const WHO_IN_CIRCLE = /^(?:who(?:'s|s| is)|whos)\s+in\b/i;
const NAMED_CIRCLE_MEMBERS = /^(?!(?:(?:the|our|my)\s+)?team\b|(?:add|remove|invite|how|who|list|show)\b)(.{2,40}?)\s+members[?.!]*$/i;
const asksCircleMembers = (text: string) => WHO_IN_CIRCLE.test(text) || NAMED_CIRCLE_MEMBERS.test(text);


/** "Locate an existing poll", "find the poll I sent", "where's my last event?": looking for one already made. */
const LOOKS_FOR_ONE_MADE = /^(?:please\s+)?(?:find|locate|look\s+up|search\s+for|show(?:\s+me)?|where(?:'s|\s+is|\s+are)?|open)\b/i;

/** "Find notices for Grade 4", "which posts went to gr 4": a lookup by Circle, never a new Notice. */
const NOTICES_FOR_A_CIRCLE =
  /^(?:please\s+)?(?:(?:find|show(?:\s+me)?|list|look\s+up|let\s+me\s+see|which|what|any)\s+)?(?:the\s+|all\s+(?:the\s+)?)?(?:notices|posts|announcements)\s+(?:(?:we\s+)?(?:sent|went|have\s+gone|go)\s+)?(?:for|to)\s+(.{2,40})$/i;

/** A follow-up that is still about a Circle, not a new when/where/how question. */
const STAYS_ON_THE_CIRCLE = /^(?:when|where|what|whats|how|why|which|can|could|take me|go to|navigate|show me|open)\b/i;

/** Who is in the Circle this message names, or a short note and a link when the member list cannot be loaded. */
async function membersOfCircle(
  text: string,
  entry: AnswerEntry,
  ctx: AnswerContext,
  rotation: number
): Promise<AssistantReply | null> {
  let circles: Awaited<ReturnType<typeof loadCircles>>;
  try {
    circles = await loadCircles(ctx);
  } catch {
    return null;
  }
  const resolved = resolveCircle(circles, text);
  if (!("circle" in resolved)) return null;
  const reply = await answer(entry, { circle: resolved.circle.name }, ctx, rotation, false, text);
  if (reply.kind !== "loadFailed") return reply;
  const page = getSidebarItem("circles");
  return {
    kind: "answer",
    text: `${resolved.circle.name} is a Circle. Open Circles to see who's in it.`,
    link: { label: `Open ${page.label}`, href: page.path },
    asked: { id: entry.id, slots: { circle: resolved.circle.name }, subject: resolved.circle.name },
  };
}

/**
 * A question about a Notice the last answer named, or "when is …?" looked up
 * among sent Notices. Nothing when it doesn't name one, so the ordinary reply stands.
 */
async function noticeFromConversation(
  text: string,
  previous: AskedQuestion | null,
  entry: CatalogueEntry | undefined,
  ctx: AnswerContext,
  rotation: number
): Promise<AssistantReply | null> {
  if (!entry?.answer || ASKS_FOR_THE_FILE_LIST.test(text) || smallTalkReply(text)) return null;
  const query = noticeFollowUpQuery(text, previous?.mentions);
  if (!query) return null;
  const reply = await answer(entry, { notice: query }, ctx, rotation, false, text);
  if (reply.kind === "loadFailed") return null;
  if (reply.kind === "askSlot" && reply.reason === "notFound") return null;
  if (reply.kind === "answer" && !reply.asked.subject) return null;
  return reply;
}
const TAKE_THERE = /^(?:take me(?: to)?|open) (?:it|that|there)$/i;

/** "it", "that one", "this notice": the Notice the last answer was about. */
const POINTS_BACK = /\b(?:that\s+one|this\s+one|the\s+one|that\s+(?:notice|event|poll)|this\s+(?:notice|event|poll)|it|that)\b(?!\s*'?s\b)/i;

/** "open it", "ok open that one", "take me to it", "show me that one please". */
const OPEN_IT =
  /^(?:(?:ok(?:ay)?|right|great|cool|yes|yeah|please|and|so)[,\s]+)*(?:(?:can|could|would)\s+(?:you|u)\s+(?:please\s+)?)?(?:(?:open|show(?:\s+me)?|take\s+me\s+to|let\s+me\s+see|pull\s+up|go\s+to)\s+(?:it|that(?:\s+one)?|this(?:\s+one)?|the\s+(?:notice|event|poll)|there)|pull\s+(?:it|that\s+one|this\s+one)\s+up)(?:\s+(?:please|pls|plz|for\s+me|then|asap))?[.!?]*$/i;

async function lookupNoticeByName(name: string, ctx: AnswerContext) {
  try {
    const found = await findNoticeForWhen(name, ctx);
    if (!found || isSlotQuestion(found)) return null;
    return found.notice;
  } catch {
    return null;
  }
}

function aboutNotice(notice: NoticeWithType, text: string, openNow = false): AssistantReply & { kind: "answer" } {
  return {
    kind: "answer",
    text,
    link: noticeLink(notice),
    asked: { id: "notices.when", slots: { notice: notice.title }, subject: notice.title, mentions: [notice.title] },
    ...(openNow ? { openNow: true } : {}),
  };
}

/** Short follow-ups that name the last Notice, file, or page instead of a new catalogue question. */
async function spokenTurn(
  text: string,
  previous: AskedQuestion | null,
  byId: Map<string, CatalogueEntry>,
  ctx: AnswerContext,
  rotation: number
): Promise<AssistantReply | null> {
  const said = text.trim();
  const subject = previous?.subject || "";

  if (/\botp\b/i.test(said) && /\bdrive\b/i.test(said)) {
    const finder = byId.get("drive.find");
    if (finder?.answer) return answer(finder, { file: "otp" }, ctx, rotation, false, said);
  }

  if (previous?.id === "drive.find" && subject && /^(?:open it|whats in it|what(?:'s| is) in it)$/i.test(said)) {
    const finder = byId.get("drive.find");
    if (finder?.answer) {
      const found = await answer(finder, { file: subject }, ctx, rotation, false, said);
      if (found.kind !== "answer") return found;
      const inside = /^what/.test(said);
      return {
        ...found,
        text: inside
          ? `I can't read what's inside ${subject}. Open it to see the pages.`
          : `Here's ${subject}. I can't read the pages from here.`,
      };
    }
  }

  if (/^show me (?:them|those)$/i.test(said) && previous && /^drive\./.test(previous.id)) {
    const files = byId.get("drive.recent");
    if (files?.answer) return answer(files, {}, ctx, rotation, false, said);
  }

  if (/^what(?:'s| is|s) in it$/.test(said) && subject) {
    const page = getSidebarItem("drive");
    return {
      kind: "answer",
      text: `I can't read what's inside ${subject}. Open it in Drive to see it.`,
      link: { label: `Open ${page.label}`, href: page.path },
      asked: { id: previous?.id || "drive.find", slots: previous?.slots || {}, subject },
    };
  }

  if (/^not the course the chat$/.test(said)) {
    return {
      kind: "answer",
      text: "Ask Noticeboard is this chat. Press the sparkle next to your picture to open it. The course is separate, in Help Center.",
      link: { label: "Go to courses", href: "/help/courses" },
      asked: { id: "concepts.whereIsAsk", slots: {} },
    };
  }

  if (/^where do i see them$/.test(said) && previous && /certificate/i.test(previous.id)) {
    const certificates = byId.get("concepts.certificates");
    if (certificates?.answer) return answer(certificates, {}, ctx, rotation, false, said);
  }

  if (/^continue the last one$/.test(said) && previous?.id === "home.drafts") {
    const drafts = byId.get("home.drafts");
    if (drafts?.answer) {
      const reply = await answer(drafts, {}, ctx, rotation, false, said);
      if (reply.kind === "answer" && /\b0\b/.test(reply.text)) {
        return { ...reply, text: "You don't have a draft to continue. A draft is a Notice you haven't published." };
      }
      return reply;
    }
  }

  if (/^not yet just tell me how$/.test(said)) {
    const which = subject ? ` ${subject} is the one to open.` : "";
    return {
      kind: "answer",
      text: `Open the sent Notice, then press Send reminder. Nothing goes out until you do that.${which}`,
      link: { label: "Open Notices", href: "/view-notices" },
      asked: previous ? { ...previous } : { id: "notices.sendReminder", slots: {} },
    };
  }

  if (/^which blackbrick one$/.test(said)) {
    try {
      const { sent } = await loadFeed(ctx);
      const named = sent.filter((notice) => /blackbrick/i.test(notice.title));
      if (named.length > 0) {
        return {
          kind: "answer",
          text: `There are a few: ${named.map((notice) => notice.title).join(", ")}. Which one?`,
          link: { label: "Open Notices", href: "/view-notices" },
          asked: { id: "notices.when", slots: {}, mentions: named.map((notice) => notice.title) },
        };
      }
    } catch {
      return null;
    }
  }

  const notice = subject ? await lookupNoticeByName(subject, ctx) : null;

  if (/^where is that$/.test(said) && notice) {
    const place = notice.eventLocation?.trim();
    return aboutNotice(notice, place ? `${notice.title} is at ${place}.` : `No location is set for ${notice.title}.`);
  }

  if ((TAKE_THERE.test(said) || /^no the notice not the calendar$/.test(said)) && notice) {
    const line = /^no the notice/.test(said)
      ? `${notice.title} is the notice, not Calendar.`
      : `Here's ${notice.title.replace(/[.!\s]+$/, "")}.`;
    return aboutNotice(notice, line, true);
  }

  if (/^what do i do$/.test(said) && notice) {
    return aboutNotice(
      notice,
      `Open ${notice.title} and check it was sent, not saved as a draft, and that the parent is in the Circle it went to.`
    );
  }

  if (/^is that the same as the dinner$/.test(said) && notice) {
    const dinner = await lookupNoticeByName("dinner at artistry", ctx);
    if (dinner) {
      const same = dinner.noticeId === notice.noticeId;
      const text = same
        ? `${notice.title} is the dinner.`
        : `No. ${whenWordings(notice)[0]} ${whenWordings(dinner)[0]}`;
      return aboutNotice(notice, text);
    }
  }

  if (/^no the check in$/.test(said)) {
    const checkIn = await lookupNoticeByName("sandton check in", ctx);
    if (checkIn) return aboutNotice(checkIn, whenWordings(checkIn)[0]);
  }

  return null;
}

export async function replyTo(
  text: string,
  {
    available,
    matcher,
    ctx,
    rotation,
    previous = null,
    task: running = null,
    usingFallback = false,
    parked = [],
  }: {
    available: CatalogueEntry[];
    matcher: QuestionMatcher | null;
    ctx: AnswerContext;
    rotation: { answers: number; offTopic: number };
    previous?: AskedQuestion | null;
    task?: TaskRecord | null;
    /** The matching model wasn't ready, so `matcher` is the shared-words fallback: an answer it finds is marked as such */
    usingFallback?: boolean;
    /** Drafts set aside earlier, newest first, that "carry on with my notice" can pick up again */
    parked?: TaskRecord[];
  }
): Promise<AssistantReply> {
  text = text.replace(/^the principal is asking:\s*/i, "").replace(/^are you sure about this:\s*/i, "");
  if (isSafeguardingConcern(text)) {
    return { kind: "safeguarding", text: SAFEGUARDING_REPLY, asked: SAFEGUARDING_ASKED };
  }
  // "should I post a notice about it to everyone?" right after one: kept private, never a Notice
  if (previous?.id === SAFEGUARDING_ASKED.id && asksToShareSafeguardingConcern(text)) {
    return { kind: "safeguarding", text: SAFEGUARDING_SHARING_REPLY, asked: SAFEGUARDING_ASKED };
  }
  if (
    (/\b(?:gmail|outlook|mailbox|inbox)\b/i.test(text) && /\b(?:check|read|find|search|open)\b/i.test(text)) ||
    /\bfind the email .+ sent me\b/i.test(text)
  ) {
    return { kind: "cannot", text: "I can't access your email or mailbox. Open your email app to find that message. I can help find Notices in Noticeboard." };
  }
  if ((/\b(?:export|download)\b/i.test(text) && /\b(?:users|parent list)\b/i.test(text) && /\b(?:excel|spreadsheet)\b/i.test(text)) || /\b(?:print)\s+all\s+(?:the\s+)?notices\b/i.test(text)) {
    return {kind:"cannot",text:"I can't export a spreadsheet or print Notices from this chat. Open the relevant page to review the information available to your role."};
  }
  if (/^(?:just tell me|pretend|say)\b.*\b(?:sent|published)\b/i.test(text.trim())) return {kind:"answer",text:"I haven't sent anything. You must review it and press Publish yourself.",link:{label:"Open Create",href:"/create-notice"},asked:{id:"unsupported.pretendSent",slots:{}}};
  // Privacy requests must never become a title, question or choices in an open draft.
  const privateReply = privateDataReply(text);
  if (privateReply) return { kind: "cannot", text: privateReply };
  // "and claim vs poll", "wait, our package": the bridge is not part of the question.
  // "whn" and "chagne it" are fixed before follow-ups, so they still resolve.
  const spoken = text;
  const normalizedSpoken=tidyMessage(spoken);
  const questionAtChoices=(looksLikeQuestion(normalizedSpoken) || /^(?:when|which)\b/i.test(normalizedSpoken)) && !/[,;\n]|\sor\s/i.test(normalizedSpoken);
  text = running?.phase === "collecting" && running.asked === "choices" && !questionAtChoices ? spoken : normalizedSpoken;
  // "For Sookchand Family, how do I edit a sent notice?": their own Organization's name only says where
  const ownName = ctx.user?.schoolName?.trim();
  if (ownName) {
    const named = text.replace(new RegExp(`^(?:for|at|in)\\s+(?:the\\s+)?${escapeRegExp(ownName)}\\s*[,:]\\s*`, "i"), "");
    if (named !== text && named.split(/\s+/).length >= 2) text = tidyMessage(named);
  }
  // "Hi" is also a Fundamentals phrase. A pure greeting, thanks, or reaction is still small talk.
  const social = smallTalkReply(text);
  // "Thank you", "Class photos" when the title is asked for: the title, never thanks or a picture question
  const waitingForWords =
    running?.phase === "collecting" && (running.asked === "title" || running.asked === "question") && text.trim().split(/\s+/).length <= 8;
  const thankYouTitle = waitingForWords && /^thank\s+you\b/i.test(text.trim());
  // On a summary, "perfect", "great thanks" or "lgtm" is a yes: it goes to the draft, never thrown away as small talk
  if(running && cancelKindOf(text.trim()) === "cancel") return {kind:"cancelled",text:CANCELLED_REPLY,choices:[],dropTask:true};
  const approvesSummary = (running?.phase === "confirming" || running?.phase === "done") && (isApproval(spoken) || isApproval(text));
  if(!running && previous?.handedOverDraft && (isApproval(spoken) || isApproval(text))) {
    const draft={...previous.handedOverDraft,phase:"confirming" as const};
    const reviewed=await taskTurn((org)=>continueTask(draft,"looks good",org),{ctx,available,matcher,rotation});
    if(reviewed)return reviewed;
  }
  if (approvesSummary && running) {
    const approved = await taskTurn((org) => continueTask(running.phase==="done"?{...running,phase:"confirming"}:running, spoken, org), { ctx, available, matcher, rotation });
    if (approved) return approved;
  }
  if (!thankYouTitle && !approvesSummary && (social === GREETING_REPLY || social === THANKS_REPLY || social === REACTION_REPLY)) {
    // A draft is never dropped without saying so: "thanks" or "hi" keeps it, and says how to carry on
    if (running && running.phase !== "done") {
      const step = await resumeTaskReply(running, { ctx, available, matcher, rotation });
      if (step?.kind === "task") return { ...step, text: `${social === THANKS_REPLY ? "You're welcome." : social === GREETING_REPLY ? "Hi!" : "Glad you like it."} ${step.text}` };
    }
    return { kind: "smallTalk", text: social };
  }
  // "bye", "what's your name?", "how are you?", "I'm nervous, it's my first week": before anything is matched
  if (!running && (social === GOODBYE_REPLY || social === HOW_ARE_YOU_REPLY || social === ABOUT_ASK_REPLY || social === NERVOUS_REPLY)) {
    return { kind: "smallTalk", text: social };
  }
  // A catalogue example said in full still matches, even when a polite "please" would otherwise be trimmed off.
  if (
    phrasingKey(spoken) !== phrasingKey(text) &&
    available.some((entry) => entry.examples.some((example) => phrasingKey(example) === phrasingKey(spoken)))
  ) {
    text = spoken;
  }

  const phrasing = phrasingKey(text);
  const exactHits = available.filter((entry) => entry.examples.some((example) => phrasingKey(example) === phrasing));
  // Exact examples are a string comparison. They do not need another embedding.
  const coreOf = spokenCore(text);
  const coreHits = coreOf
    ? available.filter((entry) => entry.examples.some((example) => phrasingKey(example) === phrasingKey(coreOf)))
    : [];
  const leavesForAnExactExample = (): boolean => {
    const busyId = running?.startedById ?? previous?.id;
    if (exactHits.length === 1 && exactHits[0].id !== busyId) return true;
    return coreHits.length === 1 && coreHits[0].id !== busyId;
  };

  const byId = new Map(available.map((e) => [e.id, e]));

  // "School's closed on the 4th": an update, not a question. Offered to Nova when they can open it.
  // Something Ask Noticeboard can already make ("choir practice Oct 30 6pm at the hall") is made here instead.
  if (
    !running &&
    !previous?.awaiting &&
    exactHits.length === 0 &&
    canOpenSidebarItem(getSidebarItem("nova"), ctx.user) &&
    isNovaUpdate(spoken) &&
    !readWorkRequest(spoken)
  ) {
    return { kind: "novaOffer", text: NOVA_OFFER_REPLY, to: toNova(spoken) };
  }

  if (!running && /^(?:(?:can|could) you\s+|please\s+)?delete\s+(?!(?:the|a|my)\s+notice[?.!]*$).+\s+notice[?.!]*$/i.test(text.trim())) {
    const entry = byId.get("unsupported.deleteNotice");
    if (entry?.answer) return answer(entry, {}, ctx, rotation.answers, usingFallback, text);
  }
  if (!running && exactHits.length === 0 && !/\b(?:register|email|principal)\b/i.test(text) && (/^(?:(?:can|could) you\s+|please\s+|we need to\s+|i need to\s+)?(?:delete|remove|cancel|call off)\s+(?!(?:the notice|a notice)[?.!]*$)/i.test(text) || /\b(?:is off|isn'?t happening anymore)\b/i.test(text)) && !cancelKindOf(text) && /\b(?:notice|event|lesson|ball|nativity|halloween)\b/i.test(text)) {
    const cancelling = byId.get("create.cancelEventHowTo");
    if (cancelling && hasHowTo(cancelling)) return answerHowTo(cancelling, ctx, text);
  }
  const lookupQuestion = !running ? withoutTextSpeak(text) : "";
  const namedWhen=!running ? text.trim().match(/^(.{3,70}?)\s*[-—–]\s*when is (?:it|that)(?: again)?[?.!]*$/i)?.[1] : undefined;
  if(namedWhen){const found=await lookupNoticeByName(`when is ${namedWhen}`,ctx);if(found)return aboutNotice(found,whenWordings(found)[rotation.answers % 3]);}
  const trailingWhen=!running ? text.trim().match(/^(.{3,70}?)\s+when[?.!]*$/i)?.[1] : undefined;
  if(trailingWhen) {const found=await lookupNoticeByName(`when is ${trailingWhen}`,ctx);if(found)return aboutNotice(found,whenWordings(found)[rotation.answers % 3]);}
  if (lookupQuestion && /^(?:(?:can|could) you\s+)?(?:find|look up|pull up|show me)\s+/i.test(lookupQuestion) && !/\b(?:parent|user|file|pdf|document|folder|worksheet|drive)\b/i.test(lookupQuestion)) {
    const notice = await lookupNoticeByName(lookupQuestion.replace(/^(?:(?:can|could) you\s+)?(?:find|look up|pull up|show me)\s+/i, "when is ").replace(/\s+for me[?.!]*$/i, ""), ctx);
    if (notice) return aboutNotice(notice, whenWordings(notice)[rotation.answers % 3]);
  }
  if (!running && /^(?:(?:what'?s|what is|wats) (?:the )?latest notice|what was the last notice we sent)[?.!]*$/i.test(text.trim())) {
    const {sent}=await loadFeed(ctx);
    if (sent.length) {
      const reply=aboutNotice(sent[0], `Your latest sent Notice is “${sent[0].title}”.`);
      return {...reply,asked:{id:"notices.open",slots:{notice:sent[0].title},subject:sent[0].title,mentions:[sent[0].title]}};
    }
  }
  const fileLookup = !running ? withoutTextSpeak(text).match(/^(?:find|look for|look up|search for)\s+(?:the\s+)?(.+\b(?:pdf|document|file|worksheet))$/i)?.[1] : undefined;
  const driveFinder=byId.get("drive.find");
  if (fileLookup && driveFinder?.answer) return answer(driveFinder,{file:fileLookup},ctx,rotation.answers,usingFallback,text);
  if (!running && previous && /^(?:circles\.|users\.|team\.|home\.userCount)/.test(previous?.id ?? "")) {
    const circle=(text.match(/^(?:(?:and\s+)?what about|and)\s+(.+?)[?.!]*$/i) ?? spoken.match(/^(?:(?:and\s+)?what about|and)\s+(.+?)[?.!]*$/i))?.[1];
    const entry=byId.get("circles.members");
    if(circle && !entry?.answer && ctx.user?.accessLevel === "Creator") return {kind:"answer",text:"A Creator cannot open Circles. An Owner or Admin can see the members.",link:{label:"Open Home",href:"/"},asked:{id:"circles.members",slots:{}}};
    if(circle && entry?.answer) {
      const reply=await membersOfCircle(circle,entry,ctx,rotation.answers);
      if(reply && "asked" in reply && reply.asked)return {...reply,asked:{...reply.asked,slots:{...previous.slots,...reply.asked.slots}}};
    }
  }
  const bareDate=!running && (!previous || /^(?:calendar\.|home\.upcomingEvents|notices\.when)/.test(previous.id)) && /^(?:(?:and|what about)\s+)?(?:the\s+)?(?:(?:this|next|last)\s+)?(?:wednesday|monday|tuesday|thursday|friday|saturday|sunday|week|weekend|month|today|tomorrow|yesterday)[?.!]*$/i.test(text.trim()) ? findDateRange(text) ?? findDateRange(`on ${text.replace(/^(?:and|what about)\s+/i, "")}`) : null;
  if(bareDate && byId.get("calendar.whatsOn")?.answer) return answer(byId.get("calendar.whatsOn") as AnswerEntry,{dateRange:bareDate},ctx,rotation.answers,usingFallback,text);
  const precise = !running || running.phase === "done" ? explicitQuestion(text) : null;
  if (precise) {
    if (precise.id === "home.userCount" && byId.get("users.total")?.answer) return answer(byId.get("users.total") as AnswerEntry, {}, ctx, rotation.answers, usingFallback, text);
    if (precise.id === "circles.members") {
      const members=byId.get("circles.members");
      if(members?.answer) {
        const reply=await membersOfCircle(precise.slots.circle ?? text,members,ctx,rotation.answers);
        if(reply)return reply;
      } else return {kind:"answer",text:"A Creator can't open Circles, so the member list isn't here. An Owner or Admin opens the Circle to see who is in it.",link:{label:"Open Home",href:"/"},asked:{id:"circles.members",slots:{}}};
    }
    if (precise.id === "users.find" && !/\b(?:parent|user)\b/i.test(text)) {
      const notice = await lookupNoticeByName(`when is ${String(precise.slots.user).replace(/\s+for me$/i, "")}`, ctx);
      if (notice) return aboutNotice(notice, whenWordings(notice)[rotation.answers % 3]);
    }
    const entry = byId.get(precise.id);
    if (entry?.answer) return answer(entry, precise.slots, ctx, rotation.answers, usingFallback, text);
    if (entry && hasHowTo(entry)) return answerHowTo(entry, ctx, text, usingFallback);
    const gated = CATALOGUE.find((item) => item.id === precise.id);
    const page = gated && [gated.sidebarItem, ...(gated.alsoNeeds ?? [])].map((key) => getSidebarItem(key)).find((item) => !canOpenSidebarItem(item, ctx.user));
    if (page) return { kind: "answer", text: `You can't open ${page.label}; it isn't in your sidebar. An Owner or Admin can open ${page.label}.`, link: { label: "Open Home", href: "/" }, asked: { id: precise.id, slots: {} } };
  }

  if(!running && previous?.id === "notices.sendReminder" && previous.subject && followUpAspect(text)==="when") {
    const notice=await lookupNoticeByName(previous.subject,ctx);
    if(notice)return {...aboutNotice(notice,`${whenWordings(notice)[rotation.answers % 3]} ${noTimeLine("notices.sendReminder")}`),asked:previous};
  }
  if (!running && previous?.id === "notices.when" && /^(?:and\s+)?(?:the\s+)?(?:next|other)\s+one[?.!]*$/i.test(text.trim())) {
    const base = (title: string) => title.toLowerCase().replace(/\([^)]*\)/g, "").replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();
    const seeds = previous.awaitingChoices?.length ? previous.awaitingChoices : previous.subject ? [previous.subject] : [];
    const feed = await loadFeed(ctx);
    const candidates = [...feed.sent, ...feed.scheduled].filter((notice) => seeds.some((seed) => base(notice.title) === base(seed)) && notice.title !== previous!.subject);
    const dated = candidates.map((notice) => ({ notice, at: getNoticeEventBounds(notice)?.start })).filter((item): item is { notice: NoticeWithType; at: Date } => Boolean(item.at));
    const upcoming = dated.filter(({at}) => at.getTime() >= new Date().setHours(0,0,0,0)).sort((a,b)=>a.at.getTime()-b.at.getTime());
    const next = upcoming[0] ?? dated.sort((a,b)=>b.at.getTime()-a.at.getTime())[0];
    if (next) return aboutNotice(next.notice, whenWordings(next.notice)[rotation.answers % 3]);
  }

  // One of the names a follow-up just offered, as is, is that answer: "School photographs" is the
  // Template, not a question about photos, and "Cancelled / weather" is not a cancel.
  // "What kind of notice?" starts the kind chosen, further down.
  const offersAKind = previous?.id === "create.offerToSend" || previous?.id === "create.scheduleOffer";
  const choiceEntry = previous?.awaiting && !offersAKind ? byId.get(previous.id) : undefined;
  const offeredChoice =
    previous?.awaitingChoices?.find((choice) => phrasingKey(choice) === phrasingKey(spoken)) ??
    choiceCalledTheOne(spoken, previous?.awaitingChoices ?? []);
  if (!running && previous?.awaiting && choiceEntry && offeredChoice) {
    // "This month", picked for which dates: the range it names, not the words
    const value = previous.awaiting === "dateRange" ? findDateRange(offeredChoice) ?? offeredChoice : offeredChoice;
    const slots = { ...previous.slots, ...slotValue(previous.awaiting, value) };
    if (hasHandoff(choiceEntry)) return startHandoff(choiceEntry, slots, ctx, previous.startedByText ?? text);
    if (choiceEntry.answer) return answer(choiceEntry, slots, ctx, rotation.answers, false, text);
  }

  if (!running && previous?.offered?.length) {
    const picked = pickOffered(text, previous.offered);
    if (picked && picked !== text) {
      // Still marked as offered, so an example picked by number starts without its example details
      const offeredOnly: AskedQuestion = { id: "suggestions", slots: {}, offered: [picked] };
      return replyTo(picked, { available, matcher, ctx, rotation, previous: offeredOnly, task: null, usingFallback, parked });
    }
  }

  const session: ReplySession = { ctx, available, matcher, rotation };
  if (!running && previous?.subject) {
    const writingAbout = text.match(/^(?:can|could) you (?:write|make|create|send) a notice about (?:that|it) for (.+)$/i);
    const audienceAbout=text.match(/^send a notice to (.+?) about (?:it|that)[?.!]*$/i)?.[1];
    if(audienceAbout)return replyTo(`create a notice to ${audienceAbout} about ${previous.subject}`,{available,matcher,ctx,rotation,usingFallback,parked});
    if (writingAbout) return replyTo(`create a notice to ${writingAbout[1]} about ${previous.subject}`, { available, matcher, ctx, rotation, usingFallback, parked });
  }
  if (!running && previous && /^(?:(?:and|what about)\s+)?(?:the\s+)?(?:(?:this|next|last)\s+)?(?:wednesday|monday|tuesday|thursday|friday|saturday|sunday|week|month|year|today|tomorrow|yesterday)[?.!]*$/i.test(text.trim()) && /^(?:calendar\.|home\.upcomingEvents)/.test(previous.id)) {
    const entry=byId.get("calendar.whatsOn");
    const dateRange=findDateRange(text);
    if (entry?.answer && dateRange) return answer(entry,{dateRange},ctx,rotation.answers,usingFallback,text);
  }

  if (!running && previous?.id.startsWith("notices.") && previous.subject) {
    const subject = previous.subject;
    const anotherTitle = (text.trim().match(/^and\s+(.+?)[?.!]*$/i) ?? spoken.trim().match(/^and\s+(.+?)[?.!]*$/i))?.[1];
    if (anotherTitle && !/^(?:the (?:date|time)|what about|when|where|who|how)\b/i.test(anotherTitle)) {
      const notice = await lookupNoticeByName(`when is ${anotherTitle}`, ctx);
      if (notice) return aboutNotice(notice, whenWordings(notice)[rotation.answers % 3]);
    }
    if (OPEN_IT.test(text.trim())) {
      const notice = await lookupNoticeByName(subject, ctx);
      if (notice) return aboutNotice(notice, `Here's ${notice.title}.`, true);
    }
    const aspect = /^(?:what(?:'s| is) the venue|where'?s it happening)[?.!]*$/i.test(text.trim()) ? "where" : followUpAspect(text);
    if (aspect === "when" || /^(?:sorry\s+)?(?:what date was that|when'?s it on|when'?s that again|what time is it at|and the date)[?.!]*$/i.test(text.trim())) {
      const notice = await lookupNoticeByName(subject, ctx);
      if (notice) return aboutNotice(notice, whenWordings(notice)[rotation.answers % 3]);
    }
    if (aspect === "where") {
      const notice=await lookupNoticeByName(subject,ctx);
      if(notice)return aboutNotice(notice,notice.eventLocation?`The location is ${notice.eventLocation}.`:`No location is set for ${notice.title}.`);
    }
    const countId = /\b(?:haven'?t|hasn'?t|not|still needs?|yet to)\b.*\b(?:seen|see|read|opened|viewed)\b/i.test(text) ? "notices.unseen"
      : /\bread count|view count|how many (?:people )?views\b/i.test(text) || /\bhow many\b.*\b(?:seen|read|opened|viewed)\b/i.test(text) ? "notices.views" : null;
    const counter = countId ? byId.get(countId) : undefined;
    if (counter?.answer) return answer(counter, { notice: subject }, ctx, rotation.answers, usingFallback, text);
    if (/^(?:nudge|remind)\b/i.test(text.trim()) && /\b(?:it|that one|parents|everyone)\b/i.test(text)) {
      const reminder = byId.get("notices.sendReminder");
      if (reminder && hasHandoff(reminder)) return startHandoff(reminder, { notice: subject }, ctx, text);
    }
  }
  if (!running && !cancelKindOf(text) && /^(?:when|what time|what date|what day|is .+ on |the .+ when|what about |and .+\?)/i.test(text.trim())) {
    const notice = await lookupNoticeByName(text.replace(/^(?:and\s+)?what about\s+/i, "when is ").replace(/^and\s+/i, "when is ").replace(/^(?:the\s+)?(.+?)\s+when[?.!]*$/i, "when is $1"), ctx);
    if (notice) return aboutNotice(notice, whenWordings(notice)[rotation.answers % 3]);
  }

  // "open that one", "how many have seen it?", "send a reminder about it" just after a Notice was named: that Notice
  const about = !running && previous?.id.startsWith("notices.") && previous.subject ? previous.subject : undefined;
  const noticeAspect = about ? followUpAspect(text) : null;
  if (about && previous && (noticeAspect === "who" || noticeAspect === "where")) {
    const priorEntry = byId.get(previous.id);
    if (priorEntry) return followUpReply(noticeAspect, priorEntry, ctx, rotation, usingFallback, previous, true);
  }
  // "Debs ball - what day is it again?" names its own Notice: "it" is that one, not the last
  const namesAnother = async () => {
    const found = await findNoticeForWhen(text.replace(POINTS_BACK, " "), ctx).catch(() => null);
    return Boolean(found && !isSlotQuestion(found) && found.notice.title !== about);
  };
  if (about && !smallTalkReply(text) && text.trim().split(/\s+/).length <= 12 && POINTS_BACK.test(text) && !text.toLowerCase().includes(about.toLowerCase()) && !(await namesAnother())) {
    if (OPEN_IT.test(text.trim())) {
      const found = await findNoticeForWhen(about, ctx).catch(() => null);
      if (found && !isSlotQuestion(found)) return aboutNotice(found.notice, `Here's ${found.notice.title.replace(/[.!\s]+$/, "")}.`, true);
    }
    const named = text.replace(POINTS_BACK, about);
    const forgetting = { ...previous!, subject: undefined, mentions: undefined };
    const resolved = await replyTo(named, { available, matcher, ctx, rotation, previous: forgetting, task: null, usingFallback, parked });
    if (!["didYouMean", "unanswered", "offTopic", "loadFailed", "searchOnly"].includes(resolved.kind)) {
      // Still about the same Notice afterwards: "send a reminder about it", then "when is it?"
      return "asked" in resolved && resolved.asked && !resolved.asked.subject
        ? ({ ...resolved, asked: { ...resolved.asked, subject: about, mentions: [about] } } as AssistantReply)
        : resolved;
    }
  }

  // "can you help me write my lesson plan?": teaching work, not a Notice to reword
  if (!running && isTeachingWork(text)) return { kind: "offTopic", text: TEACHING_WORK_REPLY };

  // "can you make one for the grade 4 team meeting?" after how to make a staff Event: one of those, started.
  // "can you start one for me?" after how certificates are made: an Award.
  const makeOne = !running && previous && !/^\s*how\b/i.test(text) ? text.match(MAKE_ONE) : null;
  if (makeOne && previous) {
    const kind = previous.id === "concepts.noticeVsEvent" ? "a notice" : /certificate|award/i.test(previous.id) ? "certificates" : /event|calendar/i.test(previous.id) ? "an event" : /poll/i.test(previous.id) ? "a poll" : /urgent/i.test(previous.id) ? "an urgent notice" : /notice|create\.offerToSend|create\.open/i.test(previous.id) ? "a notice" : null;
    if (kind) {
      const asked = kind === "certificates" ? "Make certificates" : text.replace(/\bone\b/i, kind);
      if (kind !== "certificates") {
        const taskKind = kind.includes("event") ? "event" : kind.includes("poll") ? "poll" : "notice";
        const startedBy = taskKind === "event" ? "calendar.createEvent" : "create.notice";
        const started = await taskTurn((org) => startTask(taskKind, asked, startedBy, org), { ctx, available, matcher, rotation });
        if (started) return started;
      }
      return replyTo(asked, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
    }
  }

  // "can you do it for me?" when asked what kind of notice: yes, and the same question again
  if (!running && previous?.id === "create.offerToSend" && asksToMakeItForThem(text)) {
    return replyTo("Can you send a notice for me?", { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
  }

  if (!running && previous && ["create.open", "concepts.tenSecondNotice"].includes(previous.id) && asksToMakeItForThem(text)) {
    return replyTo("create a notice", { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
  }

  // "do it for me", "can you do that?" after steps that offered Do it for me: what that button starts
  const howToBefore = !running && previous && text.trim().split(/\s+/).length <= 9 && asksToMakeItForThem(text) ? byId.get(previous.id) : undefined;
  if (howToBefore && hasHowTo(howToBefore)) {
    const steps = await answerHowTo(howToBefore, ctx, previous?.startedByText || text);
    const start = steps.kind === "howTo" ? steps.actions.find((action) => action.kind === "start") : undefined;
    if (start && "ask" in start) return replyTo(start.ask, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
  }

  // "TEST Grade 4 2027" on its own, or "anything for grade 4?" after the Events coming up: that Circle, never an
  // old Notice that shares a word with it ("Last day of Grade R")
  if (!running && !looksLikeACatalogueExample(text, available)) {
    const named = await circleNamedAlone(text, previous, ctx);
    if (named?.afterEvents) {
      return replyTo(`Which events are for ${named.name}?`, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
    }
    if (named) {
      const questions = [`Send a notice to ${named.name}`, `Who's in ${named.name}?`, `Which events are for ${named.name}?`];
      return { kind: "didYouMean", text: `${named.name} is one of your Circles. What would you like to do?`, questions, asked: withOffered(previous, questions) };
    }
  }
  const open = running && running.phase !== "done" ? running : null;
  if (!open && previous?.eventDescription) {
    const eventWork = readWorkRequest(text);
    if (asksToAddDescribedEvent(text) || (eventWork?.kind === "event" && eventWork.meansTheOpenOne)) {
      return replyTo(previous.eventDescription, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback, parked });
    }
    previous = { ...previous, eventDescription: undefined };
  }
  const describedEvent = !open && !readWorkRequest(text) && byId.has("calendar.createEvent") ? describedEventRequest(text) : null;
  if (describedEvent) {
    return { kind: "answer", text: "Would you like me to prepare that as an Event? I can keep the details you supplied; nothing is sent until you review it and press Publish.", link: { label: "Open Calendar", href: "/calendar" }, asked: { id: "calendar.createEvent", slots: {}, eventDescription: describedEvent } };
  }
  // "Send a notice about Friday's trip", pressed from the suggestions: an example, so none of its details are kept
  const pressedExample = !open && Boolean(previous?.offered?.some((offered) => phrasingKey(offered) === phrasingKey(spoken)));
  /** Said alongside a task that stays open, then the step it was waiting on. */
  const alongside = async (said: string, asked?: AskedQuestion): Promise<AssistantReply | null> => {
    if (!open) return null;
    const step = await resumeTaskReply(open, session);
    return step?.kind === "task" ? { ...step, text: `${said} ${step.text}`, ...(asked ? { asked } : {}) } : null;
  };

  if (open && explicitQuestion(text)) {
    const sideReply = await replyTo(text, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback });
    if (sideReply.kind === "answer" || sideReply.kind === "howTo") {
      const kept = await alongside(sideReply.text, sideReply.asked);
      if (kept) return kept;
    }
  }

  if(open && /^(?:will you send it for me|will it send straight away)[?.!]*$/i.test(text.trim())) {
    const kept=await alongside("I haven't sent it. You review it and press Publish yourself; choose Schedule for later if it should go out at another time.");
    if(kept)return kept;
  }
  if(open && /^how do reminders work for events[?.!]*$/i.test(text.trim())) {
    const entry=byId.get("create.eventReminderHowTo");
    const explained=entry?.answer ? await answer(entry,{},ctx,rotation.answers,usingFallback,text) : entry && hasHowTo(entry) ? await answerHowTo(entry,ctx,text) : null;
    const kept=explained && "text" in explained ? await alongside(explained.text,{id:"create.eventReminderHowTo",slots:{}}) : null;
    if(kept)return kept;
  }
  if (open && /^(?:ugh|you'?re not helping|hello[?!.\s]*is this thing on|[?!.\s]*is this thing on|remind me what we'?re making|where was i)[.!?]*$/i.test(spoken.trim())) {
    const kept = await alongside(`I've kept your ${open.task === "poll" ? "Poll" : open.task === "event" ? "Event" : "Notice"} draft.`);
    if (kept) return kept;
  }
  if (open && /^(?:tell me a joke|what'?s the capital of france|what'?s 15% of 240)[?.!]*$/i.test(text.trim())) {
    return {kind:"offTopic",text:`${offTopicReply(rotation.offTopic)} ${carryOnPrompt(open.task)}`};
  }
  if(open?.task==="poll" && open.asked==="question" && /^can parents reply (?:2|to) a notice[?.!]*$/i.test(text.trim())) {
    const reply=await replyTo("Can parents reply to a notice?",{available,matcher,ctx,rotation,previous:null,task:null,usingFallback});
    const kept="text" in reply ? await alongside(reply.text,"asked" in reply ? reply.asked : undefined) : null;
    if(kept)return kept;
  }
  if (open && /^(?:where (?:are|r) my notices|how many drafts (?:do )?i (?:have|hv)|can parents pick more than one option in a poll)[?.!]*$/i.test(text.trim())) {
    const answered = await replyTo(text,{available,matcher,ctx,rotation,previous:null,task:null,usingFallback});
    const kept = "text" in answered ? await alongside(answered.text,"asked" in answered ? answered.asked : undefined) : null;
    if (kept) return kept;
  }
  if (open && /^(?:(?:please|can you|could you)\s+)?(?:just\s+)?(?:send(?: it)?|push it out)(?: now| please)?[?.!]*$/i.test(text.trim())) {
    const kept = await alongside("I can prepare it, but you review it and press Publish yourself.");
    if (kept) return kept;
  }
  if (open && /^(?:i don'?t understand|this isn'?t working|why is this so hard|(?:ok )?where were we|that'?s not what i asked)[.!?]*$/i.test(text.trim())) {
    const kept = await alongside("Let's carry on one step at a time. I've kept the draft.");
    if (kept) return kept;
  }
  const asksDraftStatus = /^(?:(?:so|ok(?:ay)?|and|then)\s+)?(?:(?:did\s+(?:it|this|that)\s+go\s+out|(?:is|was)\s+(?:it|this|that)\s+(?:sent|published)(?:\s+now)?)|(?:has|have)\s+(?:(?:it|this|that)\s+(?:been\s+(?:sent|published)|gone\s+out)|you\s+(?:sent|published)\s+(?:it|this|that))|it'?s?\s+sent(?:\s+now)?)[?.!]*$/i.test(text.trim());
  const asksAfterHandover = /^(?:so\s+)?it'?s\s+gone\s+out\s+to\s+parents[?.!]*$/i.test(text.trim());
  if (running?.phase === "done" && (asksDraftStatus || asksAfterHandover || /^did (?:the )?parents get it[?.!]*$/i.test(text.trim()))) {
    return { kind: "answer", text: "I haven't sent anything. This chat prepares the draft and opens the wizard; you review it and press Publish. If you already pressed Publish there, check Sent on Notices for its status.", link: { label: "Open Notices", href: "/view-notices" }, asked: { id: "concepts.doesNotSend", slots: {}, handedOverDraft: running }, };
  }
  if (open && (asksDraftStatus || asksAfterHandover || /^(?:did the parents get it|has it been published)[?.!]*$/i.test(text.trim()))) {
    const kept = await alongside("I haven't sent this draft. I only prepare it; you review it in the wizard and press Publish yourself.");
    if (kept) return kept;
  }

  if (!open && (asksDraftStatus || /^(?:did (?:the )?parents get it|has it been published|so it'?s? sent now)[?.!]*$/i.test(text.trim())) && (running || !previous?.subject)) {
    return { kind: "answer", text: "I haven't sent anything. You review the draft in Create and press Publish yourself. If you have done that, check Sent on Notices.", link: { label: "Open Notices", href: "/view-notices" }, asked: { id: "concepts.doesNotSend", slots: {} } };
  }

  if (open?.phase === "confirming" && open.details.to && /^(?:(?:great|ok(?:ay)?|yes|perfect)[,\s]+)*(?:send|post)\s+it\s+to\s+(?:them|those parents|the same parents)[.!?]*$/i.test(text.trim())) {
    const approved = await taskTurn((org) => continueTask(open, "looks good", org), session);
    if (approved) return approved;
  }
  if (open && isContextualTaskRequest(open, text)) {
    const edit = await taskTurn((org) => contextualTaskTurn(open, text, org), session);
    if (edit) return edit;
  }

  if (/\b(?:image|picture|photo)\b/i.test(text) && /\b(?:edit|replace|crop|without changing|two pictures)\b/i.test(text) && looksLikeQuestion(text)) {
    const answer = "A sent Notice can't be edited. For a Draft, reopen it under Pick up where you left off on Create or Notices, then change its title or image before publishing. I can't upload, replace or crop pictures in this chat.";
    const kept = await alongside(answer, { id: IMAGE_NOTICE_HOW_TO, slots: {} });
    return kept ?? { kind: "howTo", text: answer, actions: [{ kind: "open", label: "Open Create", to: { pathname: toNewImageNotice().pathname, state: {} } }], asked: { id: IMAGE_NOTICE_HOW_TO, slots: {} } };
  }

  if (previous?.id === IMAGE_NOTICE_HOW_TO && /keep.*title|use.*(?:image|picture).*Drive/i.test(text)) {
    const title = String(previous.slots.topic ?? "");
    const reply = imageNoticeReply(title ? `titled "${title}"` : "");
    return { ...reply, text: /Drive/i.test(text)
      ? "I can't pick the file in chat. In the Image composer, choose Drive from the image attachment menu and select the existing picture. Nothing is sent until you publish."
      : title ? `Yes. Open Create will keep the title “${title}” in the Image composer.` : "Tell me the title you'd like to use, or enter it in the Image composer." };
  }

  // "Where did my notice go? Carry on with it", "go back to my dance event": a draft set aside (or the one
  // open), where it stopped
  const resumesOpen = Boolean(open && (RESUME_TASK.test(text.trim()) || RESUME_THEN.test(text.trim())));
  if ((parked.length > 0 || open) && asksForADraft(text) && !resumesOpen) {
    const draft = draftAskedFor(open ? [open, ...parked] : parked, text);
    if (draft) {
      const resumed = await resumeTaskReply(draft, session);
      if (resumed?.kind === "task") {
        return {
          ...resumed,
          ...(draft === open ? {} : { startedBy: draft.startedById }),
          text: draft === open ? `It's still open: ${draftName(draft)}. ${resumed.text}` : `Here's ${draftName(draft)} again. ${resumed.text}`,
        };
      }
    }
  }

  // "It's a question": the text it asked about is answered, and the step waits
  if (open?.pendingFreeText && /^(?:it'?s|it is|that'?s|that was|its)\s+a\s+question\b/i.test(text.trim())) {
    const waiting: TaskRecord = { ...open, pendingFreeText: undefined };
    const [answered, step] = await Promise.all([
      replyTo(open.pendingFreeText.text, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback }),
      resumeTaskReply(waiting, session),
    ]);
    // Only an answer: a guess at other questions, or a task the question would start, isn't one
    const answers = ["answer", "howTo", "handoff", "cannot", "recovery"].includes(answered.kind);
    const said = answers && "text" in answered && answered.text ? `${answered.text} ` : "";
    if (step?.kind === "task") return { ...step, text: `${said}${step.text}` };
  }

  if (open && !open.pendingFreeText && /^(?:it'?s|it is|its)\s+a\s+question\b/i.test(text.trim())) {
    const kept = await alongside("Go ahead and ask it.");
    if (kept) return kept;
  }

  // "can it be an event instead?", "I want to change it to an Event": the same draft as the other kind
  const switchTo = open ? kindSwitchedTo(text) ?? switchedKind(text) : undefined;
  if (open && switchTo && switchTo === open.task) {
    const kept = await alongside(`It's ${KIND_SAID[switchTo].replace(/\.\s.*$/, "").replace(/^An?\s/, (an) => an.toLowerCase())} already.`);
    if (kept) return kept;
  }
  if (open && switchTo && switchTo !== open.task) {
    const original = /\b(?:back|again|after all)\b/i.test(text) ? parked.find((record) => record.task === switchTo) : undefined;
    if (original) {
      const resumed = await resumeTaskReply(original, session);
      if (resumed?.kind === "task") return { ...resumed, startedBy: original.startedById, text: `Here's your ${switchTo === "poll" ? "Poll" : switchTo === "event" ? "Event" : "Notice"} again. ${resumed.text}` };
    }
    const startedBy = switchTo === "event" ? "calendar.createEvent" : "create.notice";
    if (available.some((entry) => entry.id === startedBy)) {
      const carried = carriedOver(open, switchTo);
      const after = saidAfterSwitch(text);
      const request = `create ${switchTo === "event" ? "an" : "a"} ${switchTo}${after ? ` ${after}` : ""}`;
      const started = await taskTurn((org) => startTask(switchTo, request, startedBy, org, { details: carried }), session);
      if (started?.kind === "task") {
        const kept = Object.keys(carried).length > 0 ? ", keeping what you've given" : "";
        return { ...started, startedBy, text: `${KIND_SAID[switchTo].replace(/\.\s.*$/, "")} then${kept}. ${started.text.replace(/^Sure\.\s*/, "")}` };
      }
    }
  }

  // "yes but make it urgent", "perfect, just change the title to …": a change to this draft, not a yes and not a new one
  if (open) {
    const butThen = text.match(/^(?:yes|yeah|yep|ok(?:ay)?|perfect|great|good|looks good|that's great|all good)[,.!\s]+(?:but|just|except|only|and)\s+(.+)$/i)?.[1];
    if (butThen) return replyTo(butThen, { available, matcher, ctx, rotation, previous, task: running, usingFallback, parked });
  }
  // "make it urgent", "can it be urgent?", "mark this as urgent please" on an open Notice: that Notice, urgent
  const urgentIt =
    open?.task === "notice" &&
    !/\b(?:not|don'?t|do not|no longer)\b[^.?!]*\burgent\b/i.test(text) &&
    /^(?:(?:please|actually|oh|and|also)[,\s]+)*(?:(?:can|could)\s+(?:you\s+)?(?:make|mark|set)\s+(?:it|this|that)|(?:make|mark|set)\s+(?:it|this|that|the\s+notice)|(?:can|could)\s+(?:it|this|that)\s+be)\s+(?:as\s+)?(?:an?\s+)?urgent(?:\s+(?:one|notice))?(?:\s+please)?[?.!]*$/i.test(text.trim());
  if (urgentIt && open) {
    const urgent = await taskTurn((org) => continueTask(open, "urgent", org), session);
    if (urgent?.kind === "task") return urgent;
  }

  // "delete this draft" while one is open: it's cancelled. Said with nothing open, it asks about a sent Notice, below.
  if (open && DELETE_THE_DRAFT.test(text.trim())) return { kind: "cancelled", text: CANCELLED_REPLY, choices: [], dropTask: true };

  // "start over", "restart": the same kind of draft, from its first question
  if (open && startsOver(text)) {
    const startedBy = open.startedById || (open.task === "event" ? "calendar.createEvent" : "create.notice");
    const again = await taskTurn((org) => startTask(open.task, `create ${open.task === "event" ? "an" : "a"} ${open.task}`, startedBy, org), session);
    if (again?.kind === "task") return { ...again, startedBy, text: `Starting again. ${again.text.replace(/^Sure\.\s*/, "")}` };
  }

  // "can you schedule it for 7am tomorrow?", "send it tomorrow at 7am" with a draft open: scheduled on the draft's last step, and the draft stays
  const scheduleMatch = open ? text.trim().match(SCHEDULE_IT) ?? text.trim().match(/^schedule\s+(?:for|on|at)\s+(.+)$/i) : null;
  const laterWhen = open && !scheduleMatch ? sendsLater(text) ?? text.match(/^i want it to go out (.+)$/i)?.[1] : undefined;
  const scheduleIt = scheduleMatch ?? (laterWhen ? ([text, laterWhen] as unknown as RegExpMatchArray) : null);
  if (scheduleIt) {
    const when = scheduleIt[1]?.replace(/^(?:on|at|for)\s+/i, "").replace(/[\s?.!]+$/, "");
    const explanation = `I can't schedule it from here. When it opens, choose Schedule for later on the last step and set ${when ? `it for ${when}` : "the date and time"}. Nothing goes out until you press Schedule.`;
    if (open?.task === "notice" && /\b(?:mark|make) (?:it|this)(?: as)? urgent\b/i.test(text) && !/\b(?:not|don't|do not)\b/i.test(text)) {
      const urgent = await taskTurn((org) => continueTask(open, "urgent", org), session);
      if (urgent?.kind === "task") return { ...urgent, text: `${explanation} ${urgent.text}` };
    }
    const kept = await alongside(explanation);
    if (kept) return kept;
  }

  if (open && /^how\s+(?:do|can|should)\s+i\s+schedule\s+(?:a|the|my)\s+notice\b/i.test(text.trim())) {
    const kept = await alongside("On the last step, choose Schedule for later, pick the Publish date and Publish time, then press Schedule. I can't schedule it from this chat.", { id: "create.scheduleOffer", slots: {} });
    if (kept) return kept;
  }

  // Adding a file to this Notice is done in its writing step; it keeps the same draft.
  const fileInstruction = /^(?:(?:please|can you|could you)\s+)?(?:add|upload|attach|include)\s+(?:(?:the|a|an|my)\s+)?(?:pdf|file|document|attachment|permission slip)\b/i.test(text) && !/\b(?:to|into)\s+(?:(?:the|my|our)\s+)?drive\b/i.test(text);
  const attachesToThis = /\b(?:attach|add|include|upload)\b[^.?!]*\b(?:file|document|pdf|permission slip|attachment)\b/i.test(text) && !/\b(?:to|into)\s+(?:(?:the|my|our)\s+)?drive\b/i.test(text) && (/\battach\b/i.test(text) || /\b(?:to|on|with)\s+(?:it|this|that|the notice|my notice)\b/i.test(text));
  if (open?.task === "notice" && (attachesToThis || fileInstruction) && (fileInstruction || open.phase === "confirming" || looksLikeQuestion(text) || /^i\s+(?:need|want|would like)\s+to\b/i.test(text))) {
    const kept = await alongside("When the Notice opens, add the file under Attachment on its writing step. I've kept this draft. Nothing is sent until you press Publish.", { id: "create.sendFileHowTo", slots: {} });
    if (kept) return kept;
  }

  // "make it sound less cringe" mid-task: the wizard's Rewrite with Nova does wording, and the draft stays
  if (open && (isWritingRequest(text) || isTranslationRequest(text))) {
    const said = isTranslationRequest(text) ? TRANSLATION_REQUEST_REPLY : WRITING_REQUEST_MID_TASK;
    const kept = await alongside(said, { id: "concepts.nova", slots: {} });
    if (kept) return kept;
  }

  // "just send it now, I dont want to press publish", "publish it immediately, skip the review"
  if (open?.phase === "confirming" && SEND_IT_FOR_ME.test(text) && !readWorkRequest(text)) {
    const kept = await alongside(CANT_SEND_FOR_YOU, { id: "concepts.doesNotSend", slots: {} });
    if (kept) return kept;
  }

  // "can I put a picture on the science fair notice?" while a draft is open: attached in the wizard
  if (open && (/^(?:(?:please|can you|could you)\s+)?(?:add|attach|put)\s+(?:(?:a|the|my)\s+)?(?:pic|picture|photo|image)\b/i.test(text) || looksLikeQuestion(text) || /^i\s+(?:want|need|would like)\s+to\s+attach\b/i.test(text)) && PICTURE_ON_IT.test(withoutTextSpeak(text))) {
    const kept = await alongside(PICTURE_IN_THE_WIZARD, { id: IMAGE_NOTICE_HOW_TO, slots: {} });
    if (kept) return kept;
  }
  // "…money goes to the library. add a pic pls" as the message: the message, and a word on the picture
  const pictureAsked = open && !looksLikeQuestion(text) ? text.match(PICTURE_ASKED_AT_END) : null;
  if (pictureAsked?.index) {
    const rest = text.slice(0, pictureAsked.index + (pictureAsked[0].match(/^[.,!;]+/)?.[0].length ?? 0)).trim();
    const reply = await replyTo(rest, { available, matcher, ctx, rotation, previous, task: running, usingFallback, parked });
    if (reply.kind === "task") return { ...reply, text: `${PICTURE_IN_THE_WIZARD} ${reply.text}` };
  }

  // "I need parents to sign up to bring things", "can you make the claim list for me here?": a claim list, made on Create
  // "Create a notice. Title: Reading volunteers. Message: …": a Notice with its parts given, whatever its title says
  const requested = !open ? readWorkRequest(text) : null;
  const givesItsParts = Boolean(requested && requested.kind !== "image" && (requested.title || requested.message));
  if (!open && !givesItsParts && asksForAClaimList(text) && available.some((entry) => entry.id === "create.notice")) {
    const reply = claimListReply();
    const kept = await alongside(reply.text, reply.asked);
    return kept ?? reply;
  }

  // A picture notice is made on Create, whichever way it's asked for.
  // "Turn this email into a notice: …" asks for writing, which Rewrite with Nova does (below), not a new task.
  // "help me write a notice for parents", with nothing to reword yet, is one made together
  const work = !open && !givesItsParts && isWritingRequest(text) && !HELP_ME_MAKE_ONE.test(withoutTextSpeak(text.trim())) ? null : requested ?? readWorkRequest(text);
  const short = text.trim().split(/\s+/).length;
  const wantsAPicture =
    !waitingForWords &&
    (work?.kind === "image" ||
    (!work && namesAPicture(text) && (short <= 2 || (previous?.id === "create.offerToSend" && short <= 6))) ||
    (!work && previous?.id === IMAGE_NOTICE_HOW_TO && asksToMakeItForThem(text)) ||
    // "how do i make a img one", "how do I send a picture notice"
      (!work && exactHits.length === 0 && namesAPicture(text) && HOW_TO_MAKE.test(withoutTextSpeak(text)) && !OTHER_PICTURES.test(text)) ||
      // "I want to send a photo to parents"
      (!work && !open && namesAPicture(text) && SEND_A_PICTURE.test(withoutTextSpeak(text)) && !OTHER_PICTURES.test(text)));
  if (wantsAPicture && available.some((entry) => entry.id === "create.notice")) {
    const reply = imageNoticeReply(work?.request ?? text);
    const kept = await alongside(reply.text, reply.asked);
    return kept ?? reply;
  }

  // "information then. title: Grade 7 Farewell" after an image notice was explained: the Information notice offered
  if (!open && previous?.id === IMAGE_NOTICE_HOW_TO && /^(?:ok(?:ay)?[,\s]+|fine[,.\s]+)?info(?:rmation)?\b/i.test(text.trim())) {
    const started = await taskTurn((org) => startTask("notice", text, "create.notice", org), session);
    if (started?.kind === "task") {
      return { ...started, startedBy: "create.notice", text: `An Information notice. ${started.text.replace(/^Sure\.\s*/, "")}` };
    }
  }

  // "mk a poll", "Now an EVENT! Sports Day", "event. staff meeting next wednesday…": started, with what it says.
  // Mid-task, "create an event" is a new one even though it's a catalogue example word for word. "Event" after
  // "What kind of notice?" is that choice, made further down.
  const choosingAKind = (previous?.id === "create.offerToSend" || previous?.id === "create.scheduleOffer") && !/^can (?:we|i) do (?:a|an) (?:notice|poll|event)[?.!]*$/i.test(text.trim());
  if (work && work.kind !== "image" && (exactHits.length !== 1 || open || /^can (?:we|i) do (?:a|an) (?:notice|poll|event)[?.!]*$/i.test(text.trim()) || /^quick notice[.!?]*$/i.test(text.trim()) || (exactHits[0]?.id === "create.urgentHowTo" || exactHits[0]?.id === "create.scheduleOffer")) && !choosingAKind) {
    const freeText = open?.phase === "collecting" && ["title", "message", "question", "choices"].includes(open.asked ?? "");
    // "create a poll" while one is half-made: that one, carried on, rather than started again
    const bare = !work.message && !work.title && !Object.keys(readTaskDetails(work.request).said).some((k) => k !== "noticeType");
    if (open?.phase === "collecting" && work.kind === open.task && bare) {
      const kept = await alongside(`Your ${KIND_SAID[work.kind].match(/^An?\s(\w+)/)?.[1] ?? "draft"} is already started.`,{id:open.startedById,slots:{}});
      if (kept) return kept;
    }
    // "testing. then do the bus notice next": the first sentence answers the step, and the request waits
    const answersFirst = Boolean(open && work.sentence > 0 && (await takesFirstSentence(open, text, session)));
    // "do the bus notice next": the one set aside, picked up again
    const setAside = work.meansTheOpenOne ? parked.find((record) => record.task === work.kind) : undefined;
    if (setAside && !answersFirst && (!open || open.task !== work.kind)) {
      const resumed = await resumeTaskReply(setAside, session);
      if (resumed?.kind === "task") return { ...resumed, startedBy: setAside.startedById, text: `Here's ${draftName(setAside)} again. ${resumed.text}` };
    }
    const fresh = !answersFirst && (!open || ((work.kind !== open.task || !work.meansTheOpenOne) && (!freeText || work.atStart)));
    const startedBy = work.kind === "event" ? "calendar.createEvent" : "create.notice";
    if (fresh && available.some((entry) => entry.id === startedBy)) {
      const said = pressedExample ? {} : { ...(work.message ? { message: work.message } : {}), ...(work.title ? { title: work.title } : {}) };
      const request = pressedExample ? `create ${work.kind === "event" ? "an" : "a"} ${work.kind}` : work.request;
      const started = await taskTurn((org) => startTask(work.kind, request, startedBy, org, { said }), session);
      if (started?.kind === "task") {
        const urgent = Boolean((started.task?.details as { urgent?: boolean } | undefined)?.urgent);
        const scheduling = work.kind === "notice" && /\bschedule\b|\b(?:go(?:es)? out|be sent)\s+(?:on|at|tomorrow|next|today)/i.test(text);
        const wording = (work.aside && isWritingRequest(work.aside) ? `${WRITING_REQUEST_MID_TASK} ` : "") + (scheduling ? "On the last step, choose Schedule for later and set the Publish date and Publish time. Nothing is scheduled until you press Schedule. " : "");
        const others = work.thenAlso?.length
          ? `Then ${work.thenAlso.map((k) => (k === "event" ? "the Event" : k === "poll" ? "the Poll" : "the Notice")).join(" and ")}. `
          : "";
        return { ...started, startedBy, text: startedSaying(work.kind, urgent, started.text, `${others}${wording}`) };
      }
      if (started && started.kind !== "loadFailed") return started;
    }
  }

  // "can staff see who voted?" while asked who it's for: answered, then asked again who it's for
  if (
    open?.phase === "collecting" &&
    open.asked === "to" &&
    matcher &&
    looksLikeQuestion(text) &&
    !ABOUT_WHO_IT_REACHES.test(text) &&
    !(await takesWhole(open, text, session))
  ) {
    const answered = await answerOnTheSide(text, session);
    const kept = answered ? await alongside(answered.text, answered.id ? { id: answered.id, slots: {} } : undefined) : null;
    if (kept) return kept;
  }

  // "Thank you. Next, …" is a request, above. "THANK YOU!!! You're on the TEAM 🏆" is thanks.
  if (!thankYouTitle && !readWorkRequest(text) && /^(?:thank\s*you|thanks|thx|ty|cheers|dankie)\b[\s!.,]*[^?]{0,40}$/i.test(spoken.trim()) && !looksLikeQuestion(spoken)) {
    return { kind: "smallTalk", text: THANKS_REPLY };
  }

  // Phone numbers, passwords, "your instructions": a plain no, with nothing to open
  const refusal = privateDataReply(text);
  if (refusal) return { kind: "cannot", text: refusal };

  // "write me a poem about mondays": not something Noticeboard does
  if (/\bwrite\s+(?:me\s+)?(?:a\s+)?(?:poem|song|story|essay|rap|haiku|limerick)\b/i.test(text)) {
    return { kind: "offTopic", text: offTopicReply(rotation.offTopic) };
  }

  // "whats the date today", "wat date is it more?", then "more = tomorrow"
  // "anything on 30/10?", "events next weekend?", "what's on the 12th": the Calendar for that day or range
  const createPage = byId.get("create.open");
  if (!running && createPage?.answer && /^(?:how\s+(?:do|can)\s+i\s+post|where\s+(?:do|can|should)\s+i\s+(?:start|create|make)|what'?s\s+the\s+(?:quickest|fastest|easiest)\s+way\s+to\s+(?:send|create|make))\s+(?:a\s+)?(?:new\s+)?notice[?.!]*$/i.test(text.trim())) {
    return answer(createPage, {}, ctx, rotation.answers, usingFallback, text);
  }
  const latestViews = byId.get("notices.views");
  if (!running && latestViews?.answer && /\b(?:last|latest|most recent)\s+notice\b/i.test(text) && /\b(?:read|seen|viewed|opened|views)\b/i.test(text) && !/\b(?:how\s+(?:do|can|to)|remind|send|create|write)\b/i.test(text)) {
    return answer(latestViews, { notice: "" }, ctx, rotation.answers, usingFallback, text);
  }
  const noticeHistory = byId.get("notices.sentOn");
  const historyRange = !running && /^(?:(?:show|list|find)(?:\s+me)?\s+)?(?:the\s+)?notices?\s+(?:from|in|on|during|sent)\b/i.test(text.trim()) ? findDateRange(text) : null;
  if (historyRange && noticeHistory?.answer) return answer(noticeHistory, { dateRange: historyRange }, ctx, rotation.answers, usingFallback, text);

  const whatsOn = byId.get("calendar.whatsOn");
  const onWhen = !running && whatsOn?.answer && text.trim().split(/\s+/).length <= 8 && /^(?:(?:so|and|ok)\s+)?(?:which\s+events?\s+(?:were|are)|anything|any\s+events?|events?|what'?s\s+on|whats\s+on|what\s+(?:is|was)\s+on|what'?s\s+happening|is\s+there\s+anything)\b/i.test(text.trim()) ? findDateRange(text) : null;
  if (onWhen && whatsOn?.answer) return answer(whatsOn, { dateRange: onWhen }, ctx, rotation.answers, usingFallback, text);

  // "Debs ball - what day is it again?": the thing is said first, then the question about it
  const namedFirst = !running ? text.trim().match(/^(.{3,50}?)\s*[-–—:,?]\s*(?:so\s+|um\s+|and\s+)?(?:what\s+(?:day|date|time)|when)\s+(?:is|was|does)\s+(?:it|that)\b[^.?!]*[?.!]*$/i)?.[1] : undefined;
  if (namedFirst && !/^(?:hi|hey|hello|ok|okay|sorry|quick question)\b/i.test(namedFirst)) {
    return replyTo(`When is ${namedFirst.replace(/^(?:the|our)\s+/i, "the ")}?`, { available, matcher, ctx, rotation, previous, task: null, usingFallback, parked });
  }

  if (asksTheDate(text) || (previous?.id === TODAY_ASKED.id && meansTomorrow(text))) {
    const home = getSidebarItem("home");
    const reply: AssistantReply = { kind: "answer", text: dateReply(text), link: { label: `Open ${home.label}`, href: home.path }, asked: TODAY_ASKED };
    return (await alongside(dateReply(text))) ?? reply;
  }

  // "write the date the British way, 29 October, with the time as 14:30": the app shows dates its own way
  if (/\b(?:british|uk|24[- ]?hour|day[- ]month|written|format)\b/i.test(text) && /\b(?:date|time)\b/i.test(text) && /\b(?:way|format|write|written|show|as)\b/i.test(text)) {
    const said = "I can't change how dates and times are written: each person's app shows them its own way. What's kept is the date and time themselves.";
    const kept = await alongside(said);
    if (kept) return kept;
  }

  // "if I send to Everyone does every parent get it?": who Everyone is, not a Circle called that
  // "can it go to everyone instead?" on a summary is a change, not a question about who Everyone reaches
  const asksForEveryoneInstead = running?.phase === "confirming" && /\binstead\b|^(?:actually[,\s]+)?(?:can|could)\s+(?:it|you)\b/i.test(text);
  if (open?.phase === "collecting" && open.asked === "to" && !looksLikeQuestion(text) && asksWhoEveryoneReaches(text)) {
    const recipient = await taskTurn((org) => continueTask(open, text, org), session);
    if (recipient) return recipient;
  }
  if (asksWhoEveryoneReaches(text) && !asksForEveryoneInstead) {
    const kept = await alongside(EVERYONE_REACH_REPLY, { id: "create.everyoneHowTo", slots: {} });
    if (kept) return kept;
    return { kind: "howTo", text: EVERYONE_REACH_REPLY, actions: [], asked: { id: "create.everyoneHowTo", slots: {} } };
  }

  // "send a reminder about Kara's Birthday", "remind parents about the debs ball": a reminder for that sent Notice
  const remindAbout = !open
    ? withoutTextSpeak(text.trim()).match(/^(?:(?:please|can\s+you|could\s+you)\s+)?(?:send|do|set\s+up)\s+(?:a\s+|the\s+)?reminder\s+(?:about|for|on)\s+(.+?)[?.!]*$|^(?:(?:please|can\s+you|could\s+you)\s+)?(?:remind|nudge)\s+(?:everyone|parents|families|them|people|the\s+parents)\s+(?:about|of|re)\s+(.+?)[?.!]*$/i)
    : null;
  const reminding = byId.get("notices.sendReminder");
  const remindName = remindAbout?.[1] ?? remindAbout?.[2];
  if (remindName && reminding && hasHandoff(reminding) && !/^(?:it|that|this|that one|the notice|my notice)$/i.test(remindName)) {
    const found = await findNoticeForWhen(remindName, ctx).catch(() => null);
    if (found && !isSlotQuestion(found)) return startHandoff(reminding, { notice: found.notice.title }, ctx, text);
  }

  // "how many ppl read the halloween notice": that Notice's views, by its name
  const howManySaw = !open ? (text.trim().match(HOW_MANY_SAW)?.[1] ?? text.trim().match(/^(?:(?:what(?:'s|s|\s+is)\s+(?:the\s+)?)|(?:show(?:\s+me)?\s+(?:the\s+)?))?(?:read|view)\s+count\s+(?:on|for|of)\s+(.+?)[?.!]*$/i)?.[1]) : undefined;
  const views = byId.get("notices.views");
  if (howManySaw && views?.answer && !/^(?:it|that|this|notice|last\s+one)$/i.test(howManySaw)) {
    // The whole title, or enough of it to name one Notice ("the halloween notice")
    const name = (await noticeNamedIn(howManySaw, ctx)) ?? (await findNoticeForWhen(howManySaw, ctx).then((found) => (found && !isSlotQuestion(found) ? found.notice.title : undefined)).catch(() => undefined));
    if (name) return answer(views, { notice: name }, ctx, rotation.answers, usingFallback, text);
  }

  // "who has seen Uniform Shop Sale?": that Notice's viewers, by its name
  const seenWhat = !open ? text.trim().match(WHO_HAS_SEEN)?.[1] : undefined;
  const viewers = byId.get("notices.viewers");
  if (seenWhat && viewers?.answer && !/^(?:it|that|this|my(?:\s+last)?\s+notice|the\s+notice)$/i.test(seenWhat)) {
    const name = await noticeNamedIn(seenWhat, ctx);
    if (name) return answer(viewers, { notice: name }, ctx, rotation.answers, usingFallback, text);
  }

  // "did my civvies notis go out?", "what did we send to testing today?": from the Sent tab
  if (asksIfSent(text) && (looksLikeQuestion(text) || /^\W*(?:so|and|ok)?\s*(?:did|has|have|what)\b/i.test(text)) && text.split(/\s+/).length <= 16 && available.some((entry) => entry.id === "notices.open")) {
    try {
      const { text: said, link } = await sentStatusReply(text, ctx);
      const kept = await alongside(said, { id: "notices.sentStatus", slots: {} });
      return kept ?? { kind: "answer", text: said, link, asked: { id: "notices.sentStatus", slots: {} } };
    } catch (error) {
      console.error("[Ask Noticeboard] Couldn't check what was sent", error);
      return { kind: "loadFailed", text: LOAD_FAILED_REPLY };
    }
  }

  const changeIt =
    !running && previous?.subject
      ? text.match(/^(?:please\s+)?(?:(?:can|could|would)\s+you\s+)?change\s+(?:it|that(?:\s+one)?)\b\s*(.*)$/i)
      : null;
  if (changeIt && previous?.subject) {
    const rest = changeIt[1].trim();
    const rewritten = rest ? `change ${previous.subject} ${rest}` : `change ${previous.subject}`;
    const reply = await replyTo(rewritten, {
      available,
      matcher,
      ctx,
      rotation,
      previous,
      task: null,
      usingFallback,
    });
    if (!UNRESOLVED_KINDS.has(reply.kind)) return reply;
    const prompt = `What would you like to change about ${previous.subject}?`;
    const entry = byId.get(previous.id);
    if (entry) return pointed(entry, prompt, previous, ctx);
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: prompt,
      link: { label: `Open ${home.label}`, href: home.path },
      asked: previous,
    };
  }

  // "cancel the debs ball event", "call off sports day": how a sent or scheduled Event is cancelled, not its date
  const callsOff = !running ? text.trim().match(/^(?:(?:please|we need to|i need to)\s+)?(?:cancel|call\s+off|postpone)\s+(?!(?:it|that|this|everything|all)\b)(?:the\s+|our\s+|my\s+)?(.{3,50}?)[.!?]*$/i)?.[1] : undefined;
  const cancelling = byId.get("create.cancelEventHowTo");
  if (callsOff && cancelling && hasHowTo(cancelling) && (/\bevent\b/i.test(callsOff) || (await noticeNamedIn(callsOff, ctx)))) {
    return answerHowTo(cancelling, ctx, text);
  }

  // "Find notices for Grade 4", "show the posts sent to gr 4": that Circle's Notices. Before a Notice lookup,
  // which otherwise hears "Grade" in an old title ("Last day of Grade R").
  const forCircle = !running ? text.match(NOTICES_FOR_A_CIRCLE)?.[1]?.replace(/[?.!\s]+$/, "") : undefined;
  const toCircle = byId.get("notices.toCircle");
  if (forCircle && toCircle?.answer) {
    const circles = await loadCircles(ctx).catch(() => []);
    const recipients = circles.map((circle) => ({ kind: "circle" as const, id: circle.circleId, name: circle.name }));
    const [only, ...others] = findRecipients(recipients, forCircle);
    if (only && others.length === 0) return answer(toCircle, { circle: only.name }, ctx, rotation.answers, usingFallback, text);
  }

  // "who's in Fugazi General", then "and Blackbrick Umhlanga": the name is the other Circle.
  // This has to come before a Notice lookup, which otherwise hears the name as a title.
  if (
    !running &&
    (asksCircleMembers(text) || (previous?.id === "circles.members" && !STAYS_ON_THE_CIRCLE.test(text)))
  ) {
    const members = byId.get("circles.members");
    if (members?.answer) {
      const aboutMembers = await membersOfCircle(text.match(NAMED_CIRCLE_MEMBERS)?.[1] ?? text, members, ctx, rotation.answers);
      if (aboutMembers) return aboutMembers;
    }
  }
  const destination = !running ? pageToOpen(text) : null;
  if (destination && canOpenSidebarItem(getSidebarItem(destination.key), ctx.user)) {
    return {
      kind: "answer",
      text: `Opening ${destination.label}.`,
      link: { label: `Open ${destination.label}`, href: destination.href },
      asked: { id: destination.id, slots: {} },
      openNow: true,
    };
  }
  if (destination) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: `${destination.label} isn't in your sidebar. An Owner or Admin opens ${destination.label}.`,
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: destination.id, slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "circles.open") && /\b(?:how|where)\b/i.test(text) && /\bopen circles\b/i.test(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "Circles isn't in your sidebar. An Owner or Admin opens Circles.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "circles.open", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "circles.members") && asksCircleMembers(text.trim())) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "A Creator can't open Circles, so the member list isn't here. An Owner or Admin opens the Circle to see who is in it.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "circles.members", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "team.canAddMembers") && /\badd them as an admin\b/i.test(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "No. A Creator can't add team members or change someone to Admin. An Owner or Admin does that on Team.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "team.canAddMembers", slots: {} },
    };
  }
  // "What role does Sam have?": names sway the matcher, so a person's role is read from the words themselves
  const roleOf = !running ? text.trim().match(ROLE_OF_PERSON) : null;
  const roleEntry = byId.get("team.role");
  const roleName = roleOf?.[1] ?? roleOf?.[2];
  if (roleName && roleEntry?.answer && !/^(?:i|me|my|you)$/i.test(roleName)) {
    return answer(roleEntry, { member: roleName }, ctx, rotation.answers, usingFallback, text);
  }
  // "Add Lebo Dlamini as an Admin": a new team member, named and with a role, before the matcher hears the name
  const addAs = !running ? text.trim().match(ADD_PERSON_AS_ROLE) : null;
  const addEntry = byId.get("team.addMember");
  if (addAs && addEntry && hasHandoff(addEntry) && !SOUNDS_LIKE_A_GROUP.test(addAs[1])) {
    return startHandoff(addEntry, { member: addAs[1], role: addAs[2] }, ctx, text);
  }
  if (!running && !available.some((entry) => entry.id === "troubleshooting.invitedParentMissing") && /invited (?:them|a parent)\b.*\b(?:still can'?t log in|(?:are|is) not on the list|aren'?t on the list)/i.test(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "An invite has to be accepted in the app before they join. Users isn't in your sidebar, so you can't check it here. An Admin looks under Pending Invitations and checks the email or phone.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "troubleshooting.invitedParentMissing", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "circles.empty") && /\bcircles?\b/i.test(text)) {
    const home = getSidebarItem("home");
    if (/\bempty\b/i.test(text)) {
      return {
        kind: "answer",
        text: "Circles isn't in your sidebar, so none of the member lists are here. An Owner or Admin opens Circles.",
        link: { label: `Open ${home.label}`, href: home.path },
        asked: { id: "circles.empty", slots: {} },
      };
    }
    if (/\b(?:how many people|each circle|member counts?)\b/i.test(text)) {
      return {
        kind: "answer",
        text: "Circles isn't in your sidebar, so the member counts aren't here. An Owner or Admin opens Circles.",
        link: { label: `Open ${home.label}`, href: home.path },
        asked: { id: "circles.sizes", slots: {} },
      };
    }
  }

  // "What's in Drive?" lists files. A Notice titled "Drive folder: …" is not that question.
  if (!running && ASKS_FOR_THE_FILE_LIST.test(text)) {
    const files = byId.get("drive.recent");
    if (files?.answer) return answer(files, {}, ctx, rotation.answers, false, text);
  }

  // "make it urgent and attach the form and schedule it": three steps, not one guess at the first.
  const steps = NOTICE_STEPS.filter(([pattern]) => pattern.test(text));
  if (steps.length >= 2 && /\band\b/i.test(text) && !/^(?:how|what|where|why)\b/i.test(text.trim())) {
    const said = `Those are separate steps on the same Notice: ${steps.map(([, step]) => step).join(", then ")}. Nothing is sent until you press Publish.`;
    const asked = { id: "unsupported.severalThings", slots: {} };
    return running
      ? { kind: "task", text: `${said} ${carryOnPrompt(running.task)}`, choices: [], task: running, asked }
      : { kind: "howTo", text: said, actions: [], asked };
  }

  // "I mean Grade 2", "Grade R was the circle": who it goes to, said back, and where it's chosen.
  const audience = correctedAudience(text);
  if (audience) {
    const asked = { id: running?.task === "event" ? "create.eventAudienceHowTo" : "create.audienceHowTo", slots: {} };
    let said: string;
    if (audience === "same") {
      said = "I don't keep who it went to last time. Which Circle was it? On Review, under Who should receive this?, choose that Circle again.";
    } else {
      const found = await loadCircles(ctx)
        .then((circles) => resolveCircle(circles, audience))
        .catch(() => null);
      const name = found && "circle" in found && found.circle ? found.circle.name : audience;
      const exists = Boolean(found && "circle" in found && found.circle);
      said = exists
        ? `${name} it is. On Review, under Who should receive this?, choose the ${name} Circle. Nothing is sent until you press Publish.`
        : `${name} it is. On Review, under Who should receive this?, choose the ${name} Circle. I don't see a Circle called ${name} yet; an Owner or Admin creates it.`;
    }
    return running
      ? { kind: "task", text: `${said} ${carryOnPrompt(running.task)}`, choices: [], task: running, asked }
      : { kind: "howTo", text: said, actions: [], asked };
  }

  // "What kind of notice?" answered with a title ("Spelling test tomorrow"): an Information notice, with that title.
  // Not a kind, a question, a cancel or another request; those are read as they are.
  if (
    !running &&
    previous?.id === "create.offerToSend" &&
    !noticeKindStart(text) &&
    !looksLikeQuestion(text) &&
    !cancelKindOf(text) &&
    !readWorkRequest(text) &&
    text.trim().split(/\s+/).length <= 8 &&
    available.some((entry) => entry.id === "create.notice")
  ) {
    const title = text.trim().replace(/[.!]+$/, "");
    const started = await taskTurn((org) => startTask("notice", "send a notice", "create.notice", org, { said: { title } }), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return { ...started, startedBy: "create.notice", text: `An Information notice called “${title}”. ${started.text.replace(/^Sure\.\s*/, "")}` };
    }
  }

  // "What kind of notice?" answered with "Event": that kind starts, before "event" is heard as "which Event?".
  if (!running && (previous?.id === "create.offerToSend" || previous?.id === "create.scheduleOffer") && text.trim().split(/\s+/).length <= 3) {
    const kind = noticeKindStart(text);
    if (kind && available.some((entry) => entry.id === kind.startedBy)) {
      const started = await taskTurn((org) => startTask(kind.task, kind.request, kind.startedBy, org), {
        ctx,
        available,
        matcher,
        rotation,
      });
      if (started?.kind === "task") {
        const said = /\binformation\b/i.test(text)
          ? "An Information notice. Nothing is sent until you press Publish."
          : /\burgent\b/i.test(text)
            ? "An Urgent notice. Nothing is sent until you press Publish."
            : KIND_SAID[kind.task];
        return { ...started, startedBy: kind.startedBy, text: `${said} ${started.text.replace(/^Sure\.\s*/, "")}` };
      }
      if (started) return started;
    }
  }

  const lessonHit = lessonAsReply(text, previous?.id ?? running?.startedById, rotation.answers);
  // A lesson about the Users page is no answer for someone who can't open it: the role's own reply is below.
  const lessonId = lessonHit && "asked" in lessonHit ? lessonHit.asked?.id : undefined;
  const lesson = lessonId?.startsWith("users.") && !byId.has(lessonId) ? null : lessonHit;
  const questionShaped =
    /[?]/.test(text) ||
    /^(how|what|whats|where|when|who|why|can|could|do|does|did|is|are|should|only|not|and|or|if|so|the|a|i|im|ok|gosh|hello|hi|parents|most|they|login|photos|once|after|back|use|put|print|add|invite|make|wheres|theres|pin|first|weekly|staff|quick|remind|own|\d)/i.test(
      text.trim()
    );
  const fromPhrase = lesson != null && "asked" in lesson && lesson.asked?.id.startsWith("lesson.") === true;
  // Mid-task, only a real question leaves the task for a lesson: "koeksisters, cupcakes, brownies" are the Poll's choices
  const suppliesPollQuestion = running?.task === "poll" && running.phase === "collecting" && running.asked === "question" && !fromPhrase;
  const lessonIsAQuestion = running
    ? looksLikeQuestion(text) && !suppliesPollQuestion
    : questionShaped || text.trim().split(/\s+/).length >= 4;
  // A course phrase said word for word answers now. A course rule (a pattern) only answers when nothing in the
  // catalogue fits clearly: "What were the poll results for the term dates?" is that Notice's results, not the steps.
  if (lesson && (fromPhrase || exactHits.length === 0) && (fromPhrase || (running && lessonIsAQuestion))) {
    if (!running) return lesson;
    // "can I edit it after it's sent?" half-way through: answered, and the draft waits on its step
    const kept = lesson.kind === "answer" ? await alongside(lesson.text, lesson.asked) : null;
    return kept ?? { ...lesson, dropTask: true };
  }
  const lessonIfNothingFits = lesson && exactHits.length === 0 && !running ? lesson : null;

  const noticed =
    !running && !followUpAspect(text) && exactHits.length === 0
      ? await noticeFromConversation(text, previous, byId.get("notices.when"), ctx, rotation.answers)
      : null;

  const resolveAspect = async (): Promise<FollowUpAspect | null> => {
    // "im confused" is about the chat, not a follow-up asking for the last answer again
    if (smallTalkReply(text) === FRUSTRATED_REPLY) return null;
    const exact = followUpAspect(text);
    if (exact) return exact;
    // A paraphrase only follows something already said. "Who are you?" at the start is a new question.
    // An example phrasing is that question, not a vague "who" or "when".
    if ((!running && !previous) || exactHits.length > 0 || noticed) return null;
    // A short answer to a task's question is that answer: "5:30pm" is the start time, not "what time is that?"
    if (running?.phase === "collecting" && running.asked) return null;
    if (!matcher?.scorePhrases || !isShortFollowUp(text)) return null;
    // "Which events are on Thursday?" says its own day: a new question, however much it sounds like "which event is that"
    if (findDateRange(text)) return null;
    try {
      const scores = await matcher.scorePhrases(
        text,
        FUZZY_ASPECT_PHRASES.map((item) => item.phrase)
      );
      return aspectFromScores(scores, matcher.thresholds.answer, matcher.thresholds.clearLead);
    } catch (error) {
      console.error("[Ask Noticeboard] Couldn't match a short follow-up", error);
      return null;
    }
  };
  const aspect = await resolveAspect();
  // "k when", "the event", "where tho": still about the last answer, and never the title of a task.
  if (aspect) {
    if (previous?.id === "introduction" && !running) {
      const home = getSidebarItem("home");
      const asked = { id: "introduction", slots: {} };
      const link = { label: `Open ${home.label}`, href: home.path };
      if (aspect === "repeat") {
        const intro = introductionFor(available);
        return { kind: "introduction", ...intro, asked: { ...asked, offered: intro.questions } };
      }
      const textFor =
        aspect === "when"
          ? noTimeLine("introduction")
          : aspect === "event"
            ? eventLine("introduction")
            : aspect === "where"
              ? "Ask which page you mean. You can open Home, Create, Notices, Calendar, Drive, Help Center and Account."
              : "Ask who you mean. This was about what I can help with, not one Event's audience.";
      return { kind: "answer", text: textFor, link, asked };
    }
    const anchorId = running?.startedById ?? previous?.id;
    const anchor = anchorId ? byId.get(anchorId) : undefined;
    const canReanswer = Boolean(anchor && (hasHowTo(anchor) || anchor.answer || handoffReminder(anchor.id)));
    if (anchor && (running || previous) && (aspect !== "repeat" || canReanswer)) {
      const asked: AskedQuestion = {
        id: anchor.id,
        slots: previous?.slots ?? {},
        ...(previous?.subject ? { subject: previous.subject } : {}),
        ...(previous?.mentions?.length ? { mentions: previous.mentions } : {}),
        ...(running?.startedByText || previous?.startedByText
          ? { startedByText: running?.startedByText ?? previous?.startedByText }
          : {}),
      };
      return followUpReply(aspect, anchor, ctx, rotation, usingFallback, asked, !running);
    }
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "Which one do you mean? Which Event do you mean? There is no day or time until you say which.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "home.upcomingEvents", slots: {} },
    };
  }

  // A newly named Notice is a new topic, including after a reminder handover.
  if (noticed && /^(?:and\s+)?what\s+about\b/i.test(text.trim())) return noticed;

  // "this is frustrating" and "that means nothing to me" stay on the last answer.
  if (!running && smallTalkReply(text) === FRUSTRATED_REPLY) {
    const anchor = previous ? byId.get(previous.id) : undefined;
    // "hmm" after the RSVP steps is a pause, not a request to hear those steps again.
    // After "still don't see it", the same answer a third time doesn't help either.
    if (previous && anchor?.answer && !previous.recoveryAttempts) {
      const again = await reanswer(anchor, ctx, rotation, usingFallback, previous);
      if (again && again.kind !== "howTo" && "text" in again && again.text) return { ...again, text: `Sorry. ${again.text}` };
    }
    return { kind: "smallTalk", text: FRUSTRATED_REPLY };
  }

  if (running && isStuckPhrase(text)) {
    const anchor = running.startedById ? byId.get(running.startedById) : undefined;
    if (anchor) {
      const asked: AskedQuestion = {
        id: anchor.id,
        slots: {},
        ...(running.startedByText ? { startedByText: running.startedByText } : {}),
      };
      const recovered = await recoveryReply(asked, anchor, ctx);
      return { ...recovered, dropTask: true };
    }
  }

  if (/\b(?:check|see|look at|open)\s+(?:their|a parent'?s|the parent'?s|his|her)\s+(?:phone|device|screen|app)\b/i.test(text)) {
    const reply: AssistantReply = { kind: "cannot", text: CANT_SEE_THEIR_PHONE };
    return running ? { ...reply, dropTask: true } : reply;
  }

  // "Netball, Cricket, Soccer" as a Poll's choices: the answer to the step, not off-topic
  const answersAWordStep =
    running?.phase === "collecting" && ["choices", "title", "message", "question", "place"].includes(running.asked ?? "") && (!looksLikeQuestion(text) || (running.task === "poll" && running.asked === "question" && /^(?:should|would)\b/i.test(text)));
  // "Find it", "Delete that", "The other Alex", "Build our website", with nothing open or named to go on
  // "Open it", "take me there" just after "where are my notices?": the page that answer was about
  const answeredOn = !running && previous && !previous.subject && /\.open$/.test(previous.id) ? byId.get(previous.id) : undefined;
  if (answeredOn && /^(?:please\s+)?(?:open\s+(?:it|that|there|that\s+page)|take\s+me\s+there|go\s+there)(?:\s+please)?[.!]*$/i.test(text.trim())) {
    const link = linkFor(answeredOn, ctx);
    return { kind: "answer", text: `Opening ${link.label.replace(/^Open\s+/, "")}.`, link, asked: previous!, openNow: true };
  }
  // "Delete that" just after a named Notice: how a sent one is taken down, rather than a guess
  if (!running && previous?.subject && /^(?:please\s+)?(?:delete|remove|take\s+down|unsend)\s+(?:it|that|this)(?:\s+one)?[.!?]*$/i.test(text.trim())) {
    const deleting = byId.get("unsupported.deleteNotice");
    if (deleting?.answer) return answer(deleting, {}, ctx, rotation.answers, usingFallback, text);
  }
  if (!running && !previous && exactHits.length === 0) {
    const examples = new Set(available.flatMap((entry) => entry.examples));
    const vague = vagueRequestReply(text, (question) => examples.has(question));
    if (vague?.kind === "didYouMean") return { ...vague, asked: withOffered(previous, vague.questions) };
    if (vague) return vague;
  }

  const unrelatedRequest = /^(?:(?:can|could) you\s+)?(?:recommend|suggest|find)\s+(?:me\s+)?(?:a\s+)?(?:good\s+)?(?:movie|film|restaurant|recipe)\b|^(?:(?:can|could) you\s+)?(?:order|book)\s+(?:me\s+)?(?:a\s+)?(?:pizza|taxi|flight)\b/i.test(text.trim());
  if (exactHits.length === 0 && (unrelatedRequest || (!answersAWordStep && soundsOffTopic(text)))) {
    const reply = offTopicReply(rotation.offTopic);
    return { kind: "offTopic", text: open ? `${reply} ${carryOnPrompt(open.task)}` : reply };
  }

  // "when is the debs ball" while the title is asked: a question about a Notice they have, answered on the side,
  // and the step still waits. A title that only mentions a Notice ("Debs Ball tickets") is still a title.
  if (open?.phase === "collecting" && !(open.task === "poll" && open.asked === "question" && /^(?:when|where|what|which|who|how)\s+(?:should|would)\b/i.test(text)) && ["title", "message", "question"].includes(open.asked ?? "") && /^(?:when|what\s+(?:time|day|date)|where|who)\b/i.test(text.trim())) {
    const lookup = await replyTo(text, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback });
    if (lookup.kind === "answer" && lookup.asked.id === "notices.when") {
      const kept = await alongside(lookup.text, lookup.asked);
      if (kept) return kept;
    }
  }

  if (open?.phase === "collecting" && NOTICES_FOR_A_CIRCLE.test(text.trim())) {
    const lookup=await replyTo(text,{available,matcher,ctx,rotation,previous:null,task:null,usingFallback});
    if (lookup.kind === "answer" && lookup.asked.id === "notices.toCircle") {
      const kept=await alongside(lookup.text,lookup.asked);
      if(kept)return kept;
    }
  }

  if (running) {
    // A new question that is one of the catalogue's own examples is not the title or the
    // day of the task already open. Answer it on its own, and leave that task.
    // "Staff only" is a catalogue question too, but during an Event it's who the Event is for
    const taken = leavesForAnExactExample()
      ? await taskTurn((org) => (takesAsADetail(running, text, org) ? continueTask(running, text, org) : null), {
          ctx,
          available,
          matcher,
          rotation,
        })
      : null;
    if (taken) return taken;
    if (leavesForAnExactExample()) {
      const fresh = await replyTo(text, { available, matcher, ctx, rotation, previous: null, task: null, usingFallback });
      // "wait, how many users do we have?" is answered on the side: the task waits for its next answer.
      // Starting something else, or opening a screen, leaves it.
      if (fresh.kind === "answer" || fresh.kind === "howTo" || fresh.kind === "loadFailed") {
        return { ...fresh, text: `${fresh.text} ${carryOnPrompt(running.task)}` };
      }
      return { ...fresh, dropTask: true };
    }
    // "I don't see it" was already answered above. Anything else is the task's next step.
    const helpSeeking = /^(?:i need to|help me|show me how(?: to)?)\b/i.test(text.trim());
    if (helpSeeking && matcher) {
      const rewritten = text.replace(/^(?:i need to|help me|show me how(?: to)?)\b/i, "How do I");
      const answered = await sideQuestionAnswer(rewritten, byId, matcher, ctx, rotation.answers);
      if (answered && answered.id !== running.startedById) {
        return {
          kind: "task",
          text: `${answered.text} ${carryOnPrompt(running.task)}`,
          choices: [],
          task: running,
          asked: { id: answered.id, slots: {} },
        };
      }
    }
    // "carry on with my notice" asks for the step it was on, not a Circle called that
    if (RESUME_TASK.test(text.trim())) {
      const resumed = await resumeTaskReply(running, { ctx, available, matcher, rotation });
      if (resumed) return resumed;
    }
    const afterResume = text.trim().match(RESUME_THEN)?.[1];
    if (afterResume) return replyTo(afterResume, { available, matcher, ctx, rotation, previous, task: running, usingFallback });
    const switchTo = text.trim().match(SWITCH_KIND)?.[1].toLowerCase() as "poll" | "event" | "notice" | undefined;
    if (switchTo && switchTo !== running.task) {
      const startedBy = switchTo === "event" ? "calendar.createEvent" : "create.notice";
      if (available.some((entry) => entry.id === startedBy)) {
        const started = await taskTurn((org) => startTask(switchTo, `create a ${switchTo}`, startedBy, org), {
          ctx,
          available,
          matcher,
          rotation,
        });
        if (started?.kind === "task") return { ...started, startedBy, text: `${KIND_SAID[switchTo]} ${started.text.replace(/^Sure\.\s*/, "")}` };
        if (started) return { ...started, dropTask: true };
      }
    }
    if (running.task === "notice" && /^just an info(?:rmation)? one$/i.test(text.trim())) {
      return {
        kind: "task",
        text: "An Information notice. What's the title?",
        choices: [],
        task: { ...running, asked: "title", phase: "collecting" },
        startedBy: "create.notice",
      };
    }
    // The question this message answers; a correction to a summary ("change the title to …") answers none
    const answered = running.phase === "collecting" ? running.asked : null;
    // The words that go into the Notice are kept as typed: "Please bring…" keeps its "Please"
    const writing = answered === "message" || answered === "title";
    const said = writing && !/^(?:how|what|when|which|who|where|why)\b/i.test(text) && cancelKindOf(spoken.trim()) === null ? spoken.trim() : text;
    const reply = await taskTurn((org) => continueTask(running, /^(?:change what it says to|(?:change|set|make) (?:the )?(?:message|body|text) to)\b/i.test(spoken.trim()) ? spoken : said, org), { ctx, available, matcher, rotation });
    if (reply) {
      if (reply.kind === "cancelled") return reply;
      const titleSaid = text.match(/^(?:the )?title is (.+)$/i);
      if (reply.kind === "task" && titleSaid && /what should it say/i.test(reply.text)) {
        const title = titleSaid[1].replace(/[?.!]+$/, "").trim();
        return { ...reply, text: `Title: ${title}. What should the message say?` };
      }
      const named = text.match(/^(?:only|just|for|to)\s+(.+)$/i);
      const who = named?.[1].replace(/[?.!]+$/, "").trim();
      if (reply.kind === "task" && who && !reply.text.toLowerCase().includes(who.toLowerCase())) {
        const circle = who.replace(/\b\w/g, (letter) => letter.toUpperCase());
        return echoSlot({ ...reply, text: `${circle} it is. Nothing is sent until you review it. ${reply.text}` }, text, answered);
      }
      return echoSlot(reply, text, answered);
    }
    if (running.phase === "confirming") {
      // Neither a correction nor a confirmation: answered on its own terms, with the summary
      // kept and an offer to carry on, rather than dropping the task silently.
      const [ordinary, card] = await Promise.all([
        replyTo(text, { available, matcher, ctx, rotation, previous, usingFallback }),
        resumeTaskReply(running, { ctx, available, matcher, rotation }),
      ]);
      if (ordinary.kind === "task" || ordinary.kind === "handoff" || ordinary.kind === "writingRequest") {
        return ordinary.kind === "task" ? ordinary : { ...ordinary, dropTask: true };
      }
      const withPrompt = { text: `${ordinary.text} ${carryOnPrompt(running.task)}` };
      // "Did you mean one of these?" keeps its suggestions; the draft stays open behind it
      if ((ordinary.kind === "didYouMean" || ordinary.kind === "unanswered") && ordinary.questions.length > 0) {
        return { ...ordinary, ...withPrompt };
      }
      return card ? { ...card, ...withPrompt, ...("asked" in ordinary && ordinary.asked ? {asked:ordinary.asked} : {}) } : { ...ordinary, ...withPrompt };
    }
  }
  const smallTalk = smallTalkReply(text);
  // "when" on its own is small talk only at the start. After an answer, it means "when is that?"
  // "how?" after steps were given: those steps again, further down
  const stepsAgain = Boolean(previous && STEPS_AGAIN.test(text));
  if (smallTalk && !(previous && isBareFollowUp(text)) && !stepsAgain) {
    return running ? { kind: "smallTalk", text: smallTalk, dropTask: true } : { kind: "smallTalk", text: smallTalk };
  }
  if (isIntroductionRequest(text) || (coreOf && isIntroductionRequest(coreOf))) {
    const intro = introductionFor(available);
    const spin = rotation.answers + ((rotation as { voice?: number }).voice ?? 0);
    const body = coreOf && !isIntroductionRequest(text) ? withVoice(intro.text, text, spin) : intro.text;
    return { kind: "introduction", ...intro, text: body, asked: { id: "introduction", slots: {}, offered: intro.questions } };
  }
  // Never "I can't answer that yet", and the text they pasted stays here
  if (isTranslationRequest(text)) {
    const asked = { id: "concepts.nova", slots: {} };
    return byId.has("create.notice")
      ? { kind: "writingRequest", text: TRANSLATION_REQUEST_REPLY, to: toNewNotice(), asked }
      : { kind: "writingRequest", text: TRANSLATION_REQUEST_REPLY_NO_WIZARD, asked };
  }
  if (isWritingRequest(text)) {
    const asked = { id: "concepts.nova", slots: {} };
    return byId.has("create.notice")
      ? { kind: "writingRequest", text: WRITING_REQUEST_REPLY, to: toNewNotice(), asked }
      : { kind: "writingRequest", text: WRITING_REQUEST_REPLY_NO_WIZARD, asked };
  }

  const previousEntry = previous && byId.get(previous.id);

  // "That's not what I meant" after an answer, when no step is waiting for a value.
  // Repeat that answer so the correction stays on the topic. A step that is waiting
  // still cancels, below.
  if (previous && previousEntry && !running && !previous.awaiting && cancelKindOf(text) === "notWhatIMeant") {
    const again = await reanswer(previousEntry, ctx, rotation, usingFallback, previous);
    if (again && "text" in again && again.text) return { ...again, text: `Sorry. ${again.text}` };
  }

  // "so when" before anything has been answered: ask which thing, instead of "I can't answer that yet".
  if (!previousEntry && isBareFollowUp(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "Which one do you mean? There is no day or time until you say which. Ask what's coming up, or how to send a Notice.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "home.upcomingEvents", slots: {} },
    };
  }

  if (!running && cancelKindOf(text) === "cancel") {
    return { kind: "cancelled", text: CANCELLED_REPLY, choices: [] };
  }
  // "That's not what I meant" with no answer, task or waiting step to go back to
  if (!running && !previous?.awaiting && cancelKindOf(text) === "notWhatIMeant") {
    return { kind: "smallTalk", text: "Sorry about that. Nothing was created. What did you mean?" };
  }

  const asksHow = /^(?:how|where|what|whats|why|which|who)\b/i.test(text.trim());
  const helpShape = /^(?:i need to|help me|show me how|can you show me how)\b/i.test(text.trim());
  // "Urgent, but I haven't decided what to send": nothing to start yet
  const notUrgent = /\b(?:don'?t|do not|not)\b[^.?!]*\burgent\b/i.test(text) || /\b(?:not|haven'?t|have not)\s+(?:yet\s+)?(?:decided|sure)\b/i.test(text);
  // "Find urgent notices", "show me the urgent ones": looking for them, not making one
  const looksUpUrgent = !/\b(?:send|create|make|write|draft|prepare)\b/i.test(text) && (/\b(?:find|show|list|look\s+up|let\s+me\s+see|see|view|check|search)\b[^.?!]*\burgent\b/i.test(text) || /\burgent\s+(?:notices|posts|ones)\b/i.test(text));
  // "Make it urgent" with nothing open: there's no "it" yet, so nothing is started on a guess
  if (!running && !notUrgent && /^(?:please\s+)?(?:make|mark|set|turn)\s+(?:it|this|that)(?:\s+one)?\s+(?:as\s+)?urgent\b/i.test(text.trim())) {
    return {
      kind: "howTo",
      text: "There's no Notice open here to make urgent. To start one, say “Send an urgent notice”. On Create, turn on Mark as urgent notice on the first step. A sent Notice can't be changed to urgent.",
      actions: [
        { kind: "start", label: "Send an urgent notice", ask: "Send an urgent notice" },
        { kind: "open", label: "Open Create", to: toNewNotice() },
      ],
      asked: { id: "create.urgentHowTo", slots: {} },
    };
  }
  if (!running && !asksHow && !helpShape && !notUrgent && !looksUpUrgent && exactHits.length === 0 && /\burgent\b/i.test(text) && /\b(?:send|need|create|make)\b/i.test(text)) {
    const started = await taskTurn((org) => startTask("notice", text, "create.notice", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return {
        ...started,
        startedBy: "create.notice",
        text: `I'll prepare an Urgent notice. You review it and press Publish. ${started.text.replace(/^Sure\.\s*/, "")}`,
      };
    }
  }

  if (
    !running &&
    !asksHow &&
    exactHits.length === 0 &&
    (/\b(?:create|add|make|plan)\s+an?\s+event\b/i.test(text) || /\badd\b.+\bto\s+(?:the|our|my)\s+calendar\b/i.test(text)) &&
    // "did we already make an event for it?" asks about one, it doesn't ask for one
    !/\b(?:did|have|has)\s+(?:we|i|you|they|someone)\s+(?:already\s+)?(?:create|created|add|added|make|made|plan|planned)\b/i.test(text) &&
    !/\bonly for staff|staff only|internal team|just for the team\b/i.test(text)
  ) {
    const started = await taskTurn((org) => startTask("event", text, "calendar.createEvent", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") return { ...started, startedBy: "calendar.createEvent" };
  }

  if (!running && (/^(?:the\s+)?choices\s+(?:are|were)\b/i.test(text.trim()) || /^choices\s+(?!for\b|about\b)[^?]+$/i.test(text.trim()))) {
    const started = await taskTurn((org) => startTask("poll", text, "create.notice", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return {
        ...started,
        startedBy: "create.notice",
        text: `I'll start a Poll. Nothing is published until you press Publish. ${started.text.replace(/^Sure\.\s*/, "")}`,
      };
    }
  }

  // "can I make a poll anonymous?" asks what a Poll can be, so it's answered below, not started
  const asksWhatAPollCanBe = /\bpolls?\s+(?:anonymous|private|secret|public|editable|multiple[\s-]choice)\b/i.test(text);
  if (!running && !asksHow && !asksWhatAPollCanBe && /\bpoll\b/i.test(text) && /\b(?:can we|do a|make a|create|start|quick)\b/i.test(text)) {
    const started = await taskTurn((org) => startTask("poll", text, "create.notice", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return {
        ...started,
        startedBy: "create.notice",
        text: `I'll start a Poll. Nothing is sent until you press Publish. ${started.text.replace(/^Sure\.\s*/, "")}`,
      };
    }
  }

  if (!running && /^no i meant a notice$/.test(text.trim())) {
    const started = await taskTurn((org) => startTask("notice", "send a notice", "create.notice", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return { ...started, startedBy: "create.notice", text: `A Notice is what you send. I'll start one. ${started.text.replace(/^Sure\.\s*/, "")}` };
    }
  }

  if (!running && /^just an info(?:rmation)? one$/.test(text.trim())) {
    const started = await taskTurn((org) => startTask("notice", "send an information notice", "create.notice", org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    if (started?.kind === "task") {
      return { ...started, startedBy: "create.notice", text: `An Information notice. ${started.text.replace(/^Sure\.\s*/, "")}` };
    }
  }

  const followed = await spokenTurn(text, previous, byId, ctx, rotation.answers);
  if (followed) return followed;

  // "I don't see it", "where is that?": stuck on the last answer, checked before anything
  // that would otherwise read it as a follow-up value or let it fall to the off-topic reply.
  if (previous && previousEntry && isStuckPhrase(text)) {
    return recoveryReply(previous, previousEntry, ctx);
  }

  // "Can you show me where to do that?" means the steps just given, not a new page.
  if (
    previous &&
    previousEntry &&
    hasHowTo(previousEntry) &&
    STEPS_AGAIN.test(text)
  ) {
    return answerHowTo(previousEntry, ctx, previous.startedByText || text);
  }
  // "how?", "how do I get there?" after an answer: the page it's on, with a button that opens it
  if (previous && previousEntry && !running && HOW_DO_I_GET_THERE.test(text.trim())) {
    // The page that answer's own button opened ("How do I invite a parent?" is on Users), else its sidebar page
    const again = previousEntry.answer ? await answer(previousEntry, previous.slots, ctx, rotation.answers, usingFallback, text) : null;
    const own = again?.kind === "answer" && again.link.href !== "/" ? again.link : null;
    const link = own ?? linkFor(previousEntry, ctx);
    const page = link.label.replace(/^Open\s+/, "");
    const where = own && /^the\s/i.test(page) ? `Press the button to open ${page}.` : own ? `It's on ${page}. Press the button to open it.` : `It's on ${page}, in the sidebar on the left. Press the button to open it.`;
    return { ...pointed(previousEntry, where, previous, ctx), link } as AssistantReply;
  }
  // "the one I made isn't there" after the Calendar: the Event that isn't showing
  if (!running && previous?.id.startsWith("calendar.") && /\b(?:one|event|thing)\s+i\s+(?:made|created|added|sent)\b.*\b(?:isn'?t|not|missing|gone)\b/i.test(text)) {
    const missing = byId.get("troubleshooting.eventNotOnCalendar");
    if (missing && hasHowTo(missing)) return answerHowTo(missing, ctx, text);
    if (missing?.answer) return answer(missing, {}, ctx, rotation.answers, false, text);
  }
  // "ok how do I do it myself then?" after the image notice steps: those steps again
  if (previous?.id === IMAGE_NOTICE_HOW_TO && STEPS_AGAIN.test(text)) return imageNoticeReply();
  // "will parents be told?" after cancelling a sent Event was explained: only by the follow-up Notice
  if (previous?.id === "create.cancelEventHowTo" && PARENTS_TOLD.test(text)) {
    return {
      kind: "howTo",
      text: "Not on their own. Parents only hear about it when you send the follow-up Notice, from Duplicate or the Cancelled / weather Template. A scheduled Event cancelled before it went out was never sent, so there's nothing to tell them.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
      asked: { id: previous.id, slots: {} },
    };
  }
  // "what do parents see?" after claim lists were explained: what a claim list looks like to them
  if (previous?.id.startsWith("create.claim") && PARENTS_SEE.test(text)) {
    return {
      kind: "howTo",
      text: "Parents get the claim list in the app, like any Notice, with the items still free to claim. Each family claims one item, and once it's taken it disappears for everyone else.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
      asked: { id: previous.id, slots: {} },
    };
  }
  // "can you set it up for me?" after a claim list was explained: still made on Create
  if (previous?.id === CLAIM_LIST_HOW_TO && (asksToMakeItForThem(text) || /\bset\s+(?:it|one)\s+up\b/i.test(text))) return claimListReply();
  if (previous?.id.startsWith("create.claim") && MAKE_THE_LIST_HERE.test(text)) return claimListReply();

  // After "I don't see it", the next message is a new question. It is not the name or the day
  // the handoff was waiting for.
  if (previous && previousEntry && hasHandoff(previousEntry) && !previous.recoveryAttempts && !leavesForAnExactExample()) {
    const cancel = previous.awaiting && cancelKindOf(text.trim());
    if (cancel) {
      const choices =
        cancel === "notWhatIMeant" && matcher
          ? await otherMatches(previous.startedByText ?? text, previous.id, byId, matcher, ctx)
          : [];
      return { kind: "cancelled", text: CANCELLED_REPLY, choices };
    }
    const change: SlotValues | null = previous.awaiting
      ? (() => {
          const slot = previous.awaiting as SlotName;
          // A question is not the name it was waiting for. "Who do you mean?" asks about this step, so
          // it's asked again; anything else is a new question ("Which role am I?", "Who is already on
          // the team?"). An award title is always asked again.
          // "Make them a Creator, not an Admin" before the name: the role is kept, and the name asked again.
          if (followUpRejects(slot, withoutEndPunctuation(text))) {
            const role = slot === "member" && !looksLikeQuestion(text) ? roleIn(text) : null;
            if (role) return { role };
            if (slot === "member" && ABOUT_THIS_STEP.test(text.trim())) return {};
            return slot === "user" || slot === "member" ? null : {};
          }
          const value = readDirectFollowUp(slot, text);
          return value === undefined ? {} : slotValue(slot, value);
        })()
      : previousEntry.followUpSlot
        ? (() => {
            const slot = previousEntry.followUpSlot as SlotName;
            const value = readBareFollowUp(slot, text);
            return value === undefined ? null : slotValue(slot, value);
          })()
        : readFollowUp(text);
    if (change) {
      // "What kind of notice?" answered with Information, Event, Poll or Urgent starts that guided task.
      // Anything else is a new question, matched below.
      if (previous.id === "create.offerToSend" || previous.id === "create.scheduleOffer") {
        const kind = noticeKindStart(text);
        if (kind && available.some((entry) => entry.id === kind.startedBy)) {
          const started = await taskTurn((org) => startTask(kind.task, kind.request, kind.startedBy, org), {
            ctx,
            available,
            matcher,
            rotation,
          });
          if (started?.kind === "task") return { ...started, startedBy: kind.startedBy };
          if (started) return started;
        }
      } else {
        const reply = await startHandoff(previousEntry, { ...previous.slots, ...change }, ctx, previous.startedByText ?? text);
        if (!missedTheTarget(reply)) return reply;
      }
    }
  }

  // A date range named in a bare follow-up is checked first, and answered straight away: unlike a
  // name, a date range that doesn't resolve ("this term") is still worth saying so, not a mismatch.
  if (previous && previousEntry?.answer && previousEntry.slots?.dateRange !== undefined) {
    const dateRange = readBareFollowUp("dateRange", text);
    if (dateRange !== undefined) {
      return answer(previousEntry, { ...previous.slots, ...slotValue("dateRange", dateRange) }, ctx, rotation.answers, false, text);
    }
  }

  // "Want to see this week?" answered with yes: the same count, for this week.
  if (
    previous?.id === "notices.sentOn" &&
    previousEntry?.answer &&
    (previous.slots.dateRange ?? "allTime") === "allTime" &&
    AGREES_TO_THIS_WEEK.test(text.trim())
  ) {
    return answer(previousEntry, { ...previous.slots, dateRange: "thisWeek" }, ctx, rotation.answers, false, text);
  }

  // An answer's follow-up question ("Which Circle?") answered, or a bare "same for Grade 3"
  if (previous && previousEntry?.answer && !leavesForAnExactExample()) {
    const slot = previous.awaiting ?? namedSlotOf(previousEntry);
    const rejected = Boolean(previous.awaiting && followUpRejects(previous.awaiting, withoutEndPunctuation(text)));
    const value =
      !rejected && slot ? (previous.awaiting ? readDirectFollowUp(slot, text) : readBareFollowUp(slot, text)) : undefined;
    if (slot && value) {
      const reply = await answer(previousEntry, { ...previous.slots, ...slotValue(slot, value) }, ctx, rotation.answers, false, text);
      // Asked again for the same detail: the message wasn't an answer to it, so treat it as a new question
      const askedAgain = reply.kind === "askSlot" && reply.reason === "missing";
      if (!missedTheTarget(reply) && !askedAgain) return reply;
    }
  }

  // "Their email is parent@example.com" starts the invite, with that address filled in.
  if (!running && /\b(?:their|the|her|his)\s+e-?mail\s+is\s+\S+@\S+/i.test(text)) {
    const address = text.match(/[^\s@]+@[^\s@]+\.[^\s@]+/)?.[0];
    const invite = available.find((entry) => entry.id === "users.invite");
    if (address && invite && hasHandoff(invite)) return startHandoff(invite, { contact: address }, ctx, text);
    if (address) {
      return {
        kind: "howTo",
        text: `No. You can't open Users, so nothing is invited for ${address}. An Owner or Admin presses Add user.`,
        actions: [],
        asked: { id: "users.invite", slots: { contact: address } },
      };
    }
  }
  if (!running && !available.some((entry) => entry.id === "users.remove") && /\bremove\b/i.test(text) && /\bparent\b/i.test(text)) {
    return {
      kind: "cannot",
      text: "No. You can't open Users, so nobody is removed from here. An Owner or Admin presses Remove User.",
      asked: { id: "users.remove", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "users.unjoined") && /\bjoined\b/i.test(text)) {
    return {
      kind: "howTo",
      text: "You can't open Users. Pending invitations, for people who haven't joined yet, are there for an Owner or Admin.",
      actions: [],
      asked: { id: "users.unjoined", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "users.joinRequests") && /join request/i.test(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "No. You can't open Users, so there are no Join requests to see here. Nothing is approved until an Owner or Admin presses Add user on each request.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "users.joinRequests", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "users.find") && /\bfind\b/i.test(text) && /\bparent\b/i.test(text)) {
    return {
      kind: "cannot",
      text: "You can't open Users, so this chat can't search for a parent by name. An Owner or Admin searches Users.",
      asked: { id: "users.find", slots: {} },
    };
  }
  if (!running && !available.some((entry) => entry.id === "users.total") && /(?:users page|parent list)/i.test(text)) {
    const home = getSidebarItem("home");
    return {
      kind: "answer",
      text: "A Creator can't open the Users page. Home still shows an All Users count, and it doesn't list parents.",
      link: { label: `Open ${home.label}`, href: home.path },
      asked: { id: "users.total", slots: {} },
    };
  }

  // A catalogue question word for word, about a page this person can't open: say so, and who can.
  // After the replies above that already say it for a Creator in their own words.
  if (!running && exactHits.length === 0) {
    const gated = CATALOGUE.find(
      (entry) => !byId.has(entry.id) && entry.examples.some((example) => phrasingKey(example) === phrasing)
    );
    const page = gated
      ? [gated.sidebarItem, ...(gated.alsoNeeds ?? [])].map((key) => getSidebarItem(key)).find((item) => !canOpenSidebarItem(item, ctx.user))
      : undefined;
    if (gated && page) {
      const as = ctx.user?.accessLevel ? ` as ${aRole(ctx.user.accessLevel)}` : "";
      const roles = page.teamRoles.map(aRole);
      const who = capitalize(roles.length > 1 ? `${roles.slice(0, -1).join(", ")} or ${roles[roles.length - 1]}` : roles.join(""));
      const fact = GATED_FACT[gated.id] ? ` ${GATED_FACT[gated.id]}` : "";
      const home = getSidebarItem("home");
      return {
        kind: "answer",
        text: `${page.label} isn't in your sidebar, so that isn't something you can do here${as}.${fact} ${who} can open ${page.label}.`,
        link: { label: `Open ${home.label}`, href: home.path },
        asked: { id: gated.id, slots: {} },
      };
    }
  }

  // "Who has seen it?": the viewers of the Notice just named, not a fresh guess.
  if (
    !running &&
    previous?.subject &&
    /^(?:who(?:'s| has| have)?|which\s+(?:parents|families|people)\s+have)\s+(?:seen|viewed|opened|read|looked at)\s+it\b/i.test(text.trim())
  ) {
    const viewers = byId.get("notices.viewers");
    if (viewers?.answer) return answer(viewers, { notice: previous.subject }, ctx, rotation.answers, usingFallback, text);
  }

  // "Who has seen it?", "Add Jane to it": a pronoun for the subject an earlier answer named, carried
  // into a fresh message so it can start a different question or task about that same thing. Only
  // used when it actually leads somewhere; otherwise the original message is matched as normal.
  if (previous?.subject && PRONOUN.test(text) && namesSomethingBesidesIt(text) && !leavesForAnExactExample()) {
    const reply = await replyTo(text.replace(PRONOUN, previous.subject), {
      available,
      matcher,
      ctx,
      rotation,
      usingFallback,
    });
    if (!UNRESOLVED_KINDS.has(reply.kind)) return reply;
  }

  const obvious = exactHits.length === 1 ? null : obviousWorkRequest(text);
  if (obvious && available.some((entry) => entry.id === obvious.startedBy)) {
    const started = await taskTurn((org) => startTask(obvious.task, text, obvious.startedBy, org), {
      ctx,
      available,
      matcher,
      rotation,
    });
    // "Sure. What should it say?" alone doesn't say what is being prepared, or that nothing goes out from here.
    if (started?.kind === "task" && !/\b(?:notice|poll|event|publish)\b/i.test(started.text)) {
      return { ...started, startedBy: obvious.startedBy, text: `${KIND_SAID[obvious.task]} ${started.text.replace(/^Sure\.\s*/, "")}` };
    }
    if (started?.kind === "task") return { ...started, startedBy: obvious.startedBy };
    if (started) return started;
  }

  // "which notices did Thandi send?": the same, for the Sender filter on Notices
  if (!running && ASKS_FOR_A_PERSONS_NOTICES.test(text.trim())) {
    const bySender = byId.get("notices.bySender");
    if (bySender?.answer) return answer(bySender, { sender: text }, ctx, rotation.answers, false, text);
  }

  // "Does Zola have any certificates?": a name the examples don't use pulls the model toward
  // the general certificate questions, so the shape of the question decides it.
  if (!running && ASKS_FOR_A_PERSONS_CERTIFICATES.test(text.trim())) {
    const forPerson = byId.get("certificates.forPerson");
    if (forPerson?.answer) return answer(forPerson, { child: text }, ctx, rotation.answers, false, text);
  }

  // "What files do we have?" lists Drive. A close guess was pointing at the Drive page instead.
  if (!running && ASKS_FOR_THE_FILE_LIST.test(text)) {
    const files = byId.get("drive.recent");
    if (files?.answer) return answer(files, {}, ctx, rotation.answers, false, text);
  }

  // "What's in it?" after a file: the pages aren't readable from here. Open the file.
  if (!running && previous?.id === "drive.find" && previous.subject && ASKS_WHAT_IS_INSIDE.test(text)) {
    const finder = byId.get("drive.find");
    if (finder?.answer) {
      const found = await answer(finder, { file: previous.subject }, ctx, rotation.answers, false, text);
      if (found.kind === "answer") {
        return { ...found, text: `I can't read what's inside ${previous.subject}. Open it to see the pages.` };
      }
      return found;
    }
  }

  // "How do I create one?" after certificates is a certificate, not a Circle.
  if (!running && previous && ASKS_TO_CREATE_ONE.test(text) && aboutCertificates(previous.id)) {
    const certificates = byId.get("concepts.certificates");
    if (certificates?.answer) return answer(certificates, {}, ctx, 2, false, text);
  }

  // "When is our next event?" and "I need to see my events" are answered from the
  // Notices we have, before a close match is offered as a guess.
  // "take me to them" and "no the events page" open Calendar.
  const EVENTS_TOPIC = new Set(["home.upcomingEvents", "calendar.whatsOn", "calendar.open"]);
  if (!running) {
    const eventsPage = byId.get("calendar.open");
    const takeThemThere = /^(?:take|show) me (?:to )?(?:them|those)$/i.test(text.trim());
    if (eventsPage?.answer && /^(?:the\s+)?calendar$/.test(text.trim())) {
      return answer(eventsPage, {}, ctx, rotation.answers, false, text);
    }
    if (
      eventsPage?.answer &&
      (wantsTheEventsPage(text) || (takeThemThere && previous && EVENTS_TOPIC.has(previous.id)))
    ) {
      return answer(eventsPage, {}, ctx, rotation.answers, false, text);
    }
    if (
      (asksForNextEvent(text) || wantsUpcomingEvents(text)) &&
      !exactHits.some((entry) => entry.id !== "home.upcomingEvents")
    ) {
      const upcoming = byId.get("home.upcomingEvents");
      if (upcoming?.answer) return answer(upcoming, {}, ctx, rotation.answers, false, text);
    }
    // An exact catalogue example ("Have the certificates been sent?") is that question, not a title search
    const aboutANotice = exactHits.every((entry) => entry.id === "notices.when")
      ? await noticeFromConversation(text, previous, byId.get("notices.when"), ctx, rotation.answers)
      : null;
    if (aboutANotice) return aboutANotice;
  }

  if (!matcher) {
    // Exact catalogue examples work without downloading the embedding model.
    // Do not use approximate word overlap to start tasks when the model is unavailable.
    const fallback = createWordOverlapMatcher(available);
    const exact = (await fallback.rank(text, available.map((entry) => entry.id))).filter((entry) => entry.exact);
    if (exact.length === 0) return { kind: "searchOnly", text: SEARCH_ONLY_REPLY };
    matcher = { ...fallback, rank: async (_, ids) => exact.filter((entry) => ids.includes(entry.id)) };
  }
  // Tasks are matched without their details: "send a notice to Grade 2 about the trip" as "send a notice"
  const read = readTaskDetails(text);
  // "can I send a notice to just one parent, Mrs Pillay?": who it goes to is the question, so it isn't dropped as a detail
  const task = read.said.audience && ONE_PERSON.test(read.said.audience) ? { ...read, rest: text, said: { ...read.said, audience: undefined } } : read;
  // "send a reminder about the test notice" opens Send reminder on that Notice.
  // A how-to ("How do I send a reminder…") stays an explanation.
  if (!running && exactHits.length === 0 && task.said.noticeType === "reminder" && !/^(how|where|why|what|whats|which|who|when|can|could|do|does|is|are|will|would)\b/i.test(text.trim()) && !/\?\s*$/.test(text)) {
    const reminder = byId.get("notices.sendReminder");
    if (reminder && hasHandoff(reminder)) {
      // "for the sports notice" isn't read as a topic, so the whole message is searched for the Notice it names
      return startHandoff(reminder, { notice: task.said.topic || previous?.subject || text }, ctx, text);
    }
  }
  const idsOf = (entries: CatalogueEntry[]) => entries.map((e) => e.id);
  // A question or a problem ("Can a Creator…", "My event isn't showing…") asks about something rather
  // than starting it, even when it mentions a Notice, an Event or a Team member. "Can you…" and
  // "can we send…" are requests to do the task, never one of these.
  const key = phrasingKey(text);
  const asking =
    !POLITE_REQUEST.test(key) &&
    !COLLECTIVE_REQUEST.test(key) &&
    (QUESTION_START.test(key) || /\?\s*$/.test(text) || PROBLEM_PHRASE.test(text));
  const taskIds = asking ? [] : idsOf(available.filter((e) => !e.answer && !hasHowTo(e)));
  let ranked: RankedEntry[];
  let tasks: RankedEntry[];
  let coreQuestions: RankedEntry[] = [];
  let coreTasks: RankedEntry[] = [];
  let namesACircle = false;
  try {
    // These handoffs are the answer to a "How do I…?" as well: they explain, then start the flow.
    const offerAlongsideHowTo = new Set(["create.offerToSend", "create.scheduleOffer", "create.sendToOneParent"]);
    const questionIds = idsOf(available.filter((e) => e.answer || hasHowTo(e) || offerAlongsideHowTo.has(e.id)));
    // Questions that take a detail: a date, or a named Circle, Notice or person
    const detailIds = idsOf(available.filter((e) => e.answer && e.slots));
    // "Notices sent to Grade 2" also as "Notices sent" and "Notices sent to a circle", so a name doesn't drown out
    // the question. Only for questions that take a detail: "Show me notices sent" isn't "Show me Notices".
    // "How many people are in Blackbrick Umhlanga" names a Circle without "to" or "for": found by name, as "the circle"
    // "How many people viewed the Welcome to Fugazi Capital notice?" likewise, as "the notice"
    const circleNamed = task.said.audience ? undefined : await circleNamedIn(text, ctx);
    const noticeNamed = task.said.audience || circleNamed ? undefined : await noticeNamedIn(text, ctx);
    namesACircle = Boolean(task.said.audience || circleNamed);
    const withAnyCircle = task.said.audience
      ? text.replace(task.said.audience, "a circle")
      : circleNamed
        ? text.replace(circleNamed, "the circle")
        : noticeNamed
          ? text.replace(new RegExp(`(?:\\bthe\\s+)?${escapeRegExp(noticeNamed)}(?:\\s+notice\\b)?`, "i"), "the notice")
          : text;
    const [questions, questionsWithoutDetails, questionsWithAnyCircle, questionsWithoutTaskDetails, tasksWithoutDetails, tasksAsSaid, fromCore, fromCoreTasks] =
      await rankSlots(matcher, [
        { text, allowedIds: questionIds },
        { text: task.rest, allowedIds: detailIds, worthIt: task.rest !== text },
        { text: withAnyCircle, allowedIds: detailIds, worthIt: withAnyCircle !== text },
        // "How do I send a normal notice about the museum visit?" is still "How do I send a normal notice?".
        // A Circle's name is only dropped for questions that take one (above), never for every question.
        // "my class" names no Circle, so "where do I invite parents to my class?" is still "where do I invite parents?"
        { text: task.rest, allowedIds: questionIds, worthIt: task.rest !== text && (!task.said.audience || /^(?:my|our)\b/i.test(task.said.audience.trim())) },
        // Tasks without their details, and as said, in case that's one of their examples word for word
        { text: task.rest, allowedIds: taskIds },
        { text, allowedIds: taskIds, worthIt: task.rest !== text },
        // "Imagine a fridge. Where are our files?" — the question is the last sentence.
        { text: coreOf || text, allowedIds: questionIds, worthIt: Boolean(coreOf) },
        { text: coreOf || text, allowedIds: taskIds, worthIt: Boolean(coreOf) },
      ]);
    coreQuestions = fromCore;
    coreTasks = fromCoreTasks;
    // A rewritten line ("dm one parent" read as "send a direct message") can be an
    // example of a different entry. When the words they typed are already one
    // example, that example wins and the rewrite does not count as a second.
    const spokenExactIds = new Set(
      [...questions, ...tasksAsSaid].filter((row) => row.exact).map((row) => row.id)
    );
    const asSaid = (rows: RankedEntry[]) =>
      spokenExactIds.size === 1 ? rows.map(({ id, score }) => ({ id, score })) : rows;
    tasks = bestScoreEach([...asSaid(tasksWithoutDetails), ...tasksAsSaid]).sort((a, b) => b.score - a.score);
    ranked = [
      ...bestScoreEach([
        ...questions,
        ...coreQuestions,
        ...asSaid(questionsWithoutDetails),
        ...asSaid(questionsWithAnyCircle),
        ...asSaid(questionsWithoutTaskDetails),
      ]),
      ...tasks,
    ].sort((a, b) => b.score - a.score);
  } catch (error) {
    console.error("[Ask Noticeboard] Couldn't match the question", error);
    return { kind: "searchOnly", text: SEARCH_ONLY_REPLY };
  }
  const { thresholds } = matcher;
  const questionsFor = (matches: RankedEntry[]): Promise<string[]> =>
    namedQuestions(
      matches
        .slice(0, SUGGESTION_COUNT)
        .map((r) => byId.get(r.id))
        .filter((e): e is CatalogueEntry => Boolean(e)),
      ctx
    );
  // The ramble can mention a parent or a Friday. The last sentence is the question,
  // and that sentence decides, rather than the story around it.
  const coreExactChoice =
    coreOf && exactHits.length !== 1 && coreHits.length === 1
      ? { id: coreHits[0].id, score: 1, exact: true as const }
      : undefined;
  const coreChoice =
    coreExactChoice ||
    (coreOf && exactHits.length !== 1
      ? confidentMatch([...coreQuestions, ...coreTasks], coreTasks, thresholds, wantsHelpDoing(coreOf))
      : undefined);
  // A catalogue example, compared as text. The model can still score a near miss
  // ("how many team members?" beside "how many can we have?") as a close call.
  const spokenExact =
    exactHits.length === 1 ? { id: exactHits[0].id, score: 1, exact: true as const } : undefined;
  // "what did we send to testing today?": sent on a day and sent to a Circle score alike. Naming the
  // Circle makes it that Circle's Notices, on that day
  const [first, second] = ranked;
  const toTheCircle =
    namesACircle && [first?.id, second?.id].includes("notices.sentOn") && [first?.id, second?.id].includes("notices.toCircle")
      ? ranked.find((r) => r.id === "notices.toCircle")
      : undefined;
  const chosen = coreChoice || spokenExact || toTheCircle || confidentMatch(ranked, tasks, thresholds, wantsHelpDoing(text));
  const bestScore = [ranked[0]?.score, chosen?.score].reduce<number | undefined>(
    (best, score) => (typeof score !== "number" ? best : best === undefined ? score : Math.max(best, score)),
    undefined
  );
  const withScore = (reply: AssistantReply): AssistantReply =>
    bestScore === undefined ? reply : { ...reply, topScore: bestScore };
  const askedFor = coreChoice && coreOf ? coreOf : text;
  const spin = rotation.answers + ((rotation as { voice?: number }).voice ?? 0);
  // A catalogue example, typed on its own, stays in that entry's own words.
  // A sideways question keeps the same facts and changes the sentence around them.
  const soften = (reply: AssistantReply): AssistantReply => {
    if (!spokenCore(text) || exactHits.length === 1 || !("text" in reply) || !reply.text) return reply;
    if (reply.kind !== "howTo" && reply.kind !== "answer" && reply.kind !== "handoff") return reply;
    return { ...reply, text: withVoice(reply.text, text, spin) };
  };
  // Shared-word matching, while the model is loading, may answer a question. It may not start a task or a handoff unless the words are an example.
  const weakFallback = Boolean(usingFallback && chosen && !chosen.exact);
  if (chosen && !weakFallback) {
    const entry = byId.get(chosen.id);
    // A pressed example keeps only its kind ("an urgent notice"), never its example details
    const kindOnly = task.said.noticeType ? { noticeType: task.said.noticeType } : {};
    const guided = entry && !entry.answer && entry.guided?.(pressedExample ? kindOnly : task.said);
    // "Locate an existing poll", "find the poll I sent last week": one already made is on Notices, never a new one
    const notices = byId.get("notices.open");
    if (guided && LOOKS_FOR_ONE_MADE.test(text) && !/\b(?:create|make|send|start|new|set\s+up|draft|prepare)\b/i.test(text) && notices?.answer) {
      return withScore(await answer(notices, {}, ctx, rotation.answers, usingFallback, text));
    }
    if (guided) {
      const urgent = task.said.noticeType === "urgent" && guided === "notice";
      const request = pressedExample ? (urgent ? "create an urgent notice" : `create ${guided === "event" ? "an" : "a"} ${guided}`) : askedFor;
      const started = await taskTurn((org) => startTask(guided, request, chosen.id, org), { ctx, available, matcher, rotation });
      if (started?.kind === "task" && !/\b(?:notice|poll|event|publish)\b/i.test(started.text)) {
        return withScore({ ...started, startedBy: chosen.id, text: `${KIND_SAID[guided]} ${started.text.replace(/^Sure\.\s*/, "")}` });
      }
      if (started?.kind === "task") return withScore({ ...started, startedBy: chosen.id });
      if (started) return withScore(started);
    }
    // A handoff about a named Notice ("send a reminder for the trip notice") finds it in the whole message;
    // a detail readTaskDetails already found (it never sets "notice" today) always wins over the raw text
    // "I only want to look at certificates": looking, so nothing is started; the closest answer is offered instead
    const onlyLooking = /\b(?:look\s+at|view|browse|have\s+a\s+look|just\s+see)\b/i.test(text) && !/\b(?:create|make|start|new|add|issue|send|set\s+up)\b/i.test(text);
    if (entry && hasHandoff(entry) && !onlyLooking) {
      // "Make them a Creator" names the role a new team member gets
      const role = entry.id === "team.addMember" && !task.said.role ? roleIn(askedFor) : null;
      const said = { ...task.said, notice: task.said.topic || askedFor, ...(role ? { role } : {}) };
      return withScore(soften(await startHandoff(entry, said, ctx, askedFor)));
    }
    if (entry && hasHowTo(entry)) {
      const reply = await answerHowTo(entry, ctx, askedFor, usingFallback);
      const alreadyAboutDrive =
        previous && (previous.id.startsWith("drive.") || previous.id === "troubleshooting.uploadNotFound");
      if (reply.kind === "howTo" && entry.id === "drive.downloadHowTo" && alreadyAboutDrive) {
        return withScore(soften({ ...reply, text: "Open the file in Drive. There isn't a Download all button." }));
      }
      return withScore(soften(reply));
    }
    if (entry?.answer) return withScore(soften(await answer(entry, slotsFrom(askedFor, entry), ctx, rotation.answers, usingFallback, askedFor)));
  } else if (chosen && weakFallback) {
    const entry = byId.get(chosen.id);
    if (entry && hasHowTo(entry)) {
      const reply = await answerHowTo(entry, ctx, text, usingFallback);
      const alreadyAboutDrive =
        previous && (previous.id.startsWith("drive.") || previous.id === "troubleshooting.uploadNotFound");
      if (reply.kind === "howTo" && entry.id === "drive.downloadHowTo" && alreadyAboutDrive) {
        return withScore(soften({ ...reply, text: "Open the file in Drive. There isn't a Download all button." }));
      }
      return withScore(soften(reply));
    }
    if (entry?.answer) return withScore(soften(await answer(entry, slotsFrom(text, entry), ctx, rotation.answers, usingFallback, text)));
  }

  const namedNotice = () =>
    noticeFromConversation(text, previous, byId.get("notices.when"), ctx, rotation.answers);

  // Nothing in the catalogue fitted clearly: a course answer, when one of its rules does
  if (lessonIfNothingFits) return withScore(lessonIfNothingFits);

  if (soundsOffTopic(text)) {
    return withScore((await namedNotice()) ?? { kind: "offTopic", text: offTopicReply(rotation.offTopic) });
  }

  const [top] = ranked;

  const related = ranked.filter((r) => r.score >= thresholds.related);

  // A close call: offer the top few rather than guess, unless this names a Notice we can answer about
  if (top && top.score >= thresholds.didYouMean) {
    const found = await namedNotice();
    if (found) return withScore(found);
    const questions = await questionsFor(related);
    // "who has seen Uniform Shop Sale?" with no such Notice: said by name, rather than guessed at
    const named = text.trim().match(ASKS_WHO_SAW_A_NAMED_THING)?.[1] ?? text.trim().match(ASKS_ABOUT_A_NAMED_THING)?.[1];
    if (named && ABOUT_A_NAMED_NOTICE.has(top.id) && !NOT_A_NAME.test(named)) {
      return withScore({
        kind: "unanswered",
        text: `${notFoundCalled(named)} These are the closest questions I can answer:`,
        notFound: notFoundCalled(named),
        questions,
        asked: withOffered(previous, questions),
        ...(usingFallback ? { fromFallback: true as const } : {}),
      });
    }
    // "where can I see notices I've sent?" when the first suggestion is "Where can I see the notices we've sent?": answered
    if (questions[0] && exactHits.length === 0 && nearlyTheSame(text, questions[0])) {
      return replyTo(questions[0], { available, matcher, ctx, rotation, previous, task: running, usingFallback, parked });
    }
    return withScore({
      kind: "didYouMean",
      text: DID_YOU_MEAN_REPLY,
      questions,
      asked: withOffered(previous, questions),
      ...(usingFallback ? { fromFallback: true as const } : {}),
    });
  }

  if (related.length === 0 && !soundsLikeNoticeboard(text)) {
    return withScore((await namedNotice()) ?? { kind: "offTopic", text: offTopicReply(rotation.offTopic) });
  }

  const closest = await questionsFor(related);
  const found = await namedNotice();
  if (found) return withScore(found);
  // "when is the year end gala" with no such Notice: say so by name, then the closest questions
  const missing = text.trim().match(ASKS_ABOUT_A_NAMED_THING)?.[1];
  if (missing && closest.length > 0) {
    return withScore({
      kind: "unanswered",
      text: `${notFoundCalled(missing)} These are the closest questions I can answer:`,
      notFound: notFoundCalled(missing),
      questions: closest,
      asked: withOffered(previous, closest),
      ...(usingFallback ? { fromFallback: true as const } : {}),
    });
  }
  if (closest.length > 0) {
    return withScore({
      kind: "unanswered",
      text: UNANSWERED_REPLY,
      questions: closest,
      asked: withOffered(previous, closest),
      ...(usingFallback ? { fromFallback: true as const } : {}),
    });
  }
  const examples = await namedQuestions(pickExampleEntries(available, SUGGESTION_COUNT), ctx);
  return withScore({
    kind: "unanswered",
    text: UNANSWERED_REPLY,
    questions: examples,
    asked: withOffered(previous, examples),
    ...(usingFallback ? { fromFallback: true as const } : {}),
  });
}
